import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import NextScenesConfirmDialog from "./NextScenesConfirmDialog";
import ChildrensBookManuscriptEditor from "./ChildrensBookManuscriptEditor";
import ChildrensBookManuscriptReadOnly from "./ChildrensBookManuscriptReadOnly";
import TextbookLiveChapterPageFlow from "./TextbookLiveChapterPageFlow";
import TextbookVersionHistory from "./TextbookVersionHistory";

import {
  prepareTextbookRichManuscriptBlock,
} from "../utils/textbookRichManuscriptBridge";

import {
  applyTextbookChapterEditorContent,
  buildTextbookChapterEditorContent,
  textbookChapterBoundaryId,
  textbookChapterBoundaryIndex,
} from "../utils/textbookChapterManuscriptEditor";

import {
  splitManuscriptEditorBlock,
} from "../utils/childrensBookManuscriptEditor";

import {
  buildTextbookFigureTextFlowUnits,
  isTextbookFigureTextFlowPlacement,
} from "../utils/textbookFigureTextFlow";

export const TEXTBOOK_CHAPTER_BLOCK_TYPES =
  Object.freeze([
    "paragraph",
    "heading",
    "learning_objectives",
    "definition",
    "worked_example",
    "exercise",
    "reference",
    "equation",
    "table",
    "note",
    "summary",
  ]);

export const TEXTBOOK_PERSISTED_CHAPTER_BLOCK_TYPES =
  Object.freeze([
    ...TEXTBOOK_CHAPTER_BLOCK_TYPES,
    "figure",
  ]);

const TYPE_LABELS = {
  paragraph: {
    en: "Text",
    fr: "Texte",
  },

  heading: {
    en: "Heading",
    fr: "Titre libre",
  },

  learning_objectives: {
    en: "Learning objectives",
    fr: "Objectifs d’apprentissage",
  },

  definition: {
    en: "Definition",
    fr: "Définition",
  },

  worked_example: {
    en: "Worked example / case study",
    fr: "Exemple guidé / étude de cas",
  },

  exercise: {
    en: "Exercise / practice",
    fr: "Exercice / pratique",
  },

  reference: {
    en: "Reference / citation",
    fr: "Référence / citation",
  },

  equation: {
    en: "Equation / formula",
    fr: "Équation / formule",
  },

  table: {
    en: "Table",
    fr: "Tableau",
  },

  figure: {
    en: "Figure",
    fr: "Figure",
  },

  note: {
    en: "Note",
    fr: "Note",
  },

  summary: {
    en: "Chapter summary",
    fr: "Résumé du chapitre",
  },
};

function normalizeTextbookEquation(
  value,
  fallbackText = ""
) {
  const sourceObject =
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
      ? value
      : {};

  return {
    source:
      Object.prototype
        .hasOwnProperty.call(
          sourceObject,
          "source"
        )
        ? exactText(
            sourceObject.source
          )
        : exactText(
            fallbackText
          ),

    notation:
      "plain_text",

    number:
      clean(
        sourceObject.number
      ),

    accessibleText:
      exactText(
        sourceObject
          .accessibleText
      ),
  };
}


function normalizeTextbookTable(
  value
) {
  const source =
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
      ? value
      : {};

  const rawRows =
    Array.isArray(source.rows) &&
    source.rows.length
      ? source.rows
      : [
          ["", ""],
          ["", ""],
        ];

  const width =
    Math.min(
      12,
      Math.max(
        1,
        ...rawRows.map(
          (row) =>
            Array.isArray(row)
              ? row.length
              : 0
        )
      )
    );

  const rows =
    rawRows
      .slice(0, 50)
      .map(
        (rawRow) => {
          const row =
            Array.isArray(rawRow)
              ? rawRow
                  .slice(0, width)
                  .map(
                    (cell) =>
                      exactText(cell)
                  )
              : [];

          while (
            row.length < width
          ) {
            row.push("");
          }

          return row;
        }
      );

  return {
    caption:
      exactText(
        source.caption
      ),
    headerRow:
      source.headerRow ===
        undefined
        ? true
        : Boolean(
            source.headerRow
          ),
    rows:
      rows.length
        ? rows
        : [[""]],
  };
}

function textbookTableCompatibilityText(
  table
) {
  const value =
    normalizeTextbookTable(
      table
    );

  const lines = [];

  if (clean(value.caption)) {
    lines.push(
      value.caption
    );
  }

  value.rows.forEach(
    (row) => {
      if (
        row.some(
          (cell) =>
            clean(cell)
        )
      ) {
        lines.push(
          row.join("\t")
        );
      }
    }
  );

  return lines.join("\n");
}

export function TextbookEquationPreview({
  equation,
  fallbackText = "",
  isFR = false,
}) {
  const value =
    normalizeTextbookEquation(
      equation,
      fallbackText
    );

  const accessibleLabel =
    clean(
      value.accessibleText
    ) ||
    clean(
      value.source
    ) ||
    (
      isFR
        ? "Équation"
        : "Equation"
    );

  return (
    <div
      data-textbook-equation-preview="true"
      aria-label={accessibleLabel}
      style={{
        display: "grid",
        gap: 8,
        padding:
          "14px 16px",
        borderRadius: 12,
        background:
          "#f8fafc",
        border:
          "1px solid rgba(100,116,139,0.22)",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            value.number
              ? "1fr auto"
              : "1fr",
          alignItems: "center",
          gap: 14,
        }}
      >
        <div
          style={{
            textAlign: "center",
            whiteSpace: "pre-wrap",
            overflowWrap:
              "anywhere",
            fontFamily:
              '"Cambria Math", "STIX Two Math", "Times New Roman", serif',
            fontSize: 22,
            lineHeight: 1.5,
            letterSpacing:
              "0.01em",
          }}
        >
          {value.source}
        </div>

        {value.number ? (
          <span
            aria-label={
              isFR
                ? `Numéro d’équation ${value.number}`
                : `Equation number ${value.number}`
            }
            style={{
              fontFamily:
                '"Cambria Math", "STIX Two Math", "Times New Roman", serif',
              fontSize: 15,
              whiteSpace:
                "nowrap",
            }}
          >
            ({value.number})
          </span>
        ) : null}
      </div>
    </div>
  );
}


export function TextbookTablePreview({
  table,
  isFR = false,
  label = "",
}) {
  const value =
    normalizeTextbookTable(
      table
    );

  const columnCount =
    value.rows[0]?.length ||
    1;

  const leadingRowCount =
    value.headerRow &&
    value.rows.length > 1
      ? 2
      : Math.min(
          1,
          value.rows.length
        );

  const leadingRows =
    value.rows.slice(
      0,
      leadingRowCount
    );

  const remainingRows =
    value.rows.slice(
      leadingRowCount
    );

  const renderRow = (
    row,
    rowIndex
  ) => (
    <div
      key={`table-preview-row-${rowIndex}`}
      role="row"
      data-textbook-table-row="true"
      style={{
        display: "grid",
        gridTemplateColumns:
          `repeat(${columnCount}, minmax(0, 1fr))`,
        breakInside:
          "avoid-column",
        pageBreakInside:
          "avoid",
      }}
    >
      {row.map(
        (
          cell,
          cellIndex
        ) => {
          const isHeader =
            value.headerRow &&
            rowIndex === 0;

          return (
            <div
              key={`table-preview-cell-${rowIndex}-${cellIndex}`}
              role={
                isHeader
                  ? "columnheader"
                  : "cell"
              }
              style={{
                borderLeft:
                  "1px solid #cbd5e1",
                borderTop:
                  "1px solid #cbd5e1",
                borderRight:
                  cellIndex ===
                  columnCount - 1
                    ? "1px solid #cbd5e1"
                    : undefined,
                borderBottom:
                  "1px solid #cbd5e1",
                padding:
                  "8px 10px",
                textAlign:
                  "left",
                verticalAlign:
                  "top",
                overflowWrap:
                  "anywhere",
                fontWeight:
                  isHeader
                    ? 800
                    : 400,
                background:
                  isHeader
                    ? "#f8fafc"
                    : "#ffffff",
              }}
            >
              {cell}
            </div>
          );
        }
      )}
    </div>
  );

  return (
    <div
      data-textbook-table-preview="true"
      role="table"
      style={{
        display: "grid",
        gap: 0,
      }}
    >
      <div
        data-textbook-table-start="true"
        style={{
          display: "grid",
          gap: 8,
          breakInside:
            "avoid-column",
          pageBreakInside:
            "avoid",
        }}
      >
        {clean(label) ? (
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
              color: "#475569",
            }}
          >
            {label}
          </div>
        ) : null}

        {clean(value.caption) ? (
          <div
            style={{
              fontWeight: 800,
            }}
          >
            {value.caption}
          </div>
        ) : null}

        <div
          data-textbook-table-leading-rows="true"
          style={{
            display: "grid",
            gap: 0,
          }}
        >
          {leadingRows.map(
            (row, rowIndex) =>
              renderRow(
                row,
                rowIndex
              )
          )}
        </div>
      </div>

      {remainingRows.length ? (
        <div
          data-textbook-table-continuation="true"
          style={{
            display: "grid",
            gap: 0,
          }}
        >
          {remainingRows.map(
            (
              row,
              continuationIndex
            ) =>
              renderRow(
                row,
                leadingRowCount +
                  continuationIndex
              )
          )}
        </div>
      ) : null}
    </div>
  );
}

const TEXTBOOK_FIGURE_TYPES =
  Object.freeze([
    "figure",
    "image",
    "diagram",
    "chart",
    "photograph",
    "map",
  ]);

const TEXTBOOK_FIGURE_PLACEMENTS =
  Object.freeze([
    "full_width",
    "half_left",
    "half_right",
    "small_left",
    "small_right",
  ]);

const TEXTBOOK_FIGURE_PLACEMENT_LABELS = {
  full_width: {
    en: "Full width",
    fr: "Pleine largeur",
  },

  half_left: {
    en: "Half width — left",
    fr: "Demi-largeur — gauche",
  },

  half_right: {
    en: "Half width — right",
    fr: "Demi-largeur — droite",
  },

  small_left: {
    en: "Small — left",
    fr: "Petite — gauche",
  },

  small_right: {
    en: "Small — right",
    fr: "Petite — droite",
  },
};

function normalizeTextbookFigurePlacement(
  value
) {
  const placement =
    clean(value);

  return TEXTBOOK_FIGURE_PLACEMENTS
    .includes(
      placement
    )
      ? placement
      : "full_width";
}

function textbookFigurePlacementStyle(
  value,
  flowing = false
) {
  const placement =
    normalizeTextbookFigurePlacement(
      value
    );

  if (
    placement ===
      "half_left" ||
    placement ===
      "half_right"
  ) {
    return {
      width:
        "clamp(220px, 48%, 440px)",
      maxWidth:
        "100%",
      ...(flowing
        ? {
            float:
              placement ===
                "half_right"
                ? "right"
                : "left",
            margin:
              placement ===
                "half_right"
                ? "0 0 14px 18px"
                : "0 18px 14px 0",
          }
        : {
            marginLeft:
              placement ===
                "half_right"
                ? "auto"
                : 0,
            marginRight:
              placement ===
                "half_left"
                ? "auto"
                : 0,
          }),
    };
  }

  if (
    placement ===
      "small_left" ||
    placement ===
      "small_right"
  ) {
    return {
      width:
        "clamp(160px, 32%, 300px)",
      maxWidth:
        "100%",
      ...(flowing
        ? {
            float:
              placement ===
                "small_right"
                ? "right"
                : "left",
            margin:
              placement ===
                "small_right"
                ? "0 0 12px 16px"
                : "0 16px 12px 0",
          }
        : {
            marginLeft:
              placement ===
                "small_right"
                ? "auto"
                : 0,
            marginRight:
              placement ===
                "small_left"
                ? "auto"
                : 0,
          }),
    };
  }

  return {
    width: "100%",
    maxWidth: "100%",
    marginLeft: 0,
    marginRight: 0,
  };
}

const TEXTBOOK_FIGURE_TYPE_LABELS = {
  figure: {
    en: "Figure",
    fr: "Figure",
  },

  image: {
    en: "Image",
    fr: "Image",
  },

  diagram: {
    en: "Diagram",
    fr: "Schéma",
  },

  chart: {
    en: "Chart",
    fr: "Graphique",
  },

  photograph: {
    en: "Photograph",
    fr: "Photographie",
  },

  map: {
    en: "Map",
    fr: "Carte",
  },
};

const TEXTBOOK_FIGURE_MAX_BYTES =
  5 * 1024 * 1024;

const TEXTBOOK_FIGURE_ACCEPT =
  "image/jpeg,image/png,image/webp";

function normalizeFigureAssets(
  value
) {
  return Array.isArray(value)
    ? value
        .map(
          (asset) => ({
            id:
              clean(
                asset?.id
              ),

            chapterItemId:
              clean(
                asset
                  ?.chapterItemId
              ),

            sectionItemId:
              clean(
                asset
                  ?.sectionItemId
              ),

            figureType:
              clean(
                asset
                  ?.figureType
              ) ||
              "image",

            source:
              clean(
                asset?.source
              ),

            fileName:
              clean(
                asset
                  ?.fileName
              ),

            publicUrl:
              clean(
                asset
                  ?.publicUrl
              ),

            caption:
              exactText(
                asset?.caption
              ),

            alternativeText:
              exactText(
                asset
                  ?.alternativeText
              ),

            creatorOrSource:
              exactText(
                asset
                  ?.creatorOrSource
              ),

            rightsOrLicense:
              exactText(
                asset
                  ?.rightsOrLicense
              ),

            revision:
              Number(
                asset
                  ?.revision || 1
              ),
          })
        )
        .filter(
          (asset) =>
            asset.id
        )
    : [];
}

export function TextbookFigurePreview({
  asset,
  onLoadFigureFile,
  isFR = false,
  eager = false,
}) {
  const [
    managedUrl,
    setManagedUrl,
  ] = useState("");

  const [
    loadError,
    setLoadError,
  ] = useState("");

  const source =
    clean(
      asset?.source
    );

  const publicUrl =
    clean(
      asset?.publicUrl
    );

  useEffect(() => {
    let cancelled =
      false;

    let createdUrl =
      "";

    setManagedUrl("");
    setLoadError("");

    if (
      source !== "upload"
    ) {
      return undefined;
    }

    if (
      !publicUrl ||
      typeof onLoadFigureFile !==
        "function"
    ) {
      setLoadError(
        isFR
          ? "Aperçu indisponible."
          : "Preview unavailable."
      );

      return undefined;
    }

    (
      async () => {
        try {
          const blob =
            await onLoadFigureFile(
              publicUrl
            );

          if (
            cancelled ||
            !blob ||
            Number(
              blob.size
            ) <= 0 ||
            typeof URL ===
              "undefined" ||
            typeof URL
              .createObjectURL !==
              "function"
          ) {
            return;
          }

          createdUrl =
            URL.createObjectURL(
              blob
            );

          if (!cancelled) {
            setManagedUrl(
              createdUrl
            );
          }
        } catch (error) {
          if (!cancelled) {
            setLoadError(
              clean(
                error?.message
              ) ||
                (
                  isFR
                    ? "Aperçu indisponible."
                    : "Preview unavailable."
                )
            );
          }
        }
      }
    )();

    return () => {
      cancelled = true;

      if (
        createdUrl &&
        typeof URL !==
          "undefined" &&
        typeof URL
          .revokeObjectURL ===
          "function"
      ) {
        URL.revokeObjectURL(
          createdUrl
        );
      }
    };
  }, [
    asset?.id,
    source,
    publicUrl,
    onLoadFigureFile,
    isFR,
  ]);

  const imageUrl =
    source ===
      "external_url"
      ? publicUrl
      : managedUrl;

  if (!imageUrl) {
    return (
      <div
        data-textbook-figure-preview-loading={
          loadError
            ? "false"
            : "true"
        }
        role={
          loadError
            ? "alert"
            : "status"
        }
        style={{
          minHeight: 120,
          display: "grid",
          placeItems: "center",
          padding: 16,
          borderRadius: 12,
          border:
            "1px dashed rgba(100,116,139,0.35)",
          background:
            "#f8fafc",
          color: "#64748b",
          textAlign: "center",
          fontWeight: 700,
        }}
      >
        {loadError ||
          (
            isFR
              ? "Chargement de la Figure…"
              : "Loading Figure…"
          )}
      </div>
    );
  }

  return (
    <img
      data-textbook-figure-preview-loading="false"
      src={imageUrl}
      alt={
        clean(
          asset
            ?.alternativeText
        ) ||
        clean(
          asset?.caption
        ) ||
        "Figure"
      }
      loading={
        eager
          ? "eager"
          : "lazy"
      }
      referrerPolicy="no-referrer"
      style={{
        width: "100%",
        maxHeight: 420,
        display: "block",
        objectFit: "contain",
        borderRadius: 12,
        background: "#ffffff",
      }}
    />
  );
}


function clean(value) {
  return String(
    value ?? ""
  ).trim();
}

function exactText(value) {
  return value == null
    ? ""
    : String(value);
}

function normalizeSections(value) {
  return Array.isArray(value)
    ? value
        .map(
          (item) => ({
            itemId:
              clean(
                item?.itemId
              ),

            title:
              clean(
                item?.title
              ),
          })
        )
        .filter(
          (item) =>
            item.itemId
        )
    : [];
}

export function normalizeTextbookSectionLayout(
  value,
  sections = []
) {
  const requested =
    new Set(
      (
        Array.isArray(value)
          ? value
          : []
      )
        .filter(
          (entry) =>
            entry
              ?.startOnNewPage ===
            true
        )
        .map(
          (entry) =>
            clean(
              entry
                ?.sectionItemId
            )
        )
        .filter(Boolean)
    );

  return normalizeSections(
    sections
  )
    .filter(
      (section) =>
        requested.has(
          section.itemId
        )
    )
    .map(
      (section) => ({
        sectionItemId:
          section.itemId,
        startOnNewPage:
          true,
      })
    );
}


function sameTextbookSectionLayout(
  left,
  right
) {
  return (
    JSON.stringify(
      left || []
    ) ===
    JSON.stringify(
      right || []
    )
  );
}


function normalizeBlocks(value) {
  const allowed =
    new Set(
      TEXTBOOK_PERSISTED_CHAPTER_BLOCK_TYPES
    );

  return Array.isArray(value)
    ? value
        .map(
          (item) => ({
            blockId:
              clean(
                item?.blockId
              ),

            type:
              clean(
                item?.type
              ),

            sectionItemId:
              clean(
                item
                  ?.sectionItemId
              ),

            text:
              exactText(
                item?.text
              ),

            figureAssetId:
              clean(
                item?.figureAssetId
              ),

            figurePlacement:
              clean(
                item?.type
              ) === "figure"
                ? normalizeTextbookFigurePlacement(
                    item?.figurePlacement
                  )
                : "",

            ...(
              clean(
                item?.type
              ) === "figure" &&
              clean(
                item
                  ?.figureTextFlowAnchorId
              )
                ? {
                    figureTextFlowAnchorId:
                      clean(
                        item
                          ?.figureTextFlowAnchorId
                      ),
                  }
                : {}
            ),

            manuscriptContent:
              [
                "figure",
                "equation",
                "table",
              ].includes(
                clean(
                  item?.type
                )
              )
                ? undefined
                : item
                    ?.manuscriptContent,

            equation:
              clean(
                item?.type
              ) === "equation"
                ? normalizeTextbookEquation(
                    item?.equation,
                    item?.text
                  )
                : undefined,

            table:
              clean(
                item?.type
              ) === "table"
                ? normalizeTextbookTable(
                    item?.table
                  )
                : undefined,
          })
        )
        .filter(
          (item) =>
            item.blockId &&
            allowed.has(
              item.type
            )
        )
    : [];
}

function canonicalBlocks(
  value,
  sections
) {
  const blocks =
    normalizeBlocks(value);

  const order = [
    "",
    ...sections.map(
      (section) =>
        section.itemId
    ),
  ];

  const rank =
    new Map(
      order.map(
        (id, index) => [
          id,
          index,
        ]
      )
    );

  return blocks
    .map(
      (block, index) => ({
        block,
        index,
      })
    )
    .sort(
      (left, right) => {
        const a =
          rank.has(
            left.block
              .sectionItemId
          )
            ? rank.get(
                left.block
                  .sectionItemId
              )
            : Number
                .MAX_SAFE_INTEGER;

        const b =
          rank.has(
            right.block
              .sectionItemId
          )
            ? rank.get(
                right.block
                  .sectionItemId
              )
            : Number
                .MAX_SAFE_INTEGER;

        return (
          a - b ||
          left.index -
            right.index
        );
      }
    )
    .map(
      ({ block }) =>
        block
    );
}

export function textbookPlacedObjectLabels(
  value,
  sections,
  figureAssets,
  isFR = false
) {
  const ordered =
    canonicalBlocks(
      value,
      normalizeSections(
        sections
      )
    );

  const assetsById =
    new Map(
      normalizeFigureAssets(
        figureAssets
      ).map(
        (asset) => [
          asset.id,
          asset,
        ]
      )
    );

  const counters =
    new Map();

  const labels = {};

  ordered.forEach(
    (block) => {
      let counterKey = "";
      let baseLabel = "";

      if (
        block?.type ===
          "table"
      ) {
        counterKey =
          "table";

        baseLabel =
          isFR
            ? "Tableau"
            : "Table";
      } else if (
        block?.type ===
          "figure"
      ) {
        const asset =
          assetsById.get(
            clean(
              block
                ?.figureAssetId
            )
          );

        const requestedType =
          clean(
            asset?.figureType
          );

        const figureType =
          TEXTBOOK_FIGURE_TYPES
            .includes(
              requestedType
            )
            ? requestedType
            : "figure";

        counterKey =
          `figure:${figureType}`;

        baseLabel =
          TEXTBOOK_FIGURE_TYPE_LABELS[
            figureType
          ]?.[
            isFR
              ? "fr"
              : "en"
          ] ||
          "Figure";
      }

      if (
        !counterKey ||
        !clean(
          block?.blockId
        )
      ) {
        return;
      }

      const ordinal =
        (
          counters.get(
            counterKey
          ) || 0
        ) + 1;

      counters.set(
        counterKey,
        ordinal
      );

      labels[
        block.blockId
      ] =
        `${baseLabel} ${ordinal}`;
    }
  );

  return labels;
}


export function textbookCanonNeedsConsolidation(
  value
) {
  const blocks =
    normalizeBlocks(
      value
    );

  for (
    let index = 1;
    index < blocks.length;
    index += 1
  ) {
    const previous =
      blocks[index - 1];

    const current =
      blocks[index];

    if (
      previous?.type ===
        "paragraph" &&
      current?.type ===
        "paragraph" &&
      clean(
        previous
          ?.sectionItemId
      ) ===
        clean(
          current
            ?.sectionItemId
        )
    ) {
      return true;
    }
  }

  return false;
}


function sameBlocks(
  left,
  right
) {
  const a =
    normalizeBlocks(left);

  const b =
    normalizeBlocks(right);

  return (
    a.length === b.length &&
    a.every(
      (item, index) => {
        const other =
          b[index];

        return (
          item.blockId ===
            other.blockId &&
          item.type ===
            other.type &&
          item.sectionItemId ===
            other.sectionItemId &&
          item.text ===
            other.text &&
          item.figureAssetId ===
            other.figureAssetId &&
          item.figurePlacement ===
            other.figurePlacement &&
          clean(
            item
              .figureTextFlowAnchorId
          ) ===
            clean(
              other
                .figureTextFlowAnchorId
            ) &&
          JSON.stringify(
            item.equation ??
              null
          ) ===
            JSON.stringify(
              other
                .equation ??
                null
            ) &&
          JSON.stringify(
            item.table ??
              null
          ) ===
            JSON.stringify(
              other
                .table ??
                null
            ) &&
          JSON.stringify(
            item.manuscriptContent ??
              null
          ) ===
            JSON.stringify(
              other
                .manuscriptContent ??
                null
            )
        );
      }
    )
  );
}

export function shouldOpenTextbookChapterInEditMode(
  revision,
  persistedBlocks
) {
  return (
    Number(
      revision || 0
    ) < 1 ||
    normalizeBlocks(
      persistedBlocks
    ).length === 0
  );
}

export function canSwitchTextbookOutlineGroup({
  dirty,
  currentGroupId,
  nextGroupId,
}) {
  /*
   * Continuous Chapter V1 keeps every chapter/section group mounted
   * in one document. Changing outline focus therefore does not discard
   * unsaved chapter state; it only changes where the writer is looking.
   *
   * Keep the arguments in the contract because callers/tests already
   * describe the navigation decision, but no save barrier is required.
   */
  void dirty;
  void currentGroupId;
  void nextGroupId;

  return true;
}

export function initialTextbookOutlineGroupId(
  persistedBlocks,
  sections
) {
  const presentableBlocks =
    normalizeBlocks(
      persistedBlocks
    ).filter(
      (block) =>
        block.type === "figure"
          ? Boolean(
              clean(
                block.figureAssetId
              )
            )
          : Boolean(
              clean(
                block.text
              )
            )
    );

  if (
    presentableBlocks.some(
      (block) =>
        !clean(
          block.sectionItemId
        )
    )
  ) {
    return "";
  }

  const sectionIds =
    normalizeSections(
      sections
    ).map(
      (section) =>
        section.itemId
    );

  return (
    sectionIds.find(
      (sectionItemId) =>
        presentableBlocks.some(
          (block) =>
            clean(
              block.sectionItemId
            ) ===
              sectionItemId
        )
    ) ||
    ""
  );
}

const TEXTBOOK_OUTLINE_FOCUS_STORAGE_PREFIX =
  "nextscenes:textbook:outline-focus:";

function textbookOutlineFocusStorageKey(
  chapterItemId
) {
  const chapterId =
    clean(
      chapterItemId
    );

  return chapterId
    ? `${TEXTBOOK_OUTLINE_FOCUS_STORAGE_PREFIX}${chapterId}`
    : "";
}

export function restoredTextbookOutlineGroupId(
  chapterItemId,
  persistedBlocks,
  sections
) {
  const fallback =
    initialTextbookOutlineGroupId(
      persistedBlocks,
      sections
    );

  const storageKey =
    textbookOutlineFocusStorageKey(
      chapterItemId
    );

  if (
    !storageKey ||
    typeof window ===
      "undefined" ||
    !window.sessionStorage
  ) {
    return fallback;
  }

  try {
    const stored =
      window.sessionStorage
        .getItem(
          storageKey
        );

    if (stored === null) {
      return fallback;
    }

    const restored =
      stored ===
        "__chapter__"
        ? ""
        : clean(
            stored
          );

    if (!restored) {
      return "";
    }

    const sectionExists =
      normalizeSections(
        sections
      ).some(
        (section) =>
          clean(
            section?.itemId
          ) ===
            restored
      );

    return sectionExists
      ? restored
      : fallback;
  } catch {
    return fallback;
  }
}

function rememberTextbookOutlineGroupId(
  chapterItemId,
  groupItemId
) {
  const storageKey =
    textbookOutlineFocusStorageKey(
      chapterItemId
    );

  if (
    !storageKey ||
    typeof window ===
      "undefined" ||
    !window.sessionStorage
  ) {
    return;
  }

  try {
    window.sessionStorage
      .setItem(
        storageKey,
        clean(
          groupItemId
        ) ||
          "__chapter__"
      );
  } catch {
    // Focus memory is non-critical UI state.
  }
}


function newBlockId() {
  return [
    "textbook-block",
    Date.now()
      .toString(36),
    Math.random()
      .toString(36)
      .slice(2, 10),
  ].join("-");
}


function manuscriptBlockTextLength(
  block
) {
  if (
    !block ||
    !Array.isArray(
      block.runs
    )
  ) {
    return 0;
  }

  return block.runs.reduce(
    (
      total,
      run
    ) =>
      total +
      exactText(
        run?.text
      ).length,
    0
  );
}


function normalizeTextbookInsertionPoint(
  value
) {
  const source =
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
      ? value
      : {};

  const hasOffset =
    Object.prototype
      .hasOwnProperty.call(
        source,
        "manuscriptOffset"
      );

  const numericOffset =
    hasOffset
      ? Number(
          source
            .manuscriptOffset
        )
      : Number.NaN;

  return {
    afterBlockId:
      clean(
        source.afterBlockId ||
        source.blockId
      ),

    manuscriptBlockId:
      clean(
        source
          .manuscriptBlockId
      ),

    manuscriptOffset:
      Number.isFinite(
        numericOffset
      )
        ? Math.max(
            0,
            Math.floor(
              numericOffset
            )
          )
        : null,

    workingPageIndex:
      Number.isInteger(Number(source.workingPageIndex))
        ? Math.max(0, Number(source.workingPageIndex))
        : null,

    sectionItemId:
      clean(
        source
          .sectionItemId
      ),

    label:
      clean(
        source.label
      ),
  };
}


function preparedTextbookRichFragment(
  anchor,
  manuscriptContent,
  blockId
) {
  const prepared =
    prepareTextbookRichManuscriptBlock(
      {
        ...anchor,
        blockId,
      },
      manuscriptContent
    );

  return (
    prepared?.ok &&
    prepared?.value
  )
    ? {
        ...anchor,
        ...prepared.value,
        blockId,
      }
    : null;
}


/*
 * Inserts one Textbook content block at the writer's selected live-page
 * position. Imported rich manuscript remains authoritative: when the
 * insertion falls inside that manuscript, the surrounding Textbook block
 * is split into two safe rich-manuscript fragments and the new object is
 * placed between them.
 */
export function insertTextbookBlockAtInsertionPoint(
  value,
  sections,
  insertionPoint,
  newBlock
) {
  const ordered =
    canonicalBlocks(
      value,
      sections
    );

  const point =
    normalizeTextbookInsertionPoint(
      insertionPoint
    );

  const targetIndex =
    ordered.findIndex(
      (item) =>
        clean(
          item?.blockId
        ) ===
        point.afterBlockId
    );

  if (targetIndex < 0) {
    return canonicalBlocks(
      [
        ...ordered,
        {
          ...newBlock,
          sectionItemId:
            clean(
              newBlock
                ?.sectionItemId
            ) ||
            point.sectionItemId,
        },
      ],
      sections
    );
  }

  const anchor =
    ordered[targetIndex];

  const insertedBlock = {
    ...newBlock,

    sectionItemId:
      clean(
        anchor
          ?.sectionItemId
      ),
  };

  const richBlocks =
    Array.isArray(
      anchor
        ?.manuscriptContent
        ?.blocks
    )
      ? anchor
          .manuscriptContent
          .blocks
      : [];

  const canSplitRichBlock =
    point.manuscriptBlockId &&
    richBlocks.length > 0 &&
    ![
      "figure",
      "equation",
      "table",
    ].includes(
      clean(
        anchor?.type
      )
    );

  if (canSplitRichBlock) {
    let workingContent =
      anchor.manuscriptContent;

    let workingBlocks =
      richBlocks;

    let internalIndex =
      workingBlocks.findIndex(
        (item) =>
          clean(
            item?.id
          ) ===
          point
            .manuscriptBlockId
      );

    if (internalIndex >= 0) {
      const internalBlock =
        workingBlocks[
          internalIndex
        ];

      const textLength =
        manuscriptBlockTextLength(
          internalBlock
        );

      const requestedOffset =
        point.manuscriptOffset ===
          null
          ? textLength
          : Math.min(
              point
                .manuscriptOffset,
              textLength
            );

      let boundary =
        requestedOffset <= 0
          ? internalIndex
          : internalIndex + 1;

      if (
        requestedOffset > 0 &&
        requestedOffset <
          textLength &&
        clean(
          internalBlock
            ?.type
        ) !== "page_break"
      ) {
        const split =
          splitManuscriptEditorBlock(
            workingContent,
            point
              .manuscriptBlockId,
            requestedOffset
          );

        if (
          split?.content &&
          Array.isArray(
            split
              .content
              .blocks
          )
        ) {
          workingContent =
            split.content;

          workingBlocks =
            split
              .content
              .blocks;

          internalIndex =
            workingBlocks
              .findIndex(
                (item) =>
                  clean(
                    item?.id
                  ) ===
                  point
                    .manuscriptBlockId
              );

          if (
            internalIndex >=
            0
          ) {
            boundary =
              internalIndex +
              1;
          }
        }
      }

      const beforeBlocks =
        workingBlocks.slice(
          0,
          boundary
        );

      const afterBlocks =
        workingBlocks.slice(
          boundary
        );

      if (
        beforeBlocks.length > 0 ||
        afterBlocks.length > 0
      ) {
        const replacement = [];

        if (
          beforeBlocks.length >
          0
        ) {
          const before =
            preparedTextbookRichFragment(
              anchor,
              {
                ...workingContent,
                blocks:
                  beforeBlocks,
              },
              anchor.blockId
            );

          if (!before) {
            return ordered;
          }

          replacement.push(
            before
          );
        }

        replacement.push(
          insertedBlock
        );

        if (
          afterBlocks.length >
          0
        ) {
          const afterId =
            beforeBlocks.length >
            0
              ? newBlockId()
              : anchor.blockId;

          const after =
            preparedTextbookRichFragment(
              anchor,
              {
                ...workingContent,
                blocks:
                  afterBlocks,
              },
              afterId
            );

          if (!after) {
            return ordered;
          }

          replacement.push(
            after
          );
        }

        /*
         * A saved legacy side Figure may immediately follow this rich Text
         * block and anchor to one of its internal paragraphs. If insertion
         * splits that Text block after the anchored paragraph, leaving the
         * Figure in its old array position would put the new Table/content
         * and after-fragment between the anchor and Figure. The backend
         * correctly rejects that state.
         *
         * Preserve the existing Figure authority instead: when the legacy
         * anchor now belongs to the before-fragment, keep that Figure
         * immediately after the before-fragment and place the new content
         * after the Figure. If the anchor belongs to the after-fragment,
         * the existing ordering already remains valid.
         */
        const legacyFollower =
          ordered[
            targetIndex + 1
          ];

        const legacyFollowerAnchorId =
          clean(
            legacyFollower
              ?.figureTextFlowAnchorId
          );

        const beforeHasLegacyFigureAnchor =
          clean(
            legacyFollower?.type
          ) === "figure" &&
          legacyFollowerAnchorId &&
          clean(
            legacyFollower
              ?.sectionItemId
          ) ===
            clean(
              anchor
                ?.sectionItemId
            ) &&
          beforeBlocks.some(
            (item) =>
              clean(
                item?.id
              ) ===
              legacyFollowerAnchorId
          );

        if (
          beforeHasLegacyFigureAnchor
        ) {
          replacement.splice(
            1,
            0,
            legacyFollower
          );
        }

        const next = [
          ...ordered,
        ];

        next.splice(
          targetIndex,
          beforeHasLegacyFigureAnchor
            ? 2
            : 1,
          ...replacement
        );

        return canonicalBlocks(
          next,
          sections
        );
      }
    }
  }

  const next = [
    ...ordered,
  ];

  next.splice(
    targetIndex + 1,
    0,
    insertedBlock
  );

  return canonicalBlocks(
    next,
    sections
  );
}


function freshKey(prefix) {
  try {
    if (
      typeof window !== "undefined" &&
      window.crypto &&
      typeof window.crypto
        .randomUUID === "function"
    ) {
      return `${prefix}-${window.crypto.randomUUID()}`;
    }
  } catch {
    // Random UUID is convenience only; server authority is unchanged.
  }

  return `${prefix}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;
}


function placeholderForType(
  type,
  isFR
) {
  if (
    type ===
      "heading"
  ) {
    return isFR
      ? "Donnez un nom à ce titre…"
      : "Name this heading…";
  }

  if (
    type ===
      "learning_objectives"
  ) {
    return isFR
      ? "Ajoutez les objectifs d’apprentissage…"
      : "Add the learning objectives…";
  }

  if (
    type ===
      "definition"
  ) {
    return isFR
      ? "Ajoutez la définition…"
      : "Add the definition…";
  }

  if (
    type ===
      "worked_example"
  ) {
    return isFR
      ? "Développez l’exemple ou l’étude de cas étape par étape…"
      : "Develop the example or case study step by step…";
  }

  if (
    type ===
      "exercise"
  ) {
    return isFR
      ? "Rédigez l’exercice ou les questions de pratique…"
      : "Write the exercise or practice questions…";
  }

  if (
    type ===
      "reference"
  ) {
    return isFR
      ? "Ajoutez la référence, la citation ou les détails de la source…"
      : "Add the reference, citation, or source details…";
  }

  if (
    type ===
      "equation"
  ) {
    return isFR
      ? "Écrivez l’équation ou la formule…"
      : "Write the equation or formula…";
  }

  if (
    type ===
      "note"
  ) {
    return isFR
      ? "Ajoutez une note…"
      : "Add a note…";
  }

  if (
    type ===
      "summary"
  ) {
    return isFR
      ? "Résumez les idées essentielles du chapitre…"
      : "Summarize the chapter’s essential ideas…";
  }

  return isFR
    ? "Commencez à écrire…"
    : "Start writing…";
}

function chapterAnchorId(
  kind,
  value
) {
  return [
    "textbook-chapter",
    kind,
    encodeURIComponent(
      clean(value) || "chapter"
    ),
  ].join("-");
}

function TextbookProposalSnapshotReadOnly({
  contribution,
  lang = "en",
}) {
  const snapshot =
    contribution
      ?.contentSnapshot ||
    {};

  const text =
    exactText(
      snapshot?.text
    );

  const manuscriptContent =
    snapshot
      ?.manuscriptContent;

  if (
    manuscriptContent &&
    typeof manuscriptContent ===
      "object" &&
    !Array.isArray(
      manuscriptContent
    )
  ) {
    return (
      <ChildrensBookManuscriptReadOnly
        content={
          manuscriptContent
        }
        fallbackText={text}
        lang={lang}
      />
    );
  }

  return text;
}


function TextbookRichBlockEditor({
  block,
  disabled = false,
  isFR = false,
  flowAnchorBlockId = "",
  flowContent = null,
  compactToolbar = false,
  saveActionLabel = "",
  stickyToolbar = false,
  enableWholeDocumentJustify = false,
  editorSessionKey = "",
  onChange,
}) {
  const liveEntry =
    useMemo(
      () => {
        const value = {
          id:
            clean(
              block?.blockId
            ),

          manuscriptText:
            exactText(
              block?.text
            ),
        };

        if (
          block?.manuscriptContent !==
            undefined
        ) {
          value.manuscriptContent =
            block.manuscriptContent;
        }

        return value;
      },
      [
        block?.blockId,
        block?.text,
        block?.manuscriptContent,
      ]
    );

  const isolatedEntryRef =
    useRef({
      key: "",
      entry: null,
    });

  const sessionKey =
    clean(
      editorSessionKey
    );

  if (
    sessionKey &&
    isolatedEntryRef.current.key !==
      sessionKey
  ) {
    isolatedEntryRef.current = {
      key: sessionKey,
      entry: liveEntry,
    };
  }

  const entry =
    sessionKey
      ? (
          isolatedEntryRef.current.entry ||
          liveEntry
        )
      : liveEntry;

  return (
    <ChildrensBookManuscriptEditor
      lang={isFR ? "fr" : "en"}
      entry={entry}
      disabled={disabled}
      flowAnchorBlockId={
        flowAnchorBlockId
      }
      flowContent={
        flowContent
      }
      saveActionLabel={
        saveActionLabel ||
        (
          isFR
            ? "Enregistrer le chapitre"
            : "Save Chapter"
        )
      }
      compactToolbar={
        compactToolbar
      }
      enableHighlight={true}
      stickyToolbar={
        stickyToolbar
      }
      enableWholeDocumentJustify={
        enableWholeDocumentJustify
      }
      embeddedFlow={true}
      onChange={onChange}
    />
  );
}

function TextbookChapterRichEditor({
  blocks,
  disabled = false,
  isFR = false,
  workingPageGuideCount = 1,
  workingPageBreakBoundaryIds = [],
  renderBoundary,
  onChange,
}) {
  const entry =
    useMemo(
      () => ({
        id:
          "textbook-chapter-canon",
        manuscriptContent:
          buildTextbookChapterEditorContent(
            blocks
          ),
      }),
      [blocks]
    );

  return (
    <ChildrensBookManuscriptEditor
      lang={isFR ? "fr" : "en"}
      entry={entry}
      disabled={disabled}
      saveActionLabel={
        isFR
          ? "Enregistrer le chapitre"
          : "Save Chapter"
      }
      enableHighlight={true}
      enableWholeDocumentJustify={true}
      embeddedFlow={true}
      compactContinuousParagraphSpacing={true}
      boundedSurface={true}
      workingPageGuideCount={
        workingPageGuideCount
      }
      workingPageBreakBoundaryIds={
        workingPageBreakBoundaryIds
      }
      workingPageGuideLabel={
        isFR
          ? "Les lignes indiquent les limites approximatives des pages de travail. Les limites finales sont fixées pendant la préparation de publication."
          : "Lines indicate approximate working-page boundaries. Final page boundaries are set during publication preparation."
      }
      renderPageBreak={
        renderBoundary
      }
      onChange={onChange}
    />
  );
}


function TextbookEquationEditor({
  block,
  disabled = false,
  isFR = false,
  onChange,
}) {
  const equation =
    normalizeTextbookEquation(
      block?.equation,
      block?.text
    );

  function updateEquation(
    field,
    value
  ) {
    const next = {
      ...equation,
      [field]:
        field === "number"
          ? String(
              value ?? ""
            )
          : exactText(
              value
            ),
      notation:
        "plain_text",
    };

    onChange?.({
      text:
        next.source,
      equation:
        next,
      manuscriptContent:
        undefined,
      figureAssetId:
        "",
    });
  }

  return (
    <div
      data-textbook-equation-editor="true"
      style={{
        display: "grid",
        gap: 12,
      }}
    >
      <label
        style={{
          display: "grid",
          gap: 6,
          fontWeight: 800,
        }}
      >
        <span>
          {isFR
            ? "Équation ou formule"
            : "Equation or formula"}
        </span>

        <textarea
          className="ns-input"
          rows={3}
          maxLength={4000}
          value={
            equation.source
          }
          disabled={disabled}
          onChange={(event) =>
            updateEquation(
              "source",
              event.target.value
            )
          }
          placeholder={
            isFR
              ? "Ex. E = mc² ou a² + b² = c²"
              : "e.g. E = mc² or a² + b² = c²"
          }
          style={{
            width: "100%",
            minHeight: 88,
            resize: "vertical",
            fontFamily:
              '"Cambria Math", "STIX Two Math", "Times New Roman", serif',
            fontSize: 18,
            lineHeight: 1.5,
          }}
        />
      </label>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(160px, 0.45fr) minmax(240px, 1fr)",
          gap: 12,
        }}
      >
        <label
          style={{
            display: "grid",
            gap: 6,
            fontWeight: 800,
          }}
        >
          <span>
            {isFR
              ? "Numéro d’équation (facultatif)"
              : "Equation number (optional)"}
          </span>

          <input
            className="ns-input"
            type="text"
            maxLength={80}
            value={
              equation.number
            }
            disabled={disabled}
            onChange={(event) =>
              updateEquation(
                "number",
                event.target.value
              )
            }
            placeholder="2.1"
          />
        </label>

        <label
          style={{
            display: "grid",
            gap: 6,
            fontWeight: 800,
          }}
        >
          <span>
            {isFR
              ? "Description accessible (facultatif)"
              : "Accessible description (optional)"}
          </span>

          <input
            className="ns-input"
            type="text"
            maxLength={2000}
            value={
              equation
                .accessibleText
            }
            disabled={disabled}
            onChange={(event) =>
              updateEquation(
                "accessibleText",
                event.target.value
              )
            }
            placeholder={
              isFR
                ? "Décrivez l’équation en mots si cela aide le lecteur."
                : "Describe the equation in words when that helps the reader."
            }
          />
        </label>
      </div>

      <p
        className="ns-card-meta"
        style={{
          margin: 0,
          lineHeight: 1.5,
        }}
      >
        {isFR
          ? "V1 utilise une notation texte modifiable. Vous pouvez saisir des symboles ordinaires ou Unicode. Les équations LaTeX et les équations intégrées dans une phrase viendront dans une étape distincte."
          : "V1 uses editable text notation. You can type ordinary keyboard or Unicode symbols. LaTeX rendering and equations embedded inside a sentence will come in a separate step."}
      </p>

      {clean(
        equation.source
      ) ? (
        <TextbookEquationPreview
          equation={equation}
          isFR={isFR}
        />
      ) : null}
    </div>
  );
}



function TextbookTableEditor({
  block,
  disabled = false,
  isFR = false,
  onChange,
}) {
  const table =
    normalizeTextbookTable(
      block?.table
    );

  const rowCount =
    table.rows.length;

  const columnCount =
    table.rows[0]
      ?.length || 1;

  function commit(
    nextTable
  ) {
    const normalized =
      normalizeTextbookTable(
        nextTable
      );

    onChange?.({
      text:
        textbookTableCompatibilityText(
          normalized
        ),
      table:
        normalized,
      manuscriptContent:
        undefined,
      equation:
        undefined,
      figureAssetId:
        "",
    });
  }

  function updateCell(
    rowIndex,
    columnIndex,
    value
  ) {
    const rows =
      table.rows.map(
        (row) => [...row]
      );

    rows[rowIndex][
      columnIndex
    ] =
      exactText(value);

    commit({
      ...table,
      rows,
    });
  }

  return (
    <div
      data-textbook-table-editor="true"
      style={{
        display: "grid",
        gap: 14,
      }}
    >
      <label
        style={{
          display: "grid",
          gap: 6,
          fontWeight: 800,
        }}
      >
        <span>
          {isFR
            ? "Titre du tableau (facultatif)"
            : "Table caption (optional)"}
        </span>

        <input
          className="ns-input"
          type="text"
          maxLength={500}
          value={table.caption}
          disabled={disabled}
          onChange={(event) =>
            commit({
              ...table,
              caption:
                event.target.value,
            })
          }
        />
      </label>

      <label
        style={{
          display: "flex",
          gap: 8,
          alignItems:
            "center",
          fontWeight: 800,
        }}
      >
        <input
          type="checkbox"
          checked={
            table.headerRow
          }
          disabled={disabled}
          onChange={(event) =>
            commit({
              ...table,
              headerRow:
                event.target
                  .checked,
            })
          }
        />
        <span>
          {isFR
            ? "La première ligne contient les en-têtes"
            : "First row contains column headings"}
        </span>
      </label>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            disabled ||
            rowCount >= 50
          }
          onClick={() =>
            commit({
              ...table,
              rows: [
                ...table.rows,
                Array(
                  columnCount
                ).fill(""),
              ],
            })
          }
        >
          {isFR
            ? "+ Ligne"
            : "+ Row"}
        </button>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            disabled ||
            rowCount <= 1
          }
          onClick={() =>
            commit({
              ...table,
              rows:
                table.rows.slice(
                  0,
                  -1
                ),
            })
          }
        >
          {isFR
            ? "Retirer la ligne"
            : "Remove row"}
        </button>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            disabled ||
            columnCount >= 12
          }
          onClick={() =>
            commit({
              ...table,
              rows:
                table.rows.map(
                  (row) => [
                    ...row,
                    "",
                  ]
                ),
            })
          }
        >
          {isFR
            ? "+ Colonne"
            : "+ Column"}
        </button>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            disabled ||
            columnCount <= 1
          }
          onClick={() =>
            commit({
              ...table,
              rows:
                table.rows.map(
                  (row) =>
                    row.slice(
                      0,
                      -1
                    )
                ),
            })
          }
        >
          {isFR
            ? "Retirer la colonne"
            : "Remove column"}
        </button>
      </div>

      <div
        style={{
          overflowX: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse:
              "collapse",
            tableLayout:
              "fixed",
          }}
        >
          <tbody>
            {table.rows.map(
              (row, rowIndex) => (
                <tr
                  key={`table-editor-row-${rowIndex}`}
                >
                  {row.map(
                    (
                      cell,
                      columnIndex
                    ) => (
                      <td
                        key={`table-editor-cell-${rowIndex}-${columnIndex}`}
                        style={{
                          border:
                            "1px solid #cbd5e1",
                          padding: 4,
                          background:
                            table.headerRow &&
                            rowIndex === 0
                              ? "#f8fafc"
                              : "#ffffff",
                        }}
                      >
                        <input
                          className="ns-input"
                          type="text"
                          maxLength={2000}
                          value={cell}
                          disabled={
                            disabled
                          }
                          aria-label={
                            isFR
                              ? `Ligne ${rowIndex + 1}, colonne ${columnIndex + 1}`
                              : `Row ${rowIndex + 1}, column ${columnIndex + 1}`
                          }
                          onChange={
                            (
                              event
                            ) =>
                              updateCell(
                                rowIndex,
                                columnIndex,
                                event
                                  .target
                                  .value
                              )
                          }
                        />
                      </td>
                    )
                  )}
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      <TextbookTablePreview
        table={table}
        isFR={isFR}
      />
    </div>
  );
}

export default function TextbookChapterWorkspace({
  project,
  formatPlan = {},
  chapter,
  chapterDraft,
  proposalRound = null,
  proposalRoundBlocksCanon = false,
  canManageProposalRound = false,
  canSubmitProposal = false,
  myProposals = [],
  proposalReviewProposals = [],
  figureAssets = [],
  figureLoadError = "",
  loading = false,
  loadError = "",
  onReload,
  onSave,
  onLoadVersionHistory,
  onPreviewVersion,
  onOpenProposalRound,
  onCloseProposalRound,
  onSubmitProposal,
  onDecideProposal,
  onUploadFigure,
  onCreateFigureLink,
  onUpdateFigure,
  onLoadFigureFile,
  onBackToBookMap,
  onRegisterLeaveGuard,
  canEdit = true,
  isFR = false,
}) {
  const sections =
    useMemo(
      () =>
        normalizeSections(
          chapter?.sections
        ),
      [chapter?.sections]
    );

  const persisted =
    useMemo(
      () =>
        canonicalBlocks(
          chapterDraft
            ?.blocks,
          sections
        ),
      [
        chapterDraft?.blocks,
        sections,
      ]
    );

  const persistedSectionLayout =
    useMemo(
      () =>
        normalizeTextbookSectionLayout(
          chapterDraft
            ?.sectionLayout,
          sections
        ),
      [
        chapterDraft
          ?.sectionLayout,
        sections,
      ]
    );

  const [blocks, setBlocks] =
    useState(persisted);

  const [
    savedBlocks,
    setSavedBlocks,
  ] = useState(persisted);

  const [
    sectionLayout,
    setSectionLayout,
  ] = useState(
    persistedSectionLayout
  );

  const [
    savedSectionLayout,
    setSavedSectionLayout,
  ] = useState(
    persistedSectionLayout
  );

  const [saving, setSaving] =
    useState(false);

  const pendingLeaveAction =
    useRef(null);

  const [versionHistoryOpen, setVersionHistoryOpen] =
    useState(false);

  const [
    saveError,
    setSaveError,
  ] = useState("");

  const [
    saveMessage,
    setSaveMessage,
  ] = useState("");

  const [
    proposalOpeningReason,
    setProposalOpeningReason,
  ] = useState("");

  const [
    proposalScopeMode,
    setProposalScopeMode,
  ] = useState(
    "whole_chapter"
  );

  const [
    proposalScopeSectionItemId,
    setProposalScopeSectionItemId,
  ] = useState("");

  const [
    proposalScopeSelectedPassage,
    setProposalScopeSelectedPassage,
  ] = useState("");

  const [
    proposalClosingReason,
    setProposalClosingReason,
  ] = useState("");

  const [
    showProposalOpenForm,
    setShowProposalOpenForm,
  ] = useState(false);

  const [
    showProposalCloseForm,
    setShowProposalCloseForm,
  ] = useState(false);

  const [
    proposalWorking,
    setProposalWorking,
  ] = useState(false);

  const [
    proposalError,
    setProposalError,
  ] = useState("");

  const [
    proposalNotice,
    setProposalNotice,
  ] = useState("");


  const [
    contributorProposalText,
    setContributorProposalText,
  ] = useState("");

  const [
    contributorProposalManuscriptContent,
    setContributorProposalManuscriptContent,
  ] = useState(undefined);

  const [
    contributorProposalWorking,
    setContributorProposalWorking,
  ] = useState(false);

  const [
    contributorProposalError,
    setContributorProposalError,
  ] = useState("");

  const [
    contributorProposalComposerOpen,
    setContributorProposalComposerOpen,
  ] = useState(false);

  const [
    contributorSubmitConfirmOpen,
    setContributorSubmitConfirmOpen,
  ] = useState(false);

  const [
    contributorProposalReviewId,
    setContributorProposalReviewId,
  ] = useState("");

  const [
    contributorChapterReferenceOpen,
    setContributorChapterReferenceOpen,
  ] = useState(false);

  const [
    contributorChapterReferencePartId,
    setContributorChapterReferencePartId,
  ] = useState("");

  const [
    ownerProposalIndex,
    setOwnerProposalIndex,
  ] = useState(0);

  const ownerProposalShelfRef =
    useRef(null);

  const [
    ownerProposalReviewOpen,
    setOwnerProposalReviewOpen,
  ] = useState(false);

  const [
    ownerChapterReferenceOpen,
    setOwnerChapterReferenceOpen,
  ] = useState(false);

  const [
    ownerChapterReferencePartId,
    setOwnerChapterReferencePartId,
  ] = useState("");


  const [
    ownerDecisionWorking,
    setOwnerDecisionWorking,
  ] = useState(false);

  const [
    ownerDecisionError,
    setOwnerDecisionError,
  ] = useState("");

  const [
    ownerRejectAllConfirm,
    setOwnerRejectAllConfirm,
  ] = useState(false);

  const [
    ownerApproveConfirmProposalId,
    setOwnerApproveConfirmProposalId,
  ] = useState("");

  const proposalOpenKeyRef =
    useRef(
      freshKey(
        "textbook-chapter-round-open"
      )
    );


  const contributorProposalKeyRef =
    useRef(
      freshKey(
        "textbook-chapter-proposal"
      )
    );

  const [
    pendingLeave,
    setPendingLeave,
  ] = useState(false);

  const [
    pendingCancelEditing,
    setPendingCancelEditing,
  ] = useState(false);

  const [
    ,
    setCollapsedGroups,
  ] = useState(() => new Set());

  const [
    editMode,
    setEditMode,
  ] = useState(() =>
    shouldOpenTextbookChapterInEditMode(
      chapterDraft?.revision,
      persisted
    )
  );

  const [
    activeGroupId,
    setActiveGroupId,
  ] = useState(() =>
    restoredTextbookOutlineGroupId(
      chapter?.itemId,
      persisted,
      sections
    )
  );

  const [
    liveSectionNavigation,
    setLiveSectionNavigation,
  ] = useState({
    sectionItemId: "",
    requestKey: 0,
  });

  const [
    activeBlockId,
    setActiveBlockId,
  ] = useState("");

  const [
    insertionPoint,
    setInsertionPoint,
  ] = useState(null);

  const [
    contentComposer,
    setContentComposer,
  ] = useState(null);

  const [
    contentComposerError,
    setContentComposerError,
  ] = useState("");

  const [
    insertionMessage,
    setInsertionMessage,
  ] = useState("");

  const [
    outlineNotice,
    setOutlineNotice,
  ] = useState("");

  const [
    workingPageMetrics,
    setWorkingPageMetrics,
  ] = useState({
    pageCount: 1,
    sectionPageRanges: {},
    pageBlockIds: {},
  });

  const [
    activeWorkingPageIndex,
    setActiveWorkingPageIndex,
  ] = useState(0);

  const handleWorkingPageMetricsChange =
    useCallback(
      (metrics) => {
        const next = {
          pageCount:
            Math.max(
              1,
              Number(metrics?.pageCount) || 1
            ),
          sectionPageRanges:
            metrics?.sectionPageRanges &&
            typeof metrics.sectionPageRanges === "object"
              ? metrics.sectionPageRanges
              : {},
          pageBlockIds:
            metrics?.pageBlockIds &&
            typeof metrics.pageBlockIds === "object"
              ? metrics.pageBlockIds
              : {},
        };

        setWorkingPageMetrics(
          (current) =>
            JSON.stringify(current) === JSON.stringify(next)
              ? current
              : next
        );

        setActiveWorkingPageIndex(
          (currentPageIndex) =>
            Math.max(
              0,
              Math.min(
                next.pageCount - 1,
                Number(
                  currentPageIndex
                ) || 0
              )
            )
        );
      },
      []
    );

  const [
    figureComposer,
    setFigureComposer,
  ] = useState(null);

  const [
    figureBusy,
    setFigureBusy,
  ] = useState(false);

  const [
    figureError,
    setFigureError,
  ] = useState("");

  const [
    figureMessage,
    setFigureMessage,
  ] = useState("");

  const [
    figureEditor,
    setFigureEditor,
  ] = useState(null);

  const [
    figureEditBusy,
    setFigureEditBusy,
  ] = useState(false);

  const [
    figureEditError,
    setFigureEditError,
  ] = useState("");

  const workspaceRef =
    useRef(null);

  const activeGroupChangeSourceRef =
    useRef("outline");

  useEffect(() => {
    setBlocks(persisted);
    setSavedBlocks(persisted);
    setSectionLayout(
      persistedSectionLayout
    );
    setSavedSectionLayout(
      persistedSectionLayout
    );
    setSaveError("");
    setEditMode(
      shouldOpenTextbookChapterInEditMode(
        chapterDraft?.revision,
        persisted
      )
    );
    setActiveGroupId(
      restoredTextbookOutlineGroupId(
        chapter?.itemId,
        persisted,
        sections
      )
    );
    setActiveBlockId("");
    setInsertionPoint(null);
    setContentComposer(null);
    setContentComposerError("");
    setInsertionMessage("");
    setOutlineNotice("");
    setWorkingPageMetrics({
      pageCount: 1,
      sectionPageRanges: {},
      pageBlockIds: {},
    });
    setActiveWorkingPageIndex(0);
  }, [
    chapter?.itemId,
    chapterDraft?.revision,
    persisted,
    persistedSectionLayout,
    sections,
  ]);

  useEffect(() => {
    setSaveMessage("");
    setPendingLeave(false);
    setPendingCancelEditing(false);
    setLiveSectionNavigation({
      sectionItemId: "",
      requestKey: 0,
    });
    setCollapsedGroups(
      new Set()
    );
    setFigureComposer(null);
    setFigureBusy(false);
    setFigureError("");
    setFigureMessage("");
    setFigureEditor(null);
    setFigureEditBusy(false);
    setFigureEditError("");
    setInsertionPoint(null);
    setContentComposer(null);
    setContentComposerError("");
    setInsertionMessage("");
  }, [chapter?.itemId]);


  useEffect(() => {
    rememberTextbookOutlineGroupId(
      chapter?.itemId,
      activeGroupId
    );

    const changeSource =
      activeGroupChangeSourceRef
        .current;

    activeGroupChangeSourceRef
      .current =
      "outline";

    if (
      changeSource ===
        "live"
    ) {
      return;
    }

    const nextLiveSectionId =
      clean(
        activeGroupId
      ) ||
        "__chapter__";

    setLiveSectionNavigation(
      (currentNavigation) => {
        if (
          currentNavigation
            .sectionItemId ===
            nextLiveSectionId &&
          currentNavigation
            .requestKey > 0
        ) {
          return currentNavigation;
        }

        return {
          sectionItemId:
            nextLiveSectionId,
          requestKey:
            currentNavigation
              .requestKey + 1,
        };
      }
    );
  }, [
    chapter?.itemId,
    activeGroupId,
  ]);


  useEffect(() => {
    setShowProposalOpenForm(
      false
    );
    setShowProposalCloseForm(
      false
    );
    setProposalOpeningReason(
      ""
    );
    setProposalClosingReason(
      ""
    );
    setProposalError("");
    setProposalNotice("");
  }, [
    chapter?.itemId,
    proposalRound?.id,
    proposalRound?.status,
  ]);



  useEffect(() => {
    setContributorProposalText(
      ""
    );
    setContributorProposalManuscriptContent(
      undefined
    );
    setContributorProposalWorking(
      false
    );
    setContributorProposalError(
      ""
    );
    setContributorProposalComposerOpen(
      false
    );
    setContributorSubmitConfirmOpen(
      false
    );
    setContributorProposalReviewId(
      ""
    );
    setContributorChapterReferenceOpen(
      false
    );
    setContributorChapterReferencePartId(
      ""
    );
    contributorProposalKeyRef.current =
      freshKey(
        "textbook-chapter-proposal"
      );
  }, [
    chapter?.itemId,
    proposalRound?.id,
    proposalRound?.status,
  ]);


  useEffect(() => {
    if (
      !chapter?.itemId ||
      typeof window ===
        "undefined"
    ) {
      return undefined;
    }

    const frame =
      window.requestAnimationFrame(
        () => {
          workspaceRef.current
            ?.scrollIntoView?.({
              block: "start",
              behavior: "auto",
            });
        }
      );

    return () =>
      window.cancelAnimationFrame(
        frame
      );
  }, [chapter?.itemId]);

  const revision =
    Number(
      chapterDraft
        ?.revision ?? 0
    );

  const current =
    useMemo(
      () =>
        canonicalBlocks(
          blocks,
          sections
        ),
      [blocks, sections]
    );

  const dirty =
    !sameBlocks(
      current,
      savedBlocks
    ) ||
    !sameTextbookSectionLayout(
      sectionLayout,
      savedSectionLayout
    );

  const canonNeedsConsolidation =
    textbookCanonNeedsConsolidation(
      current
    );


  const proposalRoundOpen =
    clean(
      proposalRound?.status
    ).toLowerCase() ===
    "open";

  const proposalInstruction =
    clean(
      proposalRound
        ?.openingReason
    );

  const proposalFocusMode =
    [
      "section",
      "selected_passage",
    ].includes(
      clean(
        proposalRound
          ?.scope
          ?.focusMode
      )
        .toLowerCase()
    )
      ? clean(
          proposalRound
            ?.scope
            ?.focusMode
        )
          .toLowerCase()
      : "whole_chapter";

  const proposalFocusSectionItemId =
    clean(
      proposalRound
        ?.scope
        ?.sectionItemId
    );

  const proposalFocusSectionTitle =
    clean(
      proposalRound
        ?.scope
        ?.sectionTitle
    );

  const proposalFocusSelectedPassage =
    clean(
      proposalRound
        ?.scope
        ?.selectedPassage
    );

  const proposalScopeSection =
    sections.find(
      (section) =>
        clean(
          section?.itemId
        ) ===
        clean(
          proposalScopeSectionItemId
        )
    ) ||
    null;

  const proposalScopeReady =
    proposalScopeMode ===
      "whole_chapter" ||
    (
      proposalScopeMode ===
        "section" &&
      Boolean(
        proposalScopeSection
      )
    ) ||
    (
      proposalScopeMode ===
        "selected_passage" &&
      Boolean(
        clean(
          proposalScopeSelectedPassage
        )
      )
    );

  const ownerReviewProposals =
    Array.isArray(
      proposalReviewProposals
    )
      ? proposalReviewProposals
      : [];

  const pendingOwnerReviewProposals =
    ownerReviewProposals
      .filter(
        (contribution) =>
          clean(
            contribution
              ?.decisionStatus
          ).toLowerCase() ===
          "pending"
      );

  const pendingOwnerProposalCount =
    pendingOwnerReviewProposals
      .length;

  const ownerChapterReferenceParts =
    useMemo(
      () => {
        const parts = [];

        const openingBlocks =
          current.filter(
            (block) =>
              !clean(
                block
                  ?.sectionItemId
              )
          );

        if (openingBlocks.length > 0) {
          parts.push({
            id:
              "__chapter_opening__",
            title:
              isFR
                ? "Ouverture du chapitre"
                : "Chapter opening",
            blocks:
              openingBlocks,
            sections: [],
          });
        }

        sections.forEach(
          (
            section,
            sectionIndex
          ) => {
            const sectionId =
              clean(
                section?.itemId
              );

            const sectionBlocks =
              current.filter(
                (block) =>
                  clean(
                    block
                      ?.sectionItemId
                  ) ===
                  sectionId
              );

            if (
              !sectionId ||
              sectionBlocks.length < 1
            ) {
              return;
            }

            parts.push({
              id:
                sectionId,
              title:
                clean(
                  section?.title
                ) ||
                (
                  isFR
                    ? `Section ${sectionIndex + 1}`
                    : `Section ${sectionIndex + 1}`
                ),
              blocks:
                sectionBlocks,
              sections: [
                section,
              ],
            });
          }
        );

        return parts;
      },
      [
        current,
        sections,
        isFR,
      ]
    );

  const ownerChapterReferencePart =
    ownerChapterReferenceParts
      .find(
        (part) =>
          clean(
            part?.id
          ) ===
          clean(
            ownerChapterReferencePartId
          )
      ) ||
    ownerChapterReferenceParts[
      0
    ] ||
    null;

  const ownerChapterReferenceBlocks =
    ownerChapterReferencePart
      ?.blocks ||
    [];

  const ownerChapterReferenceSections =
    ownerChapterReferencePart
      ?.sections ||
    [];

  const ownerChapterReferenceSectionLayout =
    sectionLayout.filter(
      (entry) =>
        ownerChapterReferenceSections
          .some(
            (section) =>
              clean(
                section?.itemId
              ) ===
              clean(
                entry
                  ?.sectionItemId
              )
          )
    );

  useEffect(() => {
    if (
      !ownerChapterReferenceOpen ||
      ownerChapterReferenceParts
        .length < 1
    ) {
      return;
    }

    const currentPartExists =
      ownerChapterReferenceParts
        .some(
          (part) =>
            clean(
              part?.id
            ) ===
            clean(
              ownerChapterReferencePartId
            )
        );

    if (currentPartExists) {
      return;
    }

    const preferredPartId =
      proposalFocusMode ===
        "section" &&
      ownerChapterReferenceParts
        .some(
          (part) =>
            clean(
              part?.id
            ) ===
            clean(
              proposalFocusSectionItemId
            )
        )
        ? proposalFocusSectionItemId
        : clean(
            ownerChapterReferenceParts[
              0
            ]?.id
          );

    setOwnerChapterReferencePartId(
      preferredPartId
    );
  }, [
    ownerChapterReferenceOpen,
    ownerChapterReferencePartId,
    ownerChapterReferenceParts,
    proposalFocusMode,
    proposalFocusSectionItemId,
  ]);

  useEffect(() => {
    setOwnerProposalIndex(0);
    setOwnerProposalReviewOpen(false);
    setOwnerChapterReferenceOpen(false);
    setOwnerChapterReferencePartId("");
    setOwnerDecisionWorking(false);
    setOwnerDecisionError("");
    setOwnerRejectAllConfirm(false);
    setOwnerApproveConfirmProposalId("");
  }, [
    chapter?.itemId,
    proposalRound?.id,
    proposalRound?.status,
  ]);

  useEffect(() => {
    setOwnerProposalIndex(
      (currentIndex) =>
        Math.max(
          0,
          Math.min(
            currentIndex,
            Math.max(
              0,
              pendingOwnerProposalCount - 1
            )
          )
        )
    );

    if (pendingOwnerProposalCount < 1) {
      setOwnerProposalReviewOpen(false);
    }
  }, [
    pendingOwnerProposalCount,
  ]);

  useEffect(() => {
    if (
      !ownerProposalReviewOpen ||
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    function closeOwnerProposalReviewOnEscape(
      event
    ) {
      if (event.key === "Escape") {
        setOwnerProposalReviewOpen(
          false
        );
      }
    }

    window.addEventListener(
      "keydown",
      closeOwnerProposalReviewOnEscape
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        closeOwnerProposalReviewOnEscape
      );
    };
  }, [
    ownerProposalReviewOpen,
  ]);


  useEffect(() => {
    if (
      !ownerChapterReferenceOpen ||
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    function closeOwnerReferenceOnEscape(
      event
    ) {
      if (event.key === "Escape") {
        setOwnerChapterReferenceOpen(
          false
        );
      }
    }

    window.addEventListener(
      "keydown",
      closeOwnerReferenceOnEscape
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        closeOwnerReferenceOnEscape
      );
    };
  }, [
    ownerChapterReferenceOpen,
  ]);


  useEffect(() => {
    const contributorOverlayOpen =
      Boolean(
        contributorSubmitConfirmOpen ||
        contributorProposalReviewId ||
        contributorChapterReferenceOpen
      );

    if (
      !contributorOverlayOpen ||
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    function closeContributorOverlayOnEscape(
      event
    ) {
      if (event.key !== "Escape") {
        return;
      }

      if (contributorSubmitConfirmOpen) {
        setContributorSubmitConfirmOpen(
          false
        );
        return;
      }

      if (contributorProposalReviewId) {
        setContributorProposalReviewId(
          ""
        );
        return;
      }

      setContributorChapterReferenceOpen(
        false
      );
    }

    window.addEventListener(
      "keydown",
      closeContributorOverlayOnEscape
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        closeContributorOverlayOnEscape
      );
    };
  }, [
    contributorSubmitConfirmOpen,
    contributorProposalReviewId,
    contributorChapterReferenceOpen,
  ]);


  useEffect(() => {
    setOwnerProposalIndex(
      (currentIndex) => {
        const maximum =
          Math.max(
            pendingOwnerReviewProposals
              .length - 1,
            0
          );

        return Math.min(
          Math.max(
            currentIndex,
            0
          ),
          maximum
        );
      }
    );
  }, [
    pendingOwnerReviewProposals.length,
  ]);

  const canStartProposalRound =
    Boolean(
      canManageProposalRound &&
      !proposalRoundOpen &&
      !proposalRoundBlocksCanon &&
      revision >= 1 &&
      !dirty &&
      !canonNeedsConsolidation &&
      typeof onOpenProposalRound ===
        "function"
    );

  const language =
    isFR ? "fr" : "en";

  const normalizedFigureAssets =
    useMemo(
      () =>
        normalizeFigureAssets(
          figureAssets
        ),
      [figureAssets]
    );

  const figureAssetsById =
    useMemo(
      () =>
        new Map(
          normalizedFigureAssets
            .map(
              (asset) => [
                asset.id,
                asset,
              ]
            )
        ),
      [
        normalizedFigureAssets,
      ]
    );

  const placedObjectLabels =
    useMemo(
      () =>
        textbookPlacedObjectLabels(
          current,
          sections,
          normalizedFigureAssets,
          isFR
        ),
      [
        current,
        sections,
        normalizedFigureAssets,
        isFR,
      ]
    );

  useEffect(() => {
    if (
      !dirty ||
      typeof window ===
        "undefined"
    ) {
      return undefined;
    }

    function protectUnsavedChapter(
      event
    ) {
      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener(
      "beforeunload",
      protectUnsavedChapter
    );

    return () => {
      window.removeEventListener(
        "beforeunload",
        protectUnsavedChapter
      );
    };
  }, [dirty]);

  const requestLeave =
    useCallback(
      (
        action
      ) => {
        if (
          typeof action !==
            "function"
        ) {
          return;
        }

        if (dirty) {
          pendingLeaveAction
            .current = action;
          setPendingLeave(true);
          return;
        }

        action();
      },
      [dirty]
    );

  function requestBackToBookMap() {
    requestLeave(
      onBackToBookMap
    );
  }

  function leaveWithoutSaving() {
    const action =
      pendingLeaveAction
        .current ||
      onBackToBookMap;

    pendingLeaveAction
      .current = null;
    setPendingLeave(false);
    action?.();
  }

  function stayInChapter() {
    pendingLeaveAction
      .current = null;
    setPendingLeave(false);
  }

  useEffect(() => {
    if (
      typeof onRegisterLeaveGuard !==
        "function"
    ) {
      return undefined;
    }

    onRegisterLeaveGuard(
      requestLeave
    );

    return () => {
      onRegisterLeaveGuard(
        null
      );
    };
  }, [
    onRegisterLeaveGuard,
    requestLeave,
  ]);

  function finishCancelEditing() {
    setBlocks(savedBlocks);
    setSectionLayout(
      savedSectionLayout
    );
    setPendingCancelEditing(false);
    setEditMode(false);
    setActiveBlockId("");
    setOutlineNotice("");
    setInsertionPoint(null);
    setContentComposer(null);
    setContentComposerError("");
    setInsertionMessage("");
    setFigureComposer(null);
    setFigureEditor(null);
    setSaveError("");
    setSaveMessage(
      isFR
        ? "Modifications non enregistrées annulées."
        : "Unsaved changes cancelled."
    );
  }

  function requestCancelEditing() {
    if (!canEdit || saving) {
      return;
    }

    if (dirty) {
      setPendingCancelEditing(true);
      return;
    }

    finishCancelEditing();
  }

  function keepEditing() {
    setPendingCancelEditing(false);
  }

  function clearMessages() {
    setSaveError("");
    setSaveMessage("");
  }

  function selectOutlineGroup(
    nextGroupId
  ) {
    const next =
      clean(nextGroupId);

    if (
      !canSwitchTextbookOutlineGroup({
        dirty,
        currentGroupId:
          activeGroupId,
        nextGroupId: next,
      })
    ) {
      return;
    }

    activeGroupChangeSourceRef
      .current =
      "outline";

    rememberTextbookOutlineGroupId(
      chapter?.itemId,
      next
    );
    setActiveGroupId(next);
    setOutlineNotice("");
    setInsertionMessage("");

    setLiveSectionNavigation(
      (currentNavigation) => ({
        sectionItemId:
          next || "__chapter__",
        requestKey:
          currentNavigation
            .requestKey + 1,
      })
    );

    const selectedGroup = [
      {
        itemId: "",
        title:
          isFR
            ? "Texte du chapitre"
            : "Chapter text",
      },
      ...sections,
    ].find(
      (group) =>
        clean(
          group?.itemId
        ) === next
    ) || null;

    const selectedBlocks =
      selectedGroup
        ? current.filter(
            (block) =>
              clean(
                block
                  ?.sectionItemId
              ) === next
          )
        : [];

    const selectedPoint =
      selectedGroup
        ? groupEndInsertionPoint(
            selectedBlocks,
            selectedGroup
          )
        : null;

    /*
     * Outline selection is also writing-target selection. This is
     * especially important for an empty section: the first + Add Content
     * action must inherit that section identity rather than falling back
     * to the end of the Chapter.
     */
    setInsertionPoint(
      editMode
        ? selectedPoint
        : null
    );

    /*
     * Navigation moves the writer through one continuous manuscript.
     * Empty sections have no editor anchor yet, so active-state feedback
     * and the contextual Add Content action below become the destination
     * until their first block is created.
     */
    if (
      selectedBlocks.length > 0 &&
      editMode
    ) {
      revealAndScrollTo(
        "editor-group",
        next || "__chapter__",
        next
      );
    }
  }

  function revealAndScrollTo(
    kind,
    value,
    groupItemId = null
  ) {
    if (groupItemId !== null) {
      const groupKey =
        clean(groupItemId) ||
        "__chapter__";

      setCollapsedGroups(
        (currentSet) => {
          if (
            !currentSet.has(
              groupKey
            )
          ) {
            return currentSet;
          }

          const next =
            new Set(currentSet);
          next.delete(groupKey);
          return next;
        }
      );
    }

    if (
      typeof window ===
        "undefined"
    ) {
      return;
    }

    window.requestAnimationFrame(
      () => {
        document
          .getElementById(
            chapterAnchorId(
              kind,
              value
            )
          )
          ?.scrollIntoView?.({
            block: "start",
            behavior: "smooth",
          });
      }
    );
  }

  function selectLiveInsertionPoint(
    value
  ) {
    if (
      !canEdit ||
      !editMode ||
      saving
    ) {
      return;
    }

    const point =
      normalizeTextbookInsertionPoint(
        value
      );

    if (!point.afterBlockId) {
      return;
    }

    setInsertionPoint(
      point
    );

    activeGroupChangeSourceRef
      .current =
      "live";

    rememberTextbookOutlineGroupId(
      chapter?.itemId,
      point.sectionItemId
    );

    setActiveGroupId(
      point.sectionItemId
    );

    setInsertionMessage(
      isFR
        ? "Point d’insertion sélectionné."
        : "Insertion point selected."
    );
  }


  function synchronizeLiveChapterSection(
    sectionItemId
  ) {
    const requested =
      clean(
        sectionItemId
      );

    const next =
      requested ===
        "__chapter__"
        ? ""
        : requested;

    if (
      next ===
        clean(
          activeGroupId
        )
    ) {
      return;
    }

    const selectedGroup =
      next
        ? (
            sections.find(
              (group) =>
                clean(
                  group?.itemId
                ) === next
            ) ||
            null
          )
        : {
            itemId: "",
            title:
              isFR
                ? "Texte du chapitre"
                : "Chapter text",
          };

    if (!selectedGroup) {
      return;
    }

    const selectedBlocks =
      current.filter(
        (block) =>
          clean(
            block
              ?.sectionItemId
          ) === next
      );

    activeGroupChangeSourceRef
      .current =
      "live";

    rememberTextbookOutlineGroupId(
      chapter?.itemId,
      next
    );

    setActiveGroupId(
      next
    );

    setOutlineNotice("");
    setInsertionMessage("");

    setInsertionPoint(
      editMode
        ? groupEndInsertionPoint(
            selectedBlocks,
            selectedGroup
          )
        : null
    );
  }


  function openContentComposer(
    requestedInsertionPoint =
      insertionPoint
  ) {
    if (
      !canEdit ||
      saving ||
      proposalRoundBlocksCanon
    ) {
      return;
    }

    const cursorPoint =
      normalizeTextbookInsertionPoint(
        requestedInsertionPoint
      );

    const hasCursorPoint =
      Boolean(
        cursorPoint
          .afterBlockId
      );

    const hasRequestedSectionPoint =
      Boolean(
        cursorPoint
          .sectionItemId
      );

    const hasRequestedPoint =
      hasCursorPoint ||
      hasRequestedSectionPoint ||
      Boolean(
        clean(
          cursorPoint.label
        )
      );

    const chapterPoint =
      chapterEndInsertionPoint(
        current
      );

    const point =
      hasRequestedPoint
        ? cursorPoint
        : chapterPoint;

    if (!point) {
      return;
    }

    setFigureComposer(null);
    setFigureError("");

    setContentComposer({
      insertionPoint:
        point,

      cursorInsertionPoint:
        hasCursorPoint
          ? cursorPoint
          : null,

      placementMode:
        hasCursorPoint
          ? "cursor"
          : hasRequestedSectionPoint
            ? "section_end"
            : "chapter_end",

      block: {
        blockId:
          newBlockId(),

        type:
          "paragraph",

        sectionItemId:
          point.sectionItemId,

        text: "",
      },
    });

    setContentComposerError("");
    setInsertionMessage("");
  }


  function closeContentComposer() {
    setContentComposer(null);
    setContentComposerError("");
  }


  function updateContentComposerBlock(
    patch
  ) {
    setContentComposer(
      (currentComposer) => {
        if (!currentComposer) {
          return currentComposer;
        }

        return {
          ...currentComposer,

          block: {
            ...currentComposer
              .block,
            ...patch,

            sectionItemId:
              patch?.sectionItemId !==
                undefined
                ? patch
                    .sectionItemId
                : currentComposer
                    .block
                    ?.sectionItemId,
          },
        };
      }
    );

    setContentComposerError("");
  }


  function changeContentComposerPlacement(
    placementMode
  ) {
    if (!contentComposer) {
      return;
    }

    const cursorPoint =
      normalizeTextbookInsertionPoint(
        contentComposer
          .cursorInsertionPoint
      );

    const sectionId =
      clean(
        cursorPoint
          .sectionItemId
      ) ||
      clean(
        contentComposer
          .insertionPoint
          ?.sectionItemId
      ) ||
      clean(
        activeGroupId
      );

    const point =
      placementMode === "cursor"
        ? (cursorPoint.afterBlockId ? cursorPoint : null)
        : placementMode === "page_end"
          ? workingPageSequenceEndInsertionPoint(
              activeWorkingPageIndex,
              current
            )
          : placementMode === "section_end"
            ? sectionEndInsertionPoint(sectionId, current)
            : chapterEndInsertionPoint(current);

    if (!point) {
      return;
    }

    setContentComposer(
      (currentComposer) =>
        currentComposer
          ? {
              ...currentComposer,
              placementMode,
              insertionPoint:
                point,
              block: {
                ...currentComposer
                  .block,
                sectionItemId:
                  point
                    .sectionItemId,
              },
            }
          : currentComposer
    );

    setContentComposerError("");
  }


  function changeContentComposerType(
    nextType
  ) {
    if (!contentComposer) {
      return;
    }

    if (
      nextType ===
        "figure"
    ) {
      const figureInsertionPoint =
        contentComposer
          .insertionPoint;

      const figureSectionItemId =
        clean(
          contentComposer
            .block
            ?.sectionItemId
        ) ||
        clean(
          figureInsertionPoint
            ?.sectionItemId
        );

      closeContentComposer();

      openFigureComposer(
        figureSectionItemId,
        figureInsertionPoint
      );

      return;
    }

    if (
      !TEXTBOOK_CHAPTER_BLOCK_TYPES
        .includes(
          nextType
        )
    ) {
      return;
    }

    const block =
      contentComposer.block;

    if (
      nextType ===
      block.type
    ) {
      return;
    }

    if (
      nextType ===
      "table"
    ) {
      const table =
        normalizeTextbookTable(
          block?.table
        );

      updateContentComposerBlock({
        type:
          "table",
        text:
          textbookTableCompatibilityText(
            table
          ),
        table,
        equation:
          undefined,
        manuscriptContent:
          undefined,
        figureAssetId:
          "",
      });

      return;
    }

    if (
      block.type ===
      "table"
    ) {
      updateContentComposerBlock({
        type:
          nextType,
        text: "",
        table:
          undefined,
        equation:
          undefined,
        manuscriptContent:
          undefined,
        figureAssetId:
          "",
      });

      return;
    }

    if (
      nextType ===
      "equation"
    ) {
      const equation =
        normalizeTextbookEquation(
          block?.equation,
          block?.text
        );

      updateContentComposerBlock({
        type:
          "equation",
        text:
          equation.source,
        equation,
        table:
          undefined,
        manuscriptContent:
          undefined,
        figureAssetId:
          "",
      });

      return;
    }

    if (
      block.type ===
      "equation"
    ) {
      const equation =
        normalizeTextbookEquation(
          block?.equation,
          block?.text
        );

      updateContentComposerBlock({
        type:
          nextType,
        text:
          equation.source,
        equation:
          undefined,
        manuscriptContent:
          undefined,
        figureAssetId:
          "",
      });

      return;
    }

    updateContentComposerBlock({
      type:
        nextType,
    });
  }


  function updateContentComposerRichBlock(
    editorValue
  ) {
    if (!contentComposer) {
      return;
    }

    const prepared =
      prepareTextbookRichManuscriptBlock(
        contentComposer.block,
        editorValue
          ?.manuscriptContent
      );

    if (
      !prepared.ok ||
      !prepared.value
    ) {
      setContentComposerError(
        isFR
          ? "Ce contenu n’a pas pu être préparé."
          : "This content could not be prepared."
      );
      return;
    }

    updateContentComposerBlock({
      text:
        prepared
          .value
          .text,

      manuscriptContent:
        prepared
          .value
          .manuscriptContent,
    });
  }


  function finishContentComposer() {
    if (
      !canEdit ||
      saving ||
      !contentComposer ||
      !clean(
        contentComposer
          .block
          ?.text
      )
    ) {
      return;
    }

    clearMessages();

    setBlocks(
      (items) =>
        insertTextbookBlockAtInsertionPoint(
          items,
          sections,
          contentComposer
            .insertionPoint,
          contentComposer
            .block
        )
    );

    setContentComposer(null);
    setContentComposerError("");
    setInsertionPoint(null);

    setInsertionMessage(
      isFR
        ? "Contenu inséré. Enregistrez le chapitre pour le conserver."
        : "Content inserted. Save Chapter to keep it."
    );
  }


  function renderContentComposer() {
    if (!contentComposer) {
      return null;
    }

    const block =
      contentComposer.block;

    return (
      <div
        data-textbook-floating-editor-backdrop="content"
        role="presentation"
        onMouseDown={(event) => {
          if (
            event.target ===
            event.currentTarget
          ) {
            closeContentComposer();
          }
        }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1200,
          display: "grid",
          placeItems: "center",
          padding: 20,
          background:
            "rgba(15,23,42,0.52)",
        }}
      >
      <div
        data-textbook-floating-editor="content"
        role="dialog"
        aria-modal="true"
        aria-label={
          isFR
            ? "Ajouter du contenu"
            : "Add Content"
        }
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform:
            "translate(-50%, -50%)",
          zIndex: 41,
          width:
            "min(780px, 94vw)",
          maxHeight: "86vh",
          overflowY: "auto",
          padding: 18,
          borderRadius: 18,
          border:
            "1px solid rgba(5,150,105,0.28)",
          background:
            "#ffffff",
          boxShadow:
            "0 28px 80px rgba(15,23,42,0.28)",
          display: "grid",
          gap: 14,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing:
                "0.09em",
              textTransform:
                "uppercase",
              color: "#047857",
            }}
          >
            {isFR
              ? "AJOUTER DU CONTENU"
              : "ADD CONTENT"}
          </div>

          <h4
            style={{
              margin:
                "4px 0 4px",
              fontSize: 20,
            }}
          >
            {isFR
              ? "Choisir l’emplacement"
              : "Choose placement"}
          </h4>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {clean(
              contentComposer
                .insertionPoint
                ?.label
            ) ||
              (
                isFR
                  ? "Point d’insertion sélectionné dans le chapitre."
                  : "Selected insertion point in the Chapter."
              )}
          </p>
        </div>

        <label
          style={{
            display: "grid",
            gap: 6,
            fontWeight: 800,
          }}
        >
          <span>
            {isFR
              ? "Type de contenu"
              : "Content type"}
          </span>

          <select
            className="ns-input"
            value={block.type}
            disabled={saving}
            onChange={(event) =>
              changeContentComposerType(
                event.target.value
              )
            }
          >
            {[
              ...TEXTBOOK_CHAPTER_BLOCK_TYPES,
              "figure",
            ].map(
              (type) => (
                <option
                  key={type}
                  value={type}
                >
                  {
                    TYPE_LABELS[
                      type
                    ][language]
                  }
                </option>
              )
            )}
          </select>
        </label>

        <label
          style={{
            display: "grid",
            gap: 6,
            fontWeight: 800,
          }}
        >
          <span>
            {isFR
              ? "Emplacement"
              : "Placement"}
          </span>

          <select
            className="ns-input"
            data-textbook-content-placement="true"
            value={
              contentComposer
                .placementMode ||
              "chapter_end"
            }
            disabled={saving}
            onChange={(event) =>
              changeContentComposerPlacement(
                event.target.value
              )
            }
          >
            {clean(
              contentComposer
                .cursorInsertionPoint
                ?.afterBlockId
            ) ? (
              <option value="cursor">
                {isFR
                  ? "Au curseur sélectionné"
                  : "At selected cursor"}
              </option>
            ) : null}


            {workingPageSequenceEndInsertionPoint(
              activeWorkingPageIndex,
              current
            ) ? (
              <option value="page_end">
                {isFR
                  ? `Fin de la page de travail ${activeWorkingPageIndex + 1}`
                  : `End of working page ${activeWorkingPageIndex + 1}`}
              </option>
            ) : null}

            {sectionEndInsertionPoint(
              clean(
                contentComposer
                  .cursorInsertionPoint
                  ?.sectionItemId
              ) ||
              clean(
                contentComposer
                  .insertionPoint
                  ?.sectionItemId
              ) ||
              clean(
                activeGroupId
              ),
              current
            ) ? (
              <option value="section_end">
                {isFR
                  ? "Fin de la section actuelle"
                  : "End of current section"}
              </option>
            ) : null}

            <option value="chapter_end">
              {isFR
                ? "Fin du chapitre"
                : "End of chapter"}
            </option>
          </select>

          <span
            className="ns-card-meta"
            style={{
              lineHeight: 1.45,
            }}
          >
            {clean(
              contentComposer
                .insertionPoint
                ?.label
            )}
          </span>

          {contentComposer
            .placementMode ===
              "page_end" ? (
            <span
              className="ns-card-meta"
              data-textbook-page-end-natural-flow-note="true"
              style={{
                lineHeight: 1.45,
              }}
            >
              {isFR
                ? "Insère le contenu après le dernier bloc actuellement mesuré sur cette page. Si le bloc ne tient pas dans l’espace restant, le flux normal continue sur la page suivante."
                : "Inserts after the last block currently measured on this page. If the block does not fit in the remaining space, normal flow continues on the next page."}
            </span>
          ) : null}
        </label>

        {block.type ===
        "equation" ? (
          <TextbookEquationEditor
            block={block}
            disabled={saving}
            isFR={isFR}
            onChange={
              updateContentComposerBlock
            }
          />
        ) : block.type ===
        "table" ? (
          <TextbookTableEditor
            block={block}
            disabled={saving}
            isFR={isFR}
            onChange={
              updateContentComposerBlock
            }
          />
        ) : (
          <TextbookRichBlockEditor
            block={block}
            disabled={saving}
            isFR={isFR}
            compactToolbar={true}
            editorSessionKey={
              `composer:${block.blockId}`
            }
            onChange={
              updateContentComposerRichBlock
            }
          />
        )}

        {contentComposerError ? (
          <div
            role="alert"
            style={{
              color: "#b91c1c",
              fontWeight: 700,
            }}
          >
            {contentComposerError}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            justifyContent:
              "flex-end",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={saving}
            onClick={
              closeContentComposer
            }
          >
            {isFR
              ? "Annuler"
              : "Cancel"}
          </button>

          <button
            className="ns-button ns-button-primary"
            type="button"
            disabled={
              saving ||
              !clean(
                block?.text
              )
            }
            onClick={
              finishContentComposer
            }
          >
            {isFR
              ? "Terminer la modification"
              : "Done Editing"}
          </button>
        </div>
      </div>
      </div>
    );
  }


  function chapterEndInsertionPoint(
    blocks = current
  ) {
    const candidates =
      Array.isArray(blocks)
        ? blocks.filter(
            (block) =>
              clean(
                block?.blockId
              )
          )
        : [];

    const lastBlock =
      candidates[
        candidates.length - 1
      ];

    if (!lastBlock) {
      return {
        afterBlockId: "",
        manuscriptBlockId: "",
        manuscriptOffset: null,
        sectionItemId: "",
        label:
          isFR
            ? "Fin du chapitre"
            : "End of chapter",
      };
    }

    return {
      afterBlockId:
        lastBlock.blockId,
      manuscriptBlockId: "",
      manuscriptOffset: null,
      sectionItemId:
        clean(
          lastBlock
            ?.sectionItemId
        ),
      label:
        isFR
          ? "Fin du chapitre"
          : "End of chapter",
    };
  }


  function workingPageSequenceEndInsertionPoint(
    pageIndex,
    blocks = current
  ) {
    const page =
      Number(
        pageIndex
      );

    if (
      !Number.isInteger(page) ||
      page < 0
    ) {
      return null;
    }

    const ids =
      Array.isArray(
        workingPageMetrics
          ?.pageBlockIds
          ?.[page]
      )
        ? workingPageMetrics
            .pageBlockIds[
              page
            ]
        : [];

    for (
      let index =
        ids.length - 1;
      index >= 0;
      index -= 1
    ) {
      const blockId =
        clean(
          ids[index]
        );

      if (!blockId) {
        continue;
      }

      const target =
        (
          Array.isArray(
            blocks
          )
            ? blocks
            : []
        ).find(
          (block) =>
            clean(
              block?.blockId
            ) ===
              blockId
        );

      if (!target) {
        continue;
      }

      return {
        afterBlockId:
          blockId,
        manuscriptBlockId:
          "",
        manuscriptOffset:
          null,
        workingPageIndex:
          page,
        sectionItemId:
          clean(
            target
              ?.sectionItemId
          ),
        label:
          isFR
            ? `Fin du contenu de la page de travail ${page + 1}`
            : `End of content on working page ${page + 1}`,
      };
    }

    return null;
  }


  function sectionEndInsertionPoint(
    sectionItemId,
    blocks = current
  ) {
    const sectionId =
      clean(
        sectionItemId
      );

    if (!sectionId) {
      return null;
    }

    const candidates =
      Array.isArray(blocks)
        ? blocks.filter(
            (block) =>
              clean(
                block?.blockId
              ) &&
              clean(
                block
                  ?.sectionItemId
              ) ===
                sectionId
          )
        : [];

    const lastBlock =
      candidates[
        candidates.length - 1
      ];

    const section =
      sections.find(
        (item) =>
          clean(
            item?.itemId
          ) ===
          sectionId
      );

    /*
     * An empty Book Structure section is still a real writing destination.
     * Giving it an insertion point lets the first Content block acquire the
     * correct sectionItemId before any persisted block exists there.
     */
    if (!lastBlock) {
      return {
        afterBlockId: "",
        manuscriptBlockId: "",
        manuscriptOffset: null,
        sectionItemId:
          sectionId,
        label:
          isFR
            ? `Fin de la section ${section?.title || ""}`.trim()
            : `End of section ${section?.title || ""}`.trim(),
      };
    }

    return {
      afterBlockId:
        lastBlock.blockId,
      manuscriptBlockId: "",
      manuscriptOffset: null,
      sectionItemId:
        sectionId,
      label:
        isFR
          ? `Fin de la section ${section?.title || ""}`.trim()
          : `End of section ${section?.title || ""}`.trim(),
    };
  }


  function groupEndInsertionPoint(
    blocks,
    group
  ) {
    const candidates =
      Array.isArray(blocks)
        ? blocks.filter(
            (block) =>
              clean(
                block?.blockId
              )
          )
        : [];

    const lastBlock =
      candidates[
        candidates.length - 1
      ];

    if (!lastBlock) {
      return {
        afterBlockId: "",
        manuscriptBlockId: "",
        manuscriptOffset: null,
        sectionItemId:
          clean(
            group?.itemId
          ),
        label:
          clean(
            group?.itemId
          )
            ? (
                isFR
                  ? `Fin de la section ${group?.title || ""}`.trim()
                  : `End of section ${group?.title || ""}`.trim()
              )
            : (
                isFR
                  ? "Fin du texte du chapitre"
                  : "End of Chapter text"
              ),
      };
    }

    return {
      afterBlockId:
        lastBlock.blockId,
      manuscriptBlockId: "",
      manuscriptOffset: null,
      sectionItemId:
        clean(
          group?.itemId
        ),
      label:
        clean(
          group?.itemId
        )
          ? (
              isFR
                ? `Fin de la section ${group?.title || ""}`.trim()
                : `End of section ${group?.title || ""}`.trim()
            )
          : (
              isFR
                ? "Fin du texte du chapitre"
                : "End of Chapter text"
            ),
    };
  }



  function updateBlock(
    blockId,
    patch
  ) {
    if (!canEdit) {
      return;
    }

    clearMessages();

    setBlocks(
      (items) =>
        items.map(
          (item) =>
            item.blockId ===
            blockId
              ? {
                  ...item,
                  ...patch,
                }
              : item
        )
    );
  }

  function updateRichBlock(
    block,
    editorValue
  ) {
    if (
      !canEdit ||
      !block ||
      block.type === "figure" ||
      block.type === "equation" ||
      block.type === "table"
    ) {
      return;
    }

    const prepared =
      prepareTextbookRichManuscriptBlock(
        block,
        editorValue
          ?.manuscriptContent
      );

    if (
      !prepared.ok ||
      !prepared.value
    ) {
      setSaveError(
        isFR
          ? "Le contenu enrichi de ce bloc n’a pas pu être conservé."
          : "The rich content in this block could not be preserved."
      );

      return;
    }

    updateBlock(
      block.blockId,
      {
        text:
          prepared.value
            .text,

        manuscriptContent:
          prepared.value
            .manuscriptContent,
      }
    );
  }


  function updateChapterRichManuscript(
    editorValue
  ) {
    if (
      !canEdit ||
      saving ||
      !editorValue
        ?.manuscriptContent
    ) {
      return;
    }

    clearMessages();

    setBlocks(
      (items) =>
        applyTextbookChapterEditorContent(
          canonicalBlocks(
            items,
            sections
          ),
          editorValue
            .manuscriptContent
        )
    );
  }


  function renderActiveStructuredBlockControls(
    block
  ) {
    if (
      !canEdit ||
      !editMode ||
      !block ||
      [
        "paragraph",
        "heading",
      ].includes(
        block.type
      )
    ) {
      return null;
    }

    const selectedPoint =
      normalizeTextbookInsertionPoint(
        insertionPoint
      );

    return (
      <div
        data-textbook-active-structured-controls="true"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 7,
          flexWrap: "wrap",
          marginTop: 7,
        }}
      >
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            className="ns-card-meta"
            style={{
              fontWeight: 800,
            }}
          >
            {isFR
              ? "Déplacer vers"
              : "Move to"}
          </span>

          <select
            className="ns-input"
            data-textbook-active-block-placement="true"
            defaultValue=""
            disabled={saving}
            onChange={(event) => {
              const placementMode =
                event.target.value;

              if (!placementMode) {
                return;
              }

              moveBlockToPlacement(
                block,
                placementMode
              );

              event.target.value =
                "";
            }}
            style={{
              width: "auto",
              minWidth: 190,
            }}
          >
            <option value="">
              {isFR
                ? "Choisir…"
                : "Choose…"}
            </option>

            {selectedPoint.afterBlockId ? (
              <option value="cursor">
                {isFR
                  ? "Curseur sélectionné"
                  : "Selected cursor"}
              </option>
            ) : null}


            <option value="section_end">
              {isFR
                ? "Fin de la section"
                : "End of current section"}
            </option>

            <option value="chapter_end">
              {isFR
                ? "Fin du chapitre"
                : "End of chapter"}
            </option>
          </select>
        </label>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            saving ||
            !canMove(
              block,
              -1
            )
          }
          onClick={() =>
            moveBlock(
              block.blockId,
              -1
            )
          }
        >
          {isFR
            ? "Plus tôt"
            : "Earlier"}
        </button>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            saving ||
            !canMove(
              block,
              1
            )
          }
          onClick={() =>
            moveBlock(
              block.blockId,
              1
            )
          }
        >
          {isFR
            ? "Plus tard"
            : "Later"}
        </button>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={saving}
          onClick={() =>
            removeBlock(
              block.blockId
            )
          }
        >
          {isFR
            ? "Retirer"
            : "Remove"}
        </button>
      </div>
    );
  }


  function renderChapterEditorBoundary(
    boundaryBlock
  ) {
    const index =
      textbookChapterBoundaryIndex(
        boundaryBlock?.id
      );

    if (
      index < 0 ||
      !current[index]
    ) {
      return null;
    }

    const block =
      current[index];

    const previous =
      index > 0
        ? current[index - 1]
        : null;

    const sectionChanged =
      clean(
        block
          ?.sectionItemId
      ) !==
      clean(
        previous
          ?.sectionItemId
      );

    const section =
      sections.find(
        (item) =>
          clean(
            item?.itemId
          ) ===
          clean(
            block
              ?.sectionItemId
          )
      ) ||
      null;

    const editorGroupAnchor =
      (
        index === 0 ||
        sectionChanged
      )
        ? (
            <span
              id={chapterAnchorId(
                "editor-group",
                clean(
                  block?.sectionItemId
                ) ||
                  "__chapter__"
              )}
              data-textbook-editor-group-anchor={
                clean(
                  block?.sectionItemId
                ) ||
                  "__chapter__"
              }
              contentEditable={false}
              suppressContentEditableWarning
              style={{
                display: "block",
                height: 0,
                scrollMarginTop: 190,
              }}
            />
          )
        : null;

    const sectionHeading =
      sectionChanged &&
      clean(
        section?.title
      )
        ? (
            <div
              data-textbook-editor-section-heading="true"
              style={{
                margin:
                  "18px 0 8px",
                fontWeight: 900,
                fontSize: 18,
                color: "#334155",
              }}
            >
              {section.title}
            </div>
          )
        : null;

    if (
      block.type ===
        "figure"
    ) {
      return (
        <div
          data-textbook-editor-structural-object="figure"
          style={{
            margin:
              "14px 0",
          }}
        >
          {editorGroupAnchor}
          {sectionHeading}
          {renderActiveStructuredBlockControls(
            block
          )}
          {renderFigureBlock(
            block
          )}
        </div>
      );
    }

    if (
      block.type ===
        "equation"
    ) {
      return (
        <div
          data-textbook-editor-structural-object="equation"
          style={{
            margin:
              "14px 0",
          }}
        >
          {editorGroupAnchor}
          {sectionHeading}
          {renderActiveStructuredBlockControls(
            block
          )}
          <TextbookEquationEditor
            block={block}
            disabled={
              saving ||
              !canEdit
            }
            isFR={isFR}
            onChange={(
              patch
            ) =>
              updateBlock(
                block.blockId,
                patch
              )
            }
          />
        </div>
      );
    }

    if (
      block.type ===
        "table"
    ) {
      return (
        <div
          data-textbook-editor-structural-object="table"
          style={{
            margin:
              "14px 0",
          }}
        >
          {editorGroupAnchor}
          {sectionHeading}
          {renderActiveStructuredBlockControls(
            block
          )}
          <TextbookTableEditor
            block={block}
            disabled={
              saving ||
              !canEdit
            }
            isFR={isFR}
            onChange={(
              patch
            ) =>
              updateBlock(
                block.blockId,
                patch
              )
            }
          />
        </div>
      );
    }

    const typeLabel =
      TYPE_LABELS[
        block.type
      ]?.[language] ||
      "";

    if (
      !sectionHeading &&
      (
        block.type ===
        "paragraph" ||
        !typeLabel
      )
    ) {
      return editorGroupAnchor;
    }

    return (
      <div
        data-textbook-editor-structural-label="true"
        style={{
          margin:
            "18px 0 6px",
          display: "grid",
          gap: 4,
        }}
      >
        {editorGroupAnchor}
        {sectionHeading}
        {block.type !==
          "paragraph" &&
        typeLabel ? (
          <span
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
              color: "#64748b",
            }}
          >
            {
              placedObjectLabels[
                clean(
                  block?.blockId
                )
              ] ||
              typeLabel
            }
          </span>
        ) : null}

        {renderActiveStructuredBlockControls(
          block
        )}
      </div>
    );
  }


  function changeBlockType(
    block,
    nextType
  ) {
    if (
      !canEdit ||
      !block ||
      !TEXTBOOK_CHAPTER_BLOCK_TYPES
        .includes(nextType)
    ) {
      return;
    }

    if (
      nextType ===
        block.type
    ) {
      return;
    }

    if (
      nextType ===
        "equation"
    ) {
      const equation =
        normalizeTextbookEquation(
          block?.equation,
          block?.text
        );

      updateBlock(
        block.blockId,
        {
          type:
            "equation",
          text:
            equation.source,
          equation,
          manuscriptContent:
            undefined,
          figureAssetId:
            "",
        }
      );

      return;
    }

    if (
      block.type ===
        "equation"
    ) {
      const equation =
        normalizeTextbookEquation(
          block?.equation,
          block?.text
        );

      updateBlock(
        block.blockId,
        {
          type:
            nextType,
          text:
            equation.source,
          equation:
            undefined,
          manuscriptContent:
            undefined,
          figureAssetId:
            "",
        }
      );

      return;
    }

    updateBlock(
      block.blockId,
      {
        type:
          nextType,
      }
    );
  }

  function removeBlock(
    blockId
  ) {
    if (!canEdit) {
      return;
    }

    clearMessages();

    setBlocks(
      (items) =>
        items.filter(
          (item) =>
            item.blockId !==
            blockId
        )
    );
  }

  function moveBlockToPlacement(
    block,
    placementMode
  ) {
    if (
      !canEdit ||
      !block
    ) {
      return;
    }

    clearMessages();

    setBlocks(
      (items) => {
        const withoutBlock =
          items.filter(
            (item) =>
              item.blockId !==
              block.blockId
          );

        const selectedPoint =
          normalizeTextbookInsertionPoint(
            insertionPoint
          );

        const sectionId =
          clean(
            block.sectionItemId
          ) ||
          clean(
            selectedPoint.sectionItemId
          ) ||
          clean(
            activeGroupId
          );

        const point =
          placementMode === "cursor"
            ? (selectedPoint.afterBlockId ? selectedPoint : null)
            : placementMode === "section_end"
              ? sectionEndInsertionPoint(sectionId, withoutBlock)
              : chapterEndInsertionPoint(withoutBlock);

        if (!point) {
          return items;
        }

        return insertTextbookBlockAtInsertionPoint(
          withoutBlock,
          sections,
          point,
          {
            ...block,
            sectionItemId:
              clean(
                point.sectionItemId
              ) ||
              sectionId,
          }
        );
      }
    );

    setActiveBlockId("");

    setInsertionMessage(
      isFR
        ? "Bloc déplacé. Enregistrez le chapitre pour conserver son nouvel emplacement."
        : "Block moved. Save Chapter to keep its new placement."
    );
  }


  function moveBlock(
    blockId,
    direction
  ) {
    if (!canEdit) {
      return;
    }

    clearMessages();

    setBlocks(
      (items) => {
        const ordered =
          canonicalBlocks(
            items,
            sections
          );

        const item =
          ordered.find(
            (candidate) =>
              candidate.blockId ===
              blockId
          );

        if (!item) {
          return ordered;
        }

        const siblings =
          ordered.filter(
            (candidate) =>
              candidate
                .sectionItemId ===
              item.sectionItemId
          );

        const index =
          siblings.findIndex(
            (candidate) =>
              candidate.blockId ===
              blockId
          );

        const target =
          siblings[
            index + direction
          ];

        if (!target) {
          return ordered;
        }

        const from =
          ordered.findIndex(
            (candidate) =>
              candidate.blockId ===
              blockId
          );

        const to =
          ordered.findIndex(
            (candidate) =>
              candidate.blockId ===
              target.blockId
          );

        const next = [
          ...ordered,
        ];

        [
          next[from],
          next[to],
        ] = [
          next[to],
          next[from],
        ];

        return next;
      }
    );
  }

  function canMove(
    block,
    direction
  ) {
    const siblings =
      current.filter(
        (candidate) =>
          candidate
            .sectionItemId ===
          block.sectionItemId
      );

    const index =
      siblings.findIndex(
        (candidate) =>
          candidate.blockId ===
          block.blockId
      );

    return Boolean(
      siblings[
        index + direction
      ]
    );
  }

  function openFigureComposer(
    sectionItemId,
    requestedInsertionPoint =
      insertionPoint
  ) {
    if (
      !canEdit ||
      clean(
        figureLoadError
      )
    ) {
      return;
    }

    const sectionId =
      clean(
        sectionItemId
      );

    const point =
      normalizeTextbookInsertionPoint(
        requestedInsertionPoint
      );

    if (!point.afterBlockId) {
      setInsertionMessage(
        isFR
          ? "Cliquez d’abord dans le chapitre à l’endroit où vous souhaitez ajouter une Figure."
          : "Click in the Chapter first where you want to add a Figure."
      );
      return;
    }

    setContentComposer(null);
    setContentComposerError("");

    setCollapsedGroups(
      (currentSet) => {
        const key =
          sectionId ||
          "__chapter__";

        if (
          !currentSet.has(
            key
          )
        ) {
          return currentSet;
        }

        const next =
          new Set(
            currentSet
          );

        next.delete(key);
        return next;
      }
    );

    setFigureComposer({
      sectionItemId:
        sectionId,

      insertionPoint:
        point,

      mode: "upload",
      figureType:
        "image",
      file: null,
      previewUrl: "",
      uploadedAsset: null,
      uploadedAssetMetadataDirty:
        false,
      publicUrl: "",
      caption: "",
      alternativeText: "",
      creatorOrSource: "",
      rightsOrLicense: "",
      figurePlacement:
        "half_right",
    });

    setFigureError("");
    setFigureMessage("");
  }

  function closeFigureComposer() {
    if (figureBusy) {
      return;
    }

    if (
      figureComposer
        ?.previewUrl &&
      typeof URL !==
        "undefined" &&
      typeof URL
        .revokeObjectURL ===
        "function"
    ) {
      URL.revokeObjectURL(
        figureComposer
          .previewUrl
      );
    }

    setFigureComposer(null);
    setFigureError("");
  }

  function updateFigureComposer(
    field,
    value
  ) {
    const assetMetadataFields =
      new Set([
        "figureType",
        "caption",
        "alternativeText",
        "creatorOrSource",
        "rightsOrLicense",
      ]);

    setFigureComposer(
      (currentComposer) =>
        currentComposer
          ? {
              ...currentComposer,
              [field]:
                value,
              ...(
                currentComposer
                  .uploadedAsset &&
                assetMetadataFields
                  .has(field)
                  ? {
                      uploadedAssetMetadataDirty:
                        true,
                    }
                  : {}
              ),
            }
          : currentComposer
    );

    setFigureError("");
    setFigureMessage("");
  }

  function selectFigureFile(
    event
  ) {
    const file =
      event.target
        .files?.[0] ||
      null;

    setFigureError("");
    setFigureMessage("");

    if (!file) {
      setFigureComposer(
        (currentComposer) =>
          currentComposer
            ? {
                ...currentComposer,
                file: null,
                previewUrl: "",
                uploadedAsset:
                  null,
                uploadedAssetMetadataDirty:
                  false,
              }
            : currentComposer
      );
      return;
    }

    const mimeType =
      clean(
        file?.type
      ).toLowerCase();

    if (
      mimeType &&
      ![
        "image/jpeg",
        "image/png",
        "image/webp",
      ].includes(
        mimeType
      )
    ) {
      event.target.value =
        "";

      setFigureError(
        isFR
          ? "Choisissez une image JPG, PNG ou WEBP."
          : "Choose a JPG, PNG, or WEBP Figure image."
      );
      return;
    }

    if (
      Number(
        file?.size || 0
      ) >
      TEXTBOOK_FIGURE_MAX_BYTES
    ) {
      event.target.value =
        "";

      setFigureError(
        isFR
          ? "L’image de la Figure doit faire 5 Mo ou moins."
          : "Figure image must be 5 MB or smaller."
      );
      return;
    }

    setFigureComposer(
      (currentComposer) => {
        if (!currentComposer) {
          return currentComposer;
        }

        if (
          currentComposer
            .previewUrl &&
          typeof URL !==
            "undefined" &&
          typeof URL
            .revokeObjectURL ===
            "function"
        ) {
          URL.revokeObjectURL(
            currentComposer
              .previewUrl
          );
        }

        const previewUrl =
          typeof URL !==
            "undefined" &&
          typeof URL
            .createObjectURL ===
            "function"
            ? URL.createObjectURL(
                file
              )
            : "";

        return {
          ...currentComposer,
          mode: "upload",
          file,
          previewUrl,
          uploadedAsset: null,
          uploadedAssetMetadataDirty:
            false,
        };
      }
    );
  }

  function figureComposerMetadata() {
    return {
      figureType:
        clean(
          figureComposer
            ?.figureType
        ) ||
        "image",

      sectionItemId:
        clean(
          figureComposer
            ?.sectionItemId
        ),

      caption:
        exactText(
          figureComposer
            ?.caption
        ),

      alternativeText:
        exactText(
          figureComposer
            ?.alternativeText
        ),

      creatorOrSource:
        exactText(
          figureComposer
            ?.creatorOrSource
        ),

      rightsOrLicense:
        exactText(
          figureComposer
            ?.rightsOrLicense
        ),
    };
  }

  async function uploadFigureFromComposer() {
    if (
      !canEdit ||
      figureBusy ||
      !figureComposer ||
      figureComposer.mode !==
        "upload"
    ) {
      return;
    }

    const file =
      figureComposer.file;

    if (!file) {
      setFigureError(
        isFR
          ? "Choisissez d’abord une image."
          : "Choose a Figure image first."
      );
      return;
    }

    if (
      typeof onUploadFigure !==
        "function"
    ) {
      setFigureError(
        isFR
          ? "Le téléversement de Figure est indisponible."
          : "Figure upload is unavailable."
      );
      return;
    }

    try {
      setFigureBusy(true);
      setFigureError("");
      setFigureMessage("");

      const figureAsset =
        await onUploadFigure(
          file,
          figureComposerMetadata()
        );

      if (
        !clean(
          figureAsset?.id
        )
      ) {
        throw new Error(
          isFR
            ? "La Figure téléversée est invalide."
            : "The uploaded Figure is invalid."
        );
      }

      setFigureComposer(
        (currentComposer) =>
          currentComposer
            ? {
                ...currentComposer,
                uploadedAsset:
                  figureAsset,
                uploadedAssetMetadataDirty:
                  false,
              }
            : currentComposer
      );

      setFigureMessage(
        isFR
          ? "Image téléversée. Vérifiez l’aperçu, puis placez la Figure dans le chapitre."
          : "Image uploaded. Review the preview, then place the Figure in the chapter."
      );
    } catch (error) {
      setFigureError(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible de téléverser cette Figure."
              : "Could not upload this Figure."
          )
      );
    } finally {
      setFigureBusy(false);
    }
  }

  function attachFigureAsset(
    figureAsset,
    sectionItemId,
    requestedInsertionPoint =
      figureComposer
        ?.insertionPoint ||
      insertionPoint,
    requestedPlacement =
      figureComposer
        ?.figurePlacement ||
      "half_right"
  ) {
    const figureAssetId =
      clean(
        figureAsset?.id
      );

    const sectionId =
      clean(
        sectionItemId
      );

    if (!figureAssetId) {
      throw new Error(
        isFR
          ? "La Figure ajoutée est invalide."
          : "The added Figure is invalid."
      );
    }

    if (
      clean(
        figureAsset
          ?.sectionItemId
      ) !== sectionId
    ) {
      throw new Error(
        isFR
          ? "Cette Figure appartient à une autre partie du chapitre."
          : "This Figure belongs to a different part of the chapter."
      );
    }

    if (
      current.some(
        (block) =>
          block.type ===
            "figure" &&
          block.figureAssetId ===
            figureAssetId
      )
    ) {
      setFigureComposer(null);
      setFigureError("");
      setFigureMessage(
        isFR
          ? "Cette Figure est déjà placée dans le chapitre."
          : "This Figure is already placed in the chapter."
      );
      return;
    }

    clearMessages();

    const point =
      normalizeTextbookInsertionPoint(
        requestedInsertionPoint
      );

    const placement =
      normalizeTextbookFigurePlacement(
        requestedPlacement
      );

    setBlocks(
      (items) =>
        point.afterBlockId
          ? insertTextbookBlockAtInsertionPoint(
              items,
              sections,
              point,
              {
                blockId:
                  newBlockId(),

                type:
                  "figure",

                sectionItemId:
                  sectionId,

                text: "",

                figureAssetId,

                figurePlacement:
                  placement,
              }
            )
          : canonicalBlocks(
              [
                ...items,
                {
                  blockId:
                    newBlockId(),

                  type:
                    "figure",

                  sectionItemId:
                    sectionId,

                  text: "",

                  figureAssetId,

                  figurePlacement:
                    placement,
                },
              ],
              sections
            )
    );

    setFigureComposer(null);
    setInsertionPoint(null);
    setFigureError("");

    setFigureMessage(
      isFR
        ? "Figure ajoutée. Enregistrez le chapitre pour conserver son emplacement."
        : "Figure added. Save Chapter to keep its placement."
    );
  }

  async function createFigureFromComposer() {
    if (
      !canEdit ||
      figureBusy ||
      !figureComposer
    ) {
      return;
    }

    const metadata =
      figureComposerMetadata();

    try {
      setFigureBusy(true);
      setFigureError("");
      setFigureMessage("");

      let figureAsset =
        null;

      if (
        figureComposer
          .mode === "link"
      ) {
        const publicUrl =
          clean(
            figureComposer
              .publicUrl
          );

        let validLink =
          false;

        try {
          const parsed =
            new URL(
              publicUrl
            );

          validLink =
            parsed.protocol ===
              "http:" ||
            parsed.protocol ===
              "https:";
        } catch {
          validLink =
            false;
        }

        if (!validLink) {
          throw new Error(
            isFR
              ? "Ajoutez un lien d’image HTTP ou HTTPS valide."
              : "Add a valid HTTP or HTTPS image link."
          );
        }

        if (
          typeof onCreateFigureLink !==
            "function"
        ) {
          throw new Error(
            isFR
              ? "L’ajout d’une Figure par lien est indisponible."
              : "Adding a Figure by link is unavailable."
          );
        }

        figureAsset =
          await onCreateFigureLink(
            {
              ...metadata,
              publicUrl,
            }
          );
      } else {
        figureAsset =
          figureComposer
            .uploadedAsset;

        if (
          !clean(
            figureAsset?.id
          )
        ) {
          throw new Error(
            isFR
              ? "Téléversez d’abord l’image, puis placez la Figure."
              : "Upload the image first, then place the Figure."
          );
        }

        if (
          figureComposer
            .uploadedAssetMetadataDirty
        ) {
          if (
            typeof onUpdateFigure !==
              "function"
          ) {
            throw new Error(
              isFR
                ? "La mise à jour des détails de la Figure est indisponible."
                : "Updating Figure details is unavailable."
            );
          }

          const figureRevision =
            Number(
              figureAsset
                ?.revision || 0
            );

          if (
            !Number.isInteger(
              figureRevision
            ) ||
            figureRevision < 1
          ) {
            throw new Error(
              isFR
                ? "La révision de la Figure téléversée est invalide."
                : "The uploaded Figure revision is invalid."
            );
          }

          figureAsset =
            await onUpdateFigure(
              figureAsset.id,
              metadata,
              figureRevision
            );

          if (
            !clean(
              figureAsset?.id
            )
          ) {
            throw new Error(
              isFR
                ? "Le serveur n’a pas retourné la Figure mise à jour."
                : "The server did not return the updated Figure."
            );
          }
        }
      }

      if (
        figureComposer
          .previewUrl &&
        typeof URL !==
          "undefined" &&
        typeof URL
          .revokeObjectURL ===
          "function"
      ) {
        URL.revokeObjectURL(
          figureComposer
            .previewUrl
        );
      }

      attachFigureAsset(
        figureAsset,
        metadata.sectionItemId,
        figureComposer
          .insertionPoint,
        figureComposer
          .figurePlacement
      );
    } catch (error) {
      setFigureError(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible d’ajouter cette Figure."
              : "Could not add this Figure."
          )
      );
    } finally {
      setFigureBusy(false);
    }
  }

  function availableFiguresForGroup(
    sectionItemId
  ) {
    const sectionId =
      clean(
        sectionItemId
      );

    const placed =
      new Set(
        current
          .filter(
            (block) =>
              block.type ===
                "figure"
          )
          .map(
            (block) =>
              clean(
                block
                  .figureAssetId
              )
          )
          .filter(Boolean)
      );

    return normalizedFigureAssets
      .filter(
        (asset) =>
          clean(
            asset
              .sectionItemId
          ) === sectionId &&
          !placed.has(
            asset.id
          )
      );
  }

  function renderFigureComposer(
    sectionItemId
  ) {
    const sectionId =
      clean(
        sectionItemId
      );

    if (
      !figureComposer ||
      clean(
        figureComposer
          .sectionItemId
      ) !== sectionId
    ) {
      return null;
    }

    const availableFigures =
      availableFiguresForGroup(
        sectionId
      );

    return (
      <div
        data-textbook-floating-editor-backdrop="figure"
        role="presentation"
        onMouseDown={(event) => {
          if (
            event.target ===
            event.currentTarget
          ) {
            closeFigureComposer();
          }
        }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1200,
          display: "grid",
          placeItems: "center",
          padding: 20,
          background:
            "rgba(15,23,42,0.52)",
        }}
      >
      <div
        data-textbook-floating-editor="figure"
        role="dialog"
        aria-modal="true"
        aria-label={
          isFR
            ? "Ajouter une Figure"
            : "Add Figure"
        }
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform:
            "translate(-50%, -50%)",
          zIndex: 41,
          width:
            "min(760px, 94vw)",
          maxHeight: "86vh",
          overflowY: "auto",
          padding: 18,
          borderRadius: 18,
          border:
            "1px solid rgba(5,150,105,0.28)",
          background:
            "#ffffff",
          boxShadow:
            "0 28px 80px rgba(15,23,42,0.28)",
          display: "grid",
          gap: 12,
        }}
      >
        <div>
          <strong>
            {isFR
              ? "Ajouter une Figure"
              : "Add Figure"}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin:
                "4px 0 0",
            }}
          >
            {isFR
              ? "Ajoutez l’image ici. NextScenes conservera les détails techniques en arrière-plan."
              : "Add the image here. NextScenes will keep the technical details in the background."}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <button
            className={
              figureComposer
                .mode ===
              "upload"
                ? "ns-button ns-button-primary"
                : "ns-button ns-button-outline"
            }
            type="button"
            disabled={figureBusy}
            onClick={() =>
              updateFigureComposer(
                "mode",
                "upload"
              )
            }
          >
            {isFR
              ? "Fichier image"
              : "Image File"}
          </button>

          <button
            className={
              figureComposer
                .mode ===
              "link"
                ? "ns-button ns-button-primary"
                : "ns-button ns-button-outline"
            }
            type="button"
            disabled={figureBusy}
            onClick={() =>
              updateFigureComposer(
                "mode",
                "link"
              )
            }
          >
            {isFR
              ? "Utiliser un lien d’image"
              : "Use Image Link"}
          </button>
        </div>

        {figureComposer.mode ===
        "link" ? (
          <label
            style={{
              display: "grid",
              gap: 6,
              fontWeight: 750,
            }}
          >
            <span>
              {isFR
                ? "Lien de l’image"
                : "Image link"}
            </span>

            <input
              className="ns-input"
              type="url"
              value={
                figureComposer
                  .publicUrl
              }
              disabled={figureBusy}
              onChange={(event) =>
                updateFigureComposer(
                  "publicUrl",
                  event.target.value
                )
              }
              placeholder="https://…"
            />
          </label>
        ) : (
          <label
            style={{
              display: "grid",
              gap: 6,
              fontWeight: 750,
            }}
          >
            <span>
              Image
            </span>

            <input
              type="file"
              accept={
                TEXTBOOK_FIGURE_ACCEPT
              }
              disabled={figureBusy}
              onChange={
                selectFigureFile
              }
            />

            <span
              className="ns-card-meta"
              style={{
                fontWeight: 500,
              }}
            >
              {figureComposer.file
                ? (
                    isFR
                      ? `Sélectionnée : ${figureComposer.file.name}`
                      : `Selected: ${figureComposer.file.name}`
                  )
                : (
                    isFR
                      ? "JPG, PNG ou WEBP · 5 Mo maximum."
                      : "JPG, PNG, or WEBP · 5 MB maximum."
                  )}
            </span>

            {figureComposer
              .uploadedAsset &&
            figureComposer
              .previewUrl ? (
              <div
                data-textbook-figure-upload-preview="true"
                style={{
                  display: "grid",
                  placeItems:
                    "center",
                  padding: 10,
                  borderRadius: 12,
                  border:
                    "1px solid rgba(148,163,184,0.32)",
                  background:
                    "#f8fafc",
                }}
              >
                <img
                  src={
                    figureComposer
                      .previewUrl
                  }
                  alt={
                    isFR
                      ? "Aperçu de l’image sélectionnée"
                      : "Selected Figure preview"
                  }
                  style={{
                    display:
                      "block",
                    width: "100%",
                    maxWidth: 340,
                    maxHeight: 230,
                    objectFit:
                      "contain",
                    borderRadius: 8,
                  }}
                />
              </div>
            ) : null}

            <button
              className="ns-button ns-button-primary"
              type="button"
              disabled={
                figureBusy ||
                !figureComposer
                  .file ||
                Boolean(
                  figureComposer
                    .uploadedAsset
                )
              }
              aria-busy={
                figureBusy
              }
              onClick={
                uploadFigureFromComposer
              }
              style={{
                justifySelf:
                  "start",
              }}
            >
              {figureBusy
                ? (
                    isFR
                      ? "Téléversement…"
                      : "Uploading…"
                  )
                : figureComposer
                    .uploadedAsset
                  ? (
                      isFR
                        ? "Image téléversée"
                        : "Image Uploaded"
                    )
                  : (
                      isFR
                        ? "Téléverser l’image"
                        : "Upload Image"
                    )}
            </button>

            {figureComposer
              .uploadedAsset ? (
              <span
                role="status"
                style={{
                  color: "#166534",
                  fontWeight: 800,
                }}
              >
                {isFR
                  ? "Téléversement terminé — prête à être placée."
                  : "Upload complete — ready to place."}
              </span>
            ) : null}
          </label>
        )}

        <label
          style={{
            display: "grid",
            gap: 6,
            fontWeight: 750,
          }}
        >
          <span>
            {isFR
              ? "Légende (facultatif)"
              : "Caption (optional)"}
          </span>

          <input
            className="ns-input"
            type="text"
            value={
              figureComposer.caption
            }
            disabled={figureBusy}
            onChange={(event) =>
              updateFigureComposer(
                "caption",
                event.target.value
              )
            }
          />
        </label>

        <label
          data-textbook-figure-insert-layout="true"
          style={{
            display: "grid",
            gap: 6,
            fontWeight: 750,
          }}
        >
          <span>
            {isFR
              ? "Disposition"
              : "Layout"}
          </span>

          <select
            className="ns-input"
            value={
              normalizeTextbookFigurePlacement(
                figureComposer
                  .figurePlacement
              )
            }
            disabled={figureBusy}
            onChange={(event) =>
              updateFigureComposer(
                "figurePlacement",
                event.target.value
              )
            }
          >
            <option value="full_width">
              {isFR
                ? "Pleine largeur — texte en dessous"
                : "Full width — text below"}
            </option>

            <option value="half_left">
              {isFR
                ? "Gauche — le texte s’habille autour"
                : "Left — text wraps beside it"}
            </option>

            <option value="half_right">
              {isFR
                ? "Droite — le texte s’habille autour"
                : "Right — text wraps beside it"}
            </option>

            <option value="small_left">
              {isFR
                ? "Petite à gauche — texte habillé"
                : "Small left — text wraps"}
            </option>

            <option value="small_right">
              {isFR
                ? "Petite à droite — texte habillé"
                : "Small right — text wraps"}
            </option>
          </select>

          <span
            className="ns-card-meta"
            style={{
              fontWeight: 500,
              lineHeight: 1.45,
            }}
          >
            {isFR
              ? "Pour une Figure ordinaire, Gauche ou Droite laisse le texte continuer naturellement à côté de l’image."
              : "For an ordinary Figure, Left or Right lets the Chapter text continue naturally beside the image."}
          </span>
        </label>

        <details>
          <summary
            style={{
              cursor: "pointer",
              fontWeight: 800,
            }}
          >
            {isFR
              ? "Détails de la Figure"
              : "Figure details"}
          </summary>

          <div
            style={{
              display: "grid",
              gap: 12,
              marginTop: 10,
            }}
          >
            <label
              style={{
                display: "grid",
                gap: 6,
                fontWeight: 750,
              }}
            >
              <span>
                {isFR
                  ? "Type de Figure"
                  : "Figure type"}
              </span>

              <select
                className="ns-input"
                value={
                  figureComposer
                    .figureType
                }
                disabled={figureBusy}
                onChange={(event) =>
                  updateFigureComposer(
                    "figureType",
                    event.target.value
                  )
                }
              >
                {TEXTBOOK_FIGURE_TYPES.map(
                  (type) => (
                    <option
                      key={type}
                      value={type}
                    >
                      {
                        TEXTBOOK_FIGURE_TYPE_LABELS[
                          type
                        ][language]
                      }
                    </option>
                  )
                )}
              </select>
            </label>

            <label
              style={{
                display: "grid",
                gap: 6,
                fontWeight: 750,
              }}
            >
              <span>
                {isFR
                  ? "Texte alternatif"
                  : "Alternative text"}
              </span>

              <textarea
                className="ns-input"
                rows={3}
                value={
                  figureComposer
                    .alternativeText
                }
                disabled={figureBusy}
                onChange={(event) =>
                  updateFigureComposer(
                    "alternativeText",
                    event.target.value
                  )
                }
              />
            </label>

            <label
              style={{
                display: "grid",
                gap: 6,
                fontWeight: 750,
              }}
            >
              <span>
                {isFR
                  ? "Créateur ou source"
                  : "Creator or source"}
              </span>

              <input
                className="ns-input"
                type="text"
                value={
                  figureComposer
                    .creatorOrSource
                }
                disabled={figureBusy}
                onChange={(event) =>
                  updateFigureComposer(
                    "creatorOrSource",
                    event.target.value
                  )
                }
              />
            </label>

            <label
              style={{
                display: "grid",
                gap: 6,
                fontWeight: 750,
              }}
            >
              <span>
                {isFR
                  ? "Droits ou licence"
                  : "Rights or licence"}
              </span>

              <input
                className="ns-input"
                type="text"
                value={
                  figureComposer
                    .rightsOrLicense
                }
                disabled={figureBusy}
                onChange={(event) =>
                  updateFigureComposer(
                    "rightsOrLicense",
                    event.target.value
                  )
                }
              />
            </label>
          </div>
        </details>

        <p
          className="ns-card-meta"
          style={{
            margin: 0,
          }}
        >
          {isFR
            ? "Ajoutez uniquement des images que vous possédez ou que vous avez l’autorisation d’utiliser."
            : "Only add images you own or have permission to use."}
        </p>

        {availableFigures.length >
        0 ? (
          <details>
            <summary
              style={{
                cursor:
                  "pointer",
                fontWeight: 800,
              }}
            >
              {isFR
                ? "Utiliser une Figure existante"
                : "Use an existing Figure"}
            </summary>

            <div
              style={{
                display: "grid",
                gap: 8,
                marginTop: 10,
              }}
            >
              {availableFigures.map(
                (asset) => (
                  <div
                    key={asset.id}
                    style={{
                      display:
                        "flex",
                      justifyContent:
                        "space-between",
                      alignItems:
                        "center",
                      gap: 10,
                      flexWrap:
                        "wrap",
                      padding:
                        "10px 12px",
                      borderRadius:
                        10,
                      background:
                        "#ffffff",
                      border:
                        "1px solid rgba(148,163,184,0.24)",
                    }}
                  >
                    <span>
                      {clean(
                        asset.caption
                      ) ||
                        clean(
                          asset
                            .fileName
                        ) ||
                        (
                          isFR
                            ? "Figure sans légende"
                            : "Untitled Figure"
                        )}
                    </span>

                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      disabled={
                        figureBusy
                      }
                      onClick={() =>
                        attachFigureAsset(
                          asset,
                          sectionId,
                          figureComposer
                            .insertionPoint
                        )
                      }
                    >
                      {isFR
                        ? "Utiliser cette Figure"
                        : "Use This Figure"}
                    </button>
                  </div>
                )
              )}
            </div>
          </details>
        ) : null}

        {figureError ? (
          <div
            role="alert"
            style={{
              color: "#b91c1c",
              fontWeight: 700,
            }}
          >
            {figureError}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            justifyContent:
              "flex-end",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={figureBusy}
            onClick={
              closeFigureComposer
            }
          >
            {isFR
              ? "Annuler"
              : "Cancel"}
          </button>

          <button
            className="ns-button ns-button-primary"
            type="button"
            disabled={
              figureBusy ||
              (
                figureComposer
                  .mode ===
                  "upload" &&
                !figureComposer
                  .uploadedAsset
              )
            }
            aria-busy={
              figureBusy
            }
            onClick={
              createFigureFromComposer
            }
          >
            {figureBusy
              ? (
                  isFR
                    ? "Ajout…"
                    : "Adding…"
                )
              : (
                  isFR
                    ? "Placer la Figure"
                    : "Place Figure"
                )}
          </button>
        </div>
      </div>
      </div>
    );
  }

  function openFigureEditor(
    figureAsset
  ) {
    if (
      !canEdit ||
      typeof onUpdateFigure !==
        "function"
    ) {
      return;
    }

    const figureId =
      clean(
        figureAsset?.id
      );

    const figureRevision =
      Number(
        figureAsset
          ?.revision || 0
      );

    if (
      !figureId ||
      !Number.isInteger(
        figureRevision
      ) ||
      figureRevision < 1
    ) {
      return;
    }

    setFigureComposer(null);
    setFigureError("");
    setFigureMessage("");

    setFigureEditor({
      id:
        figureId,

      revision:
        figureRevision,

      figureType:
        clean(
          figureAsset
            ?.figureType
        ) ||
        "image",

      caption:
        exactText(
          figureAsset?.caption
        ),

      alternativeText:
        exactText(
          figureAsset
            ?.alternativeText
        ),

      creatorOrSource:
        exactText(
          figureAsset
            ?.creatorOrSource
        ),

      rightsOrLicense:
        exactText(
          figureAsset
            ?.rightsOrLicense
        ),
    });

    setFigureEditError("");
  }

  function closeFigureEditor() {
    if (figureEditBusy) {
      return;
    }

    setFigureEditor(null);
    setFigureEditError("");
  }

  function updateFigureEditor(
    field,
    value
  ) {
    setFigureEditor(
      (currentEditor) =>
        currentEditor
          ? {
              ...currentEditor,
              [field]:
                value,
            }
          : currentEditor
    );

    setFigureEditError("");
    setFigureMessage("");
  }

  async function saveFigureEditor() {
    if (
      !canEdit ||
      figureEditBusy ||
      !figureEditor ||
      typeof onUpdateFigure !==
        "function"
    ) {
      return;
    }

    try {
      setFigureEditBusy(true);
      setFigureEditError("");
      setFigureMessage("");

      await onUpdateFigure(
        figureEditor.id,
        {
          figureType:
            clean(
              figureEditor
                .figureType
            ) ||
            "image",

          caption:
            exactText(
              figureEditor
                .caption
            ),

          alternativeText:
            exactText(
              figureEditor
                .alternativeText
            ),

          creatorOrSource:
            exactText(
              figureEditor
                .creatorOrSource
            ),

          rightsOrLicense:
            exactText(
              figureEditor
                .rightsOrLicense
            ),
        },
        figureEditor.revision
      );

      setFigureEditor(null);
      setFigureEditError("");

      setFigureMessage(
        isFR
          ? "Figure mise à jour."
          : "Figure updated."
      );
    } catch (error) {
      const revisionConflict =
        Number(
          error?.status
        ) === 409 &&
        clean(
          error?.data?.code
        ) ===
          "textbook_figure_asset_revision_conflict";

      setFigureEditError(
        revisionConflict
          ? (
              isFR
                ? "Cette Figure a changé pendant votre modification. Annulez puis rouvrez Modifier la Figure pour charger les derniers détails."
                : "This Figure changed while you were editing it. Cancel and reopen Edit Figure to load the latest details."
            )
          : (
              clean(
                error?.message
              ) ||
              (
                isFR
                  ? "Impossible de mettre à jour cette Figure."
                  : "Could not update this Figure."
              )
            )
      );
    } finally {
      setFigureEditBusy(false);
    }
  }

  function renderFigureEditor(
    figureAsset
  ) {
    if (
      !figureEditor ||
      figureEditor.id !==
        clean(
          figureAsset?.id
        )
    ) {
      return null;
    }

    return (
      <div
        role="region"
        aria-label={
          isFR
            ? "Modifier la Figure"
            : "Edit Figure"
        }
        style={{
          display: "grid",
          gap: 12,
          padding: 14,
          borderRadius: 12,
          border:
            "1px solid rgba(5,150,105,0.24)",
          background:
            "#ffffff",
        }}
      >
        <label
          style={{
            display: "grid",
            gap: 6,
            fontWeight: 750,
          }}
        >
          <span>
            {isFR
              ? "Légende"
              : "Caption"}
          </span>

          <input
            className="ns-input"
            type="text"
            value={
              figureEditor.caption
            }
            disabled={
              figureEditBusy
            }
            onChange={(event) =>
              updateFigureEditor(
                "caption",
                event.target.value
              )
            }
          />
        </label>

        <label
          style={{
            display: "grid",
            gap: 6,
            fontWeight: 750,
          }}
        >
          <span>
            {isFR
              ? "Type de Figure"
              : "Figure type"}
          </span>

          <select
            className="ns-input"
            value={
              figureEditor
                .figureType
            }
            disabled={
              figureEditBusy
            }
            onChange={(event) =>
              updateFigureEditor(
                "figureType",
                event.target.value
              )
            }
          >
            {TEXTBOOK_FIGURE_TYPES.map(
              (type) => (
                <option
                  key={type}
                  value={type}
                >
                  {
                    TEXTBOOK_FIGURE_TYPE_LABELS[
                      type
                    ][language]
                  }
                </option>
              )
            )}
          </select>
        </label>

        <details>
          <summary
            style={{
              cursor: "pointer",
              fontWeight: 800,
            }}
          >
            {isFR
              ? "Détails de la Figure"
              : "Figure details"}
          </summary>

          <div
            style={{
              display: "grid",
              gap: 12,
              marginTop: 10,
            }}
          >
            <label
              style={{
                display: "grid",
                gap: 6,
                fontWeight: 750,
              }}
            >
              <span>
                {isFR
                  ? "Texte alternatif"
                  : "Alternative text"}
              </span>

              <textarea
                className="ns-input"
                rows={3}
                value={
                  figureEditor
                    .alternativeText
                }
                disabled={
                  figureEditBusy
                }
                onChange={(event) =>
                  updateFigureEditor(
                    "alternativeText",
                    event.target.value
                  )
                }
              />
            </label>

            <label
              style={{
                display: "grid",
                gap: 6,
                fontWeight: 750,
              }}
            >
              <span>
                {isFR
                  ? "Créateur ou source"
                  : "Creator or source"}
              </span>

              <input
                className="ns-input"
                type="text"
                value={
                  figureEditor
                    .creatorOrSource
                }
                disabled={
                  figureEditBusy
                }
                onChange={(event) =>
                  updateFigureEditor(
                    "creatorOrSource",
                    event.target.value
                  )
                }
              />
            </label>

            <label
              style={{
                display: "grid",
                gap: 6,
                fontWeight: 750,
              }}
            >
              <span>
                {isFR
                  ? "Droits ou licence"
                  : "Rights or licence"}
              </span>

              <input
                className="ns-input"
                type="text"
                value={
                  figureEditor
                    .rightsOrLicense
                }
                disabled={
                  figureEditBusy
                }
                onChange={(event) =>
                  updateFigureEditor(
                    "rightsOrLicense",
                    event.target.value
                  )
                }
              />
            </label>
          </div>
        </details>

        {figureEditError ? (
          <div
            role="alert"
            style={{
              color: "#b91c1c",
              fontWeight: 700,
            }}
          >
            {figureEditError}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            justifyContent:
              "flex-end",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={
              figureEditBusy
            }
            onClick={
              closeFigureEditor
            }
          >
            {isFR
              ? "Annuler"
              : "Cancel"}
          </button>

          <button
            className="ns-button ns-button-primary"
            type="button"
            disabled={
              figureEditBusy
            }
            aria-busy={
              figureEditBusy
            }
            onClick={
              saveFigureEditor
            }
          >
            {figureEditBusy
              ? (
                  isFR
                    ? "Enregistrement…"
                    : "Saving…"
                )
              : (
                  isFR
                    ? "Enregistrer la Figure"
                    : "Save Figure"
                )}
          </button>
        </div>
      </div>
    );
  }



  function renderFigureBlock(
    block,
    flowing = false,
    readOnly = false,
    flowAnchorBlockId = ""
  ) {
    const figureAsset =
      figureAssetsById.get(
        clean(
          block
            ?.figureAssetId
        )
      );

    if (!figureAsset) {
      return (
        <div
          role="note"
          style={{
            padding:
              "14px 16px",
            borderRadius: 12,
            border:
              "1px solid rgba(185,28,28,0.22)",
            background:
              "#fff7ed",
            color: "#7c2d12",
            lineHeight: 1.55,
          }}
        >
          {isFR
            ? "Cette Figure n’est pas disponible. Rechargez le chapitre avant de continuer."
            : "This Figure is unavailable. Reload the chapter before continuing."}
        </div>
      );
    }

    const typeLabel =
      TEXTBOOK_FIGURE_TYPE_LABELS[
        figureAsset
          .figureType
      ]?.[language] ||
      "Figure";

    const placedLabel =
      placedObjectLabels[
        clean(
          block?.blockId
        )
      ] ||
      typeLabel;

    const figurePlacement =
      normalizeTextbookFigurePlacement(
        block?.figurePlacement
      );

    const placementStyle =
      textbookFigurePlacementStyle(
        figurePlacement,
        flowing
      );


    return (
      <figure
        className={
          flowing
            ? "ns-textbook-figure-flow-figure"
            : undefined
        }
        data-textbook-figure-placement={
          figurePlacement
        }
        data-textbook-figure-flow-anchor-id={
          flowing
            ? clean(
                flowAnchorBlockId
              )
            : ""
        }
        style={{
          margin: 0,
          ...placementStyle,
          padding: 14,
          borderRadius: 14,
          border:
            "1px solid rgba(5,150,105,0.20)",
          background:
            "#f8fafc",
          display: "grid",
          gap: 10,
          breakInside:
            "avoid-column",
          pageBreakInside:
            "avoid",
        }}
      >
        <TextbookFigurePreview
          asset={
            figureAsset
          }
          onLoadFigureFile={
            onLoadFigureFile
          }
          isFR={isFR}
        />

        <figcaption
          style={{
            display: "grid",
            gap: 4,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
              color: "#047857",
            }}
          >
            {placedLabel}
          </span>

          {clean(
            figureAsset.caption
          ) ? (
            <strong>
              {
                figureAsset
                  .caption
              }
            </strong>
          ) : (
            <span
              className="ns-card-meta"
            >
              {isFR
                ? "Figure sans légende"
                : "Untitled Figure"}
            </span>
          )}

          {clean(
            figureAsset
              .creatorOrSource
          ) ? (
            <span
              className="ns-card-meta"
            >
              {isFR
                ? "Source : "
                : "Source: "}
              {
                figureAsset
                  .creatorOrSource
              }
            </span>
          ) : null}

          {clean(
            figureAsset
              .rightsOrLicense
          ) ? (
            <span
              className="ns-card-meta"
            >
              {isFR
                ? "Droits : "
                : "Rights: "}
              {
                figureAsset
                  .rightsOrLicense
              }
            </span>
          ) : null}
        </figcaption>

        {!readOnly && canEdit && editMode ? (
          <label
            style={{
              display: "grid",
              gap: 6,
              fontWeight: 750,
            }}
          >
            <span>
              {isFR
                ? "Placement dans le chapitre"
                : "Placement in chapter"}
            </span>

            <select
              className="ns-input"
              value={
                figurePlacement
              }
              disabled={
                saving ||
                proposalRoundBlocksCanon
              }
              onChange={(event) => {
                const nextPlacement =
                  normalizeTextbookFigurePlacement(
                    event.target.value
                  );

                updateBlock(
                  block.blockId,
                  {
                    figurePlacement:
                      nextPlacement,

                    ...(
                      nextPlacement ===
                        "full_width"
                        ? {
                            figureTextFlowAnchorId:
                              "",
                          }
                        : {}
                    ),
                  }
                );
              }}
            >
              {TEXTBOOK_FIGURE_PLACEMENTS.map(
                (placement) => (
                  <option
                    key={placement}
                    value={placement}
                  >
                    {
                      TEXTBOOK_FIGURE_PLACEMENT_LABELS[
                        placement
                      ][language]
                    }
                  </option>
                )
              )}
            </select>

            {isTextbookFigureTextFlowPlacement(
              figurePlacement
            ) ? (
              <div
                data-textbook-figure-text-wrapping="cursor"
                style={{
                  display: "grid",
                  gap: 5,
                  marginTop: 8,
                  padding:
                    "10px 12px",
                  borderRadius: 10,
                  border:
                    "1px solid rgba(5,150,105,0.20)",
                  background:
                    "#ffffff",
                }}
              >
                <strong>
                  {isFR
                    ? "Habillage automatique"
                    : "Automatic text wrapping"}
                </strong>

                <span
                  className="ns-card-meta"
                  style={{
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}
                >
                  {isFR
                    ? "L’habillage commence à l’endroit où vous avez placé la Figure. Le texte revient automatiquement à pleine largeur après la Figure."
                    : "Wrapping starts where you placed the Figure. Text automatically returns to full width after the Figure."}
                </span>
              </div>
            ) : null}
          </label>
        ) : null}

        {!readOnly && canEdit ? (
          figureEditor?.id ===
          figureAsset.id ? (
            renderFigureEditor(
              figureAsset
            )
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent:
                  "flex-end",
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  figureEditBusy
                }
                onClick={() =>
                  openFigureEditor(
                    figureAsset
                  )
                }
              >
                {isFR
                  ? "Modifier la Figure"
                  : "Edit Figure"}
              </button>
            </div>
          )
        ) : null}

        {!readOnly &&
        canEdit &&
        editMode &&
        flowing ? (
          <div
            data-textbook-flowing-figure-actions="true"
            contentEditable={false}
            suppressContentEditableWarning
            style={{
              display: "flex",
              gap: 7,
              flexWrap: "wrap",
              justifyContent:
                "flex-end",
            }}
          >
            <button
              className="ns-button ns-button-outline"
              type="button"
              disabled={
                saving ||
                !canMove(
                  block,
                  -1
                )
              }
              onClick={() =>
                moveBlock(
                  block.blockId,
                  -1
                )
              }
            >
              {isFR
                ? "Plus tôt"
                : "Earlier"}
            </button>

            <button
              className="ns-button ns-button-outline"
              type="button"
              disabled={
                saving ||
                !canMove(
                  block,
                  1
                )
              }
              onClick={() =>
                moveBlock(
                  block.blockId,
                  1
                )
              }
            >
              {isFR
                ? "Plus tard"
                : "Later"}
            </button>

            <button
              className="ns-button ns-button-outline"
              type="button"
              disabled={saving}
              onClick={() =>
                removeBlock(
                  block.blockId
                )
              }
            >
              {isFR
                ? "Retirer"
                : "Remove"}
            </button>
          </div>
        ) : null}
      </figure>
    );
  }




  function renderReferenceBlock(
    block,
    figureFlow = null,
    readOnly = false
  ) {
    if (
      block?.type ===
        "figure"
    ) {
      return renderFigureBlock(
        block,
        false,
        readOnly
      );
    }

    if (
      block?.type ===
        "equation"
    ) {
      const equation =
        normalizeTextbookEquation(
          block?.equation,
          block?.text
        );

      if (
        !clean(
          equation.source
        )
      ) {
        return null;
      }

      return (
        <div
          style={{
            padding:
              "12px 14px",
            borderRadius: 12,
            border:
              "1px solid rgba(100,116,139,0.22)",
            background:
              "#f8fafc",
            display: "grid",
            gap: 8,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
              color: "#475569",
            }}
          >
            {
              TYPE_LABELS
                .equation[
                  language
                ]
            }
          </div>

          <TextbookEquationPreview
            equation={equation}
            isFR={isFR}
          />
        </div>
      );
    }

    if (
      block?.type ===
        "table"
    ) {
      return (
        <div
          data-textbook-reference-table="true"
          style={{
            padding:
              "12px 14px",
            borderRadius: 12,
            border:
              "1px solid rgba(148,163,184,0.24)",
            background:
              "#f8fafc",
            display: "grid",
            gap: 8,
          }}
        >
          <TextbookTablePreview
            table={
              block?.table
            }
            isFR={isFR}
            label={
              placedObjectLabels[
                clean(
                  block?.blockId
                )
              ] ||
              TYPE_LABELS
                .table[
                  language
                ]
            }
          />
        </div>
      );
    }

    const text =
      exactText(
        block?.text
      );

    const hasStructuredContent =
      block?.manuscriptContent !==
        undefined;

    /*
     * Structured cards with no meaningful text should not reserve page
     * space. Clearing a Reference / Citation, Exercise, Note, etc. therefore
     * removes its visual footprint even before the writer deletes the block.
     */
    if (
      !clean(text) &&
      ![
        "paragraph",
        "heading",
      ].includes(
        block?.type
      )
    ) {
      return null;
    }

    if (
      !clean(text) &&
      !hasStructuredContent
    ) {
      return null;
    }

    const richReadOnly =
      hasStructuredContent ? (
        <ChildrensBookManuscriptReadOnly
          content={
            block.manuscriptContent
          }
          fallbackText={text}
          lang={language}
          flowAnchorBlockId={
            clean(
              figureFlow
                ?.anchorBlockId
            )
          }
          flowContent={
            figureFlow
              ?.content ||
            null
          }
          flowInsertions={
            Array.isArray(
              figureFlow
                ?.additionalInsertions
            )
              ? figureFlow
                  .additionalInsertions
              : []
          }
          embeddedFlow={true}
        />
      ) : null;

    if (
      block?.type ===
        "heading"
    ) {
      return (
        <h4
          style={{
            margin:
              "4px 0 0",
            fontSize: 20,
            lineHeight: 1.3,
          }}
        >
          {text}
        </h4>
      );
    }

    if (
      block?.type ===
        "paragraph"
    ) {
      const ParagraphTag =
        richReadOnly
          ? "div"
          : "p";

      return (
        <ParagraphTag
          style={{
            margin: 0,
            whiteSpace:
              "pre-wrap",
            lineHeight: 1.7,
          }}
        >
          {richReadOnly || text}
        </ParagraphTag>
      );
    }

    const label =
      TYPE_LABELS[
        block?.type
      ]?.[language] ||
      (
        isFR
          ? "Contenu"
          : "Content"
      );

    return (
      <div
        style={{
          padding:
            "12px 14px",
          borderRadius: 12,
          border:
            "1px solid rgba(148,163,184,0.24)",
          background:
            block?.type ===
              "note"
              ? "#fffbeb"
              : [
                  "worked_example",
                  "exercise",
                ].includes(
                  block?.type
                )
              ? "#eff6ff"
              : "#f8fafc",
        }}
      >
        <div
          style={{
            marginBottom: 5,
            fontSize: 11,
            fontWeight: 900,
            letterSpacing:
              "0.08em",
            textTransform:
              "uppercase",
            color: "#475569",
          }}
        >
          {label}
        </div>

        <div
          style={{
            whiteSpace:
              "pre-wrap",
            lineHeight: 1.65,
          }}
        >
          {richReadOnly || text}
        </div>
      </div>
    );
  }

  function renderReferenceFlowUnit(
    unit,
    readOnly = false
  ) {
    const figure =
      unit?.figure;

    if (
      [
        "rich_manuscript_figure_flow",
        "cursor_figure_flow",
      ].includes(
        unit?.kind
      )
    ) {
      return (
        <div
          key={
            `rich-figure-flow-${figure?.blockId || "figure"}`
          }
          data-textbook-rich-manuscript-figure-flow="true"
        >
          {renderReferenceBlock(
            unit.paragraph,
            {
              anchorBlockId:
                unit.anchorId,

              content:
                renderFigureBlock(
                  figure,
                  true,
                  readOnly,
                  unit.anchorId
                ),

              additionalInsertions:
                (
                  Array.isArray(
                    unit
                      ?.additionalFigureFlows
                  )
                    ? unit
                        .additionalFigureFlows
                    : []
                ).map(
                  (
                    additional
                  ) => ({
                    anchorBlockId:
                      additional
                        .anchorId,

                    content:
                      renderFigureBlock(
                        additional
                          .figure,
                        true,
                        readOnly,
                        additional
                          .anchorId
                      ),
                  })
                ),
            }
          )}

          {/*
           * Solo / Group Open pattern: keep the manuscript itself continuous,
           * then close only the explicit Figure-flow unit. This prevents the
           * Figure float from leaking into a later Textbook block without
           * making the whole rich manuscript an unfragmentable flow-root.
           */}
          <div
            data-textbook-figure-flow-end="true"
            aria-hidden="true"
            style={{
              clear: "both",
              height: 0,
              margin: 0,
              padding: 0,
            }}
          />
        </div>
      );
    }

    const paragraphs =
      Array.isArray(
        unit?.paragraphs
      )
        ? unit.paragraphs
        : [];

    if (
      !figure ||
      paragraphs.length === 0
    ) {
      return null;
    }

    return (
      <div
        key={
          `figure-flow-${figure.blockId}`
        }
        className="ns-textbook-figure-flow"
        data-textbook-figure-text-flow="true"
      >
        {renderFigureBlock(
          figure,
          true,
          readOnly
        )}

        {paragraphs.map(
          (paragraph) => (
            <div
              key={
                paragraph.blockId
              }
              className="ns-textbook-figure-flow-paragraph"
            >
              {renderReferenceBlock(
                paragraph
              )}
            </div>
          )
        )}
      </div>
    );
  }




  function renderProposalFocusCard() {
    if (!proposalRoundOpen) {
      return null;
    }

    const focusSection =
      proposalFocusMode ===
        "section"
        ? sections.find(
            (section) =>
              clean(
                section?.itemId
              ) ===
              proposalFocusSectionItemId
          ) ||
          null
        : null;

    const focusSectionBlocks =
      focusSection
        ? current.filter(
            (block) =>
              clean(
                block
                  ?.sectionItemId
              ) ===
              clean(
                focusSection
                  ?.itemId
              )
          )
        : [];

    const focusLabel =
      proposalFocusMode ===
        "section"
        ? (
            isFR
              ? "SECTION"
              : "SECTION"
          )
        : proposalFocusMode ===
            "selected_passage"
          ? (
              isFR
                ? "PASSAGE SÉLECTIONNÉ"
                : "SELECTED PASSAGE"
            )
          : (
              isFR
                ? "CHAPITRE ENTIER"
                : "WHOLE CHAPTER"
            );

    const focusTitle =
      proposalFocusMode ===
        "section"
        ? (
            proposalFocusSectionTitle ||
            clean(
              focusSection
                ?.title
            ) ||
            (
              isFR
                ? "Section sélectionnée"
                : "Selected Section"
            )
          )
        : proposalFocusMode ===
            "selected_passage"
          ? (
              isFR
                ? "Travaillez sur ce passage précis"
                : "Work on this exact passage"
            )
          : (
              isFR
                ? "Examinez le chapitre dans son ensemble"
                : "Review the chapter as a whole"
            );

    return (
      <section
        data-textbook-proposal-focus="true"
        aria-label={
          isFR
            ? "Portée de la proposition"
            : "Proposal focus"
        }
        style={{
          marginBottom: 18,
          padding: 16,
          borderRadius: 14,
          border:
            "1.5px solid rgba(180,83,9,0.28)",
          background:
            "#fffbeb",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            letterSpacing:
              "0.1em",
            textTransform:
              "uppercase",
            color: "#92400e",
          }}
        >
          {isFR
            ? "FOCUS DE CETTE PROPOSITION"
            : "FOCUS FOR THIS PROPOSAL"}
        </div>

        <div
          style={{
            marginTop: 8,
            display: "flex",
            gap: 10,
            alignItems:
              "baseline",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
              color: "#b45309",
            }}
          >
            {focusLabel}
          </span>

          <strong
            style={{
              fontSize: 17,
            }}
          >
            {focusTitle}
          </strong>
        </div>

        {proposalFocusMode ===
          "selected_passage" ? (
          <div
            style={{
              marginTop: 12,
              padding:
                "13px 14px",
              borderRadius: 11,
              background:
                "#ffffff",
              border:
                "1px solid rgba(180,83,9,0.18)",
              whiteSpace:
                "pre-wrap",
              lineHeight: 1.65,
            }}
          >
            {proposalFocusSelectedPassage}
          </div>
        ) : null}

        {proposalFocusMode ===
          "section" &&
        focusSectionBlocks.length >
          0 ? (
          <div
            style={{
              marginTop: 14,
              padding:
                "14px 15px",
              borderRadius: 12,
              background:
                "#ffffff",
              border:
                "1px solid rgba(180,83,9,0.16)",
              display: "grid",
              gap: 12,
            }}
          >
            {buildTextbookFigureTextFlowUnits(
              focusSectionBlocks
            ).map(
              (unit) => {
                if (
                  [
                    "figure_text_flow",
                    "rich_manuscript_figure_flow",
                    "cursor_figure_flow",
                  ].includes(
                    unit.kind
                  )
                ) {
                  return renderReferenceFlowUnit(
                    unit,
                    true
                  );
                }

                const block =
                  unit.block;

                return (
                  <div
                    key={
                      block?.blockId
                    }
                  >
                    {renderReferenceBlock(
                      block
                    )}
                  </div>
                );
              }
            )}
          </div>
        ) : null}

        {proposalFocusMode ===
          "whole_chapter" ? (
          <p
            className="ns-card-meta"
            style={{
              margin:
                "10px 0 0",
              lineHeight: 1.55,
            }}
          >
            {isFR
              ? "Le chapitre entier constitue la portée de cette demande. Le Canon complet reste disponible ci-dessous comme contexte en lecture seule."
              : "The entire chapter is in scope for this request. The complete Canon remains available below as read-only context."}
          </p>
        ) : (
          <p
            className="ns-card-meta"
            style={{
              margin:
                "10px 0 0",
              lineHeight: 1.55,
            }}
          >
            {isFR
              ? "Votre contribution doit rester dans cette portée. Le chapitre entier reste disponible plus bas comme contexte facultatif."
              : "Keep your contribution within this focus. The whole chapter remains available below as optional context."}
          </p>
        )}
      </section>
    );
  }


  async function openProposalRound() {
    if (
      !canStartProposalRound ||
      proposalWorking
    ) {
      return;
    }

    const openingReason =
      clean(
        proposalOpeningReason
      );

    if (!openingReason) {
      setProposalError(
        isFR
          ? "Indiquez ce que vous souhaitez que les autres Writers examinent ou améliorent."
          : "State what you want other writers to review or improve."
      );
      return;
    }

    if (!proposalScopeReady) {
      setProposalError(
        proposalScopeMode ===
          "section"
          ? (
              isFR
                ? "Choisissez la section à examiner."
                : "Choose the Section contributors should review."
            )
          : (
              isFR
                ? "Ajoutez le passage précis à examiner."
                : "Add the exact passage contributors should review."
            )
      );
      return;
    }

    const proposalScope = {
      mode:
        proposalScopeMode,

      sectionItemId:
        proposalScopeMode ===
          "section"
          ? clean(
              proposalScopeSection
                ?.itemId
            )
          : "",

      sectionTitle:
        proposalScopeMode ===
          "section"
          ? clean(
              proposalScopeSection
                ?.title
            )
          : "",

      selectedPassage:
        proposalScopeMode ===
          "selected_passage"
          ? clean(
              proposalScopeSelectedPassage
            )
          : "",
    };

    try {
      setProposalWorking(true);
      setProposalError("");
      setProposalNotice("");

      await onOpenProposalRound({
        openingReason,
        closesAt: null,
        idempotencyKey:
          proposalOpenKeyRef
            .current,
        proposalScope,
      });

      proposalOpenKeyRef.current =
        freshKey(
          "textbook-chapter-round-open"
        );

      setProposalOpeningReason(
        ""
      );
      setProposalScopeMode(
        "whole_chapter"
      );
      setProposalScopeSectionItemId(
        ""
      );
      setProposalScopeSelectedPassage(
        ""
      );
      setShowProposalOpenForm(
        false
      );

      setProposalNotice(
        isFR
          ? "Propositions ouvertes."
          : "Proposals opened."
      );
    } catch (error) {
      setProposalError(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible d’ouvrir les propositions."
              : "Could not open proposals."
          )
      );
    } finally {
      setProposalWorking(false);
    }
  }


  async function closeProposalRound() {
    const proposalRoundId =
      clean(
        proposalRound?.id
      );

    const expectedRevision =
      Number(
        proposalRound
          ?.revision
      );

    const closingReason =
      clean(
        proposalClosingReason
      );

    if (
      !canManageProposalRound ||
      !proposalRoundOpen ||
      !proposalRoundId ||
      !Number.isInteger(
        expectedRevision
      ) ||
      expectedRevision < 1 ||
      typeof onCloseProposalRound !==
        "function" ||
      proposalWorking
    ) {
      return;
    }

    if (!closingReason) {
      setProposalError(
        isFR
          ? "Indiquez brièvement pourquoi les propositions sont clôturées."
          : "Briefly record why proposals are being closed."
      );
      return;
    }

    try {
      setProposalWorking(true);
      setProposalError("");
      setProposalNotice("");

      await onCloseProposalRound({
        proposalRoundId,
        expectedRevision,
        closingReason,
      });

      setProposalClosingReason(
        ""
      );
      setShowProposalCloseForm(
        false
      );

      setProposalNotice(
        isFR
          ? "Propositions clôturées."
          : "Proposals closed."
      );
    } catch (error) {
      setProposalError(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible de clôturer les propositions."
              : "Could not close proposals."
          )
      );
    } finally {
      setProposalWorking(false);
    }
  }



  function updateContributorProposalRichBlock(
    editorValue
  ) {
    const prepared =
      prepareTextbookRichManuscriptBlock(
        {
          blockId:
            "textbook-proposal-draft",
          type:
            "paragraph",
          sectionItemId:
            "",
          text:
            contributorProposalText,
          manuscriptContent:
            contributorProposalManuscriptContent,
        },
        editorValue
          ?.manuscriptContent
      );

    if (
      !prepared.ok ||
      !prepared.value
    ) {
      setContributorProposalError(
        isFR
          ? "Le format de cette proposition n’a pas pu être conservé."
          : "The formatting in this proposal could not be preserved."
      );
      return;
    }

    setContributorProposalText(
      prepared.value.text
    );

    setContributorProposalManuscriptContent(
      prepared
        .value
        .manuscriptContent
    );

    setContributorProposalError(
      ""
    );
  }


  async function submitContributorProposal() {
    const proposalRoundId =
      clean(
        proposalRound?.id
      );

    const expectedChapterRevision =
      Number(
        proposalRound
          ?.scope
          ?.baseContentRevision
      );

    const proposalText =
      clean(
        contributorProposalText
      );

    if (
      !canSubmitProposal ||
      !proposalRoundOpen ||
      !proposalRoundId ||
      !Number.isInteger(
        expectedChapterRevision
      ) ||
      expectedChapterRevision < 1 ||
      typeof onSubmitProposal !==
        "function" ||
      contributorProposalWorking
    ) {
      return;
    }

    if (!proposalText) {
      setContributorProposalError(
        isFR
          ? "Écrivez votre proposition avant de l’envoyer."
          : "Write your proposal before submitting it."
      );
      return;
    }

    try {
      setContributorProposalWorking(
        true
      );
      setContributorProposalError(
        ""
      );
      await onSubmitProposal({
        proposalRoundId,
        expectedChapterRevision,
        proposalText,
        proposalManuscriptContent:
          contributorProposalManuscriptContent,
        submissionKey:
          contributorProposalKeyRef
            .current,
      });

      contributorProposalKeyRef.current =
        freshKey(
          "textbook-chapter-proposal"
        );

      setContributorProposalText(
        ""
      );

      setContributorProposalManuscriptContent(
        undefined
      );

      setContributorProposalComposerOpen(
        false
      );

      setContributorSubmitConfirmOpen(
        false
      );

    } catch (error) {
      setContributorProposalError(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible d’envoyer votre proposition."
              : "Could not submit your proposal."
          )
      );
    } finally {
      setContributorProposalWorking(
        false
      );
    }
  }


  async function save(
    event
  ) {
    event?.preventDefault?.();

    if (
      !canEdit ||
      saving ||
      (
        !dirty &&
        !canonNeedsConsolidation
      ) ||
      !Number.isInteger(
        revision
      ) ||
      revision < 0 ||
      typeof onSave !==
        "function"
    ) {
      return;
    }

    try {
      setSaving(true);
      setSaveError("");
      setSaveMessage("");

      const savedDraft =
        await onSave(
          current,
          revision,
          sectionLayout
        );

      const nextSavedBlocks =
        canonicalBlocks(
          savedDraft?.blocks ??
            current,
          sections
        );

      const nextSavedSectionLayout =
        normalizeTextbookSectionLayout(
          savedDraft
            ?.sectionLayout ??
            sectionLayout,
          sections
        );

      setSavedBlocks(
        nextSavedBlocks
      );
      setBlocks(
        nextSavedBlocks
      );
      setSavedSectionLayout(
        nextSavedSectionLayout
      );
      setSectionLayout(
        nextSavedSectionLayout
      );
      setEditMode(false);
      setActiveBlockId("");
      setInsertionPoint(null);
      setContentComposer(null);
      setContentComposerError("");
      setInsertionMessage("");
      setFigureComposer(null);
      setOutlineNotice("");
      setPendingCancelEditing(false);

      setSaveMessage(
        isFR
          ? "Enregistré."
          : "Saved."
      );
    } catch (error) {
      setSaveError(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible d’enregistrer ce chapitre."
              : "Could not save this chapter."
          )
      );
    } finally {
      setSaving(false);
    }
  }



  if (
    loading ||
    (
      !chapter &&
      !loadError
    )
  ) {
    return (
      <section
        className="ns-card"
        style={{
          padding: 22,
          borderRadius: 20,
        }}
      >
        <strong>
          {isFR
            ? "Ouverture du chapitre…"
            : "Opening chapter…"}
        </strong>
      </section>
    );
  }

  if (
    !chapter ||
    loadError
  ) {
    return (
      <section
        className="ns-card"
        style={{
          padding: 22,
          borderRadius: 20,
        }}
      >
        <strong>
          {isFR
            ? "Impossible d’ouvrir ce chapitre."
            : "Could not open this chapter."}
        </strong>

        {loadError ? (
          <p
            role="alert"
            style={{
              color: "#b91c1c",
              fontWeight: 700,
            }}
          >
            {loadError}
          </p>
        ) : null}

        {typeof onReload ===
        "function" ? (
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onReload}
          >
            {isFR
              ? "Réessayer"
              : "Try Again"}
          </button>
        ) : null}
      </section>
    );
  }

  const groups = [
    {
      itemId: "",
      title:
        isFR
          ? "Texte du chapitre"
          : "Chapter text",
    },

    ...sections,
  ];

  const activeGroup =
    groups.find(
      (group) =>
        clean(
          group.itemId
        ) ===
        clean(
          activeGroupId
        )
    ) ||
    groups[0] ||
    null;

  const activeGroupBlocks =
    activeGroup
      ? current.filter(
          (block) =>
            clean(
              block
                ?.sectionItemId
            ) ===
            clean(
              activeGroup
                ?.itemId
            )
        )
      : [];

  const activeGroupIsEmpty =
    activeGroupBlocks.length === 0;

  const activeGroupInsertionPoint =
    activeGroup
      ? groupEndInsertionPoint(
          activeGroupBlocks,
          activeGroup
        )
      : null;

  const sectionGroups =
    groups.filter(
      (group) => Boolean(clean(group?.itemId))
    );

  const activeSectionIndex =
    sectionGroups.findIndex(
      (group) =>
        clean(group?.itemId) ===
        clean(activeGroup?.itemId)
    );

  const workingPageBreakBoundaryIds =
    (() => {
      const requested =
        new Set(
          sectionLayout
            .filter(
              (entry) =>
                entry
                  ?.startOnNewPage ===
                true
            )
            .map(
              (entry) =>
                clean(
                  entry
                    ?.sectionItemId
                )
            )
            .filter(Boolean)
        );

      const ids = [];
      let previousSectionId =
        "";

      current.forEach(
        (
          block,
          index
        ) => {
          const sectionItemId =
            clean(
              block
                ?.sectionItemId
            );

          const startsSection =
            Boolean(
              sectionItemId &&
              sectionItemId !==
                previousSectionId
            );

          if (
            startsSection &&
            requested.has(
              sectionItemId
            )
          ) {
            ids.push(
              textbookChapterBoundaryId(
                index
              )
            );
          }

          previousSectionId =
            sectionItemId;
        }
      );

      return ids;
    })();

  const activeSectionStartsOnNewPage =
    Boolean(
      clean(
        activeGroup
          ?.itemId
      ) &&
      sectionLayout
        .some(
          (entry) =>
            clean(
              entry
                ?.sectionItemId
            ) ===
              clean(
                activeGroup
                  ?.itemId
              ) &&
            entry
              ?.startOnNewPage ===
              true
        )
    );

  function updateActiveSectionStartOnNewPage(
    checked
  ) {
    const sectionItemId =
      clean(
        activeGroup
          ?.itemId
      );

    if (
      !sectionItemId ||
      !canEdit ||
      saving ||
      proposalRoundBlocksCanon
    ) {
      return;
    }

    const withoutSection =
      sectionLayout
        .filter(
          (entry) =>
            clean(
              entry
                ?.sectionItemId
            ) !==
            sectionItemId
        );

    setSectionLayout(
      normalizeTextbookSectionLayout(
        checked
          ? [
              ...withoutSection,
              {
                sectionItemId,
                startOnNewPage:
                  true,
              },
            ]
          : withoutSection,
        sections
      )
    );

    setSaveMessage("");
  }

  /*
   * Do not derive one visible active group here.
   * Continuous Chapter V1 renders every group below in Book Structure
   * order while activeGroup remains navigation/highlight state only.
   */


  function scrollOwnerProposalShelf(
    direction
  ) {
    const container =
      ownerProposalShelfRef.current;

    if (!container) {
      return;
    }

    container.scrollBy({
      left:
        direction === "left"
          ? -320
          : 320,
      behavior: "smooth",
    });
  }


  async function decideOwnerProposal(
    action,
    proposal = null
  ) {
    if (
      ownerDecisionWorking ||
      typeof onDecideProposal !==
        "function"
    ) {
      return;
    }

    const normalizedAction =
      clean(action)
        .toLowerCase();

    const proposalRoundId =
      clean(
        proposalRound?.id
      );

    const expectedRoundRevision =
      Number(
        proposalRound
          ?.revision
      );

    const contributionId =
      clean(
        proposal?.id
      );

    if (
      !proposalRoundId ||
      !Number.isInteger(
        expectedRoundRevision
      ) ||
      expectedRoundRevision < 1 ||
      !Number.isInteger(
        revision
      ) ||
      revision < 1 ||
      (
        normalizedAction !==
          "reject_all" &&
        !contributionId
      )
    ) {
      setOwnerDecisionError(
        isFR
          ? "Rechargez ce chapitre avant de décider."
          : "Reload this chapter before deciding."
      );
      return;
    }

    try {
      setOwnerDecisionWorking(
        true
      );
      setOwnerDecisionError(
        ""
      );
      setOwnerRejectAllConfirm(
        false
      );
      setOwnerApproveConfirmProposalId(
        ""
      );

      await onDecideProposal({
        action:
          normalizedAction,
        proposalRoundId,
        contributionId:
          normalizedAction ===
            "reject_all"
            ? ""
            : contributionId,
        expectedRoundRevision,
        expectedChapterRevision:
          revision,
      });

      if (
        normalizedAction ===
          "reject"
      ) {
        setOwnerProposalReviewOpen(
          false
        );
      }

    } catch (error) {
      setOwnerDecisionError(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible d’enregistrer cette décision."
              : "Could not record this proposal decision."
          )
      );
    } finally {
      setOwnerDecisionWorking(
        false
      );
    }
  }


  if (
    canManageProposalRound &&
    proposalRoundOpen
  ) {
    const activeOwnerProposal =
      pendingOwnerReviewProposals[
        ownerProposalIndex
      ] || null;

    const ownerApproveConfirmProposal =
      pendingOwnerReviewProposals
        .find(
          (proposal) =>
            clean(
              proposal?.id
            ) ===
            clean(
              ownerApproveConfirmProposalId
            )
        ) ||
      null;

    return (
      <section
        ref={workspaceRef}
        id="textbook-chapter-workspace"
        className="ns-card"
        style={{
          padding:
            "clamp(18px, 3vw, 28px)",
          borderRadius: 20,
          border:
            "1.5px solid rgba(37,99,235,0.24)",
          scrollMarginTop: 104,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems:
              "flex-start",
            justifyContent:
              "space-between",
            gap: 14,
            flexWrap: "wrap",
            marginBottom: 18,
          }}
        >
          <div
            style={{
              minWidth: 0,
              maxWidth: 760,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing:
                  "0.11em",
                textTransform:
                  "uppercase",
                color: "#1d4ed8",
              }}
            >
              {isFR
                ? "REVUE DU PROPRIÉTAIRE"
                : "OWNER REVIEW"}
            </div>

            <h3
              className="ns-card-title ns-textbook-academic-title"
              style={{
                margin:
                  "6px 0 6px",
                fontSize: 24,
                lineHeight: 1.2,
              }}
            >
              {clean(
                chapter?.title
              ) ||
                (
                  isFR
                    ? "Chapitre sans titre"
                    : "Untitled Chapter"
                )}
            </h3>

            <p
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Examinez les propositions reçues. Le Canon reste en lecture seule tant que ce Proposal Round est ouvert."
                : "Review the submitted proposals. Canon stays read-only while this Proposal Round is open."}
            </p>
          </div>

          {typeof onBackToBookMap ===
          "function" ? (
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={
                requestBackToBookMap
              }
            >
              {isFR
                ? "← Chapitres"
                : "← Chapters"}
            </button>
          ) : null}
        </div>

        <section
          aria-label={
            isFR
              ? "Demande du propriétaire"
              : "Owner request"
          }
          style={{
            marginBottom: 18,
            padding: 16,
            borderRadius: 14,
            border:
              "1.5px solid rgba(37,99,235,0.30)",
            background:
              "#eff6ff",
          }}
        >
          <strong>
            {isFR
              ? "Propositions ouvertes"
              : "Proposals Open"}
          </strong>

          {proposalInstruction ? (
            <div
              style={{
                marginTop: 10,
                padding:
                  "11px 13px",
                borderRadius: 10,
                background:
                  "#ffffff",
                border:
                  "1px solid rgba(37,99,235,0.18)",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing:
                    "0.08em",
                  textTransform:
                    "uppercase",
                  color: "#1d4ed8",
                  marginBottom: 5,
                }}
              >
                {isFR
                  ? "DEMANDE DU PROPRIÉTAIRE"
                  : "OWNER REQUEST"}
              </div>

              <div
                style={{
                  lineHeight: 1.6,
                }}
              >
                {proposalInstruction}
              </div>
            </div>
          ) : null}
        </section>

        {renderProposalFocusCard()}

        <section
          data-textbook-owner-submitted-proposals="true"
          aria-label={
            isFR
              ? "Propositions reçues"
              : "Submitted proposals"
          }
          style={{
            marginBottom: 22,
            padding: 18,
            borderRadius: 16,
            border:
              "1.5px solid rgba(5,150,105,0.28)",
            background:
              "#f7fdf9",
            boxShadow:
              "0 10px 24px rgba(5,150,105,0.06)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing:
                    "0.1em",
                  textTransform:
                    "uppercase",
                  color: "#047857",
                }}
              >
                {isFR
                  ? "PROPOSITIONS REÇUES"
                  : "SUBMITTED PROPOSALS"}
              </div>

              <h4
                className="ns-textbook-academic-title"
                style={{
                  margin:
                    "5px 0 0",
                  fontSize: 20,
                }}
              >
                {pendingOwnerProposalCount > 0
                  ? (
                      isFR
                        ? `${pendingOwnerProposalCount} proposition${pendingOwnerProposalCount === 1 ? "" : "s"} à examiner`
                        : `${pendingOwnerProposalCount} proposal${pendingOwnerProposalCount === 1 ? "" : "s"} ready for review`
                    )
                  : (
                      isFR
                        ? "Aucune proposition reçue"
                        : "No proposals submitted yet"
                    )}
              </h4>
            </div>

            <span
              className="ns-card-meta"
              style={{
                fontWeight: 850,
                padding: "5px 10px",
                borderRadius: 999,
                background: "#ecfdf5",
                border:
                  "1px solid rgba(5,150,105,0.20)",
                color: "#047857",
              }}
            >
              {
                pendingOwnerReviewProposals
                  .length
              }{" "}
              {isFR
                ? "en attente"
                : "pending"}
            </span>
          </div>

          {activeOwnerProposal ? (
            <>
              <div
                data-textbook-owner-proposal-shelf-navigation="true"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "space-between",
                  gap: 10,
                  margin:
                    "16px 0 8px",
                  flexWrap: "wrap",
                }}
              >
                <div
                  className="ns-card-meta"
                  style={{
                    fontWeight: 800,
                  }}
                >
                  {isFR
                    ? "Parcourez les propositions reçues. Consultez, rejetez ou approuvez une proposition à la fois."
                    : "Browse submitted proposals. View, reject, or approve one proposal at a time."}
                </div>

                {pendingOwnerProposalCount > 1 ? (
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                    }}
                  >
                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      aria-label={
                        isFR
                          ? "Faire défiler les propositions vers la gauche"
                          : "Scroll proposals left"
                      }
                      onClick={() =>
                        scrollOwnerProposalShelf(
                          "left"
                        )
                      }
                      style={{
                        padding:
                          "4px 11px",
                      }}
                    >
                      ◀
                    </button>

                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      aria-label={
                        isFR
                          ? "Faire défiler les propositions vers la droite"
                          : "Scroll proposals right"
                      }
                      onClick={() =>
                        scrollOwnerProposalShelf(
                          "right"
                        )
                      }
                      style={{
                        padding:
                          "4px 11px",
                      }}
                    >
                      ▶
                    </button>
                  </div>
                ) : null}
              </div>

              <div
                ref={
                  ownerProposalShelfRef
                }
                data-textbook-owner-proposal-shelf="true"
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  overflowX: "auto",
                  overflowY: "hidden",
                  gap: 10,
                  padding:
                    "8px 2px 12px",
                  scrollSnapType:
                    "x proximity",
                  overscrollBehaviorX:
                    "contain",
                }}
              >
                {pendingOwnerReviewProposals.map(
                  (
                    proposal,
                    proposalIndex
                  ) => {
                    const proposalText =
                      exactText(
                        proposal
                          ?.contentSnapshot
                          ?.text
                      );

                    const contributorName =
                      clean(
                        proposal
                          ?.contributor
                          ?.displayName
                      ) ||
                      clean(
                        proposal
                          ?.contributor
                          ?.username
                      ) ||
                      "Writer";

                    const selected =
                      proposalIndex ===
                      ownerProposalIndex;

                    return (
                      <article
                        key={
                          clean(
                            proposal?.id
                          ) ||
                          `proposal-${proposalIndex}`
                        }
                        data-textbook-owner-proposal-card="true"
                        data-textbook-owner-proposal-selected={
                          selected
                            ? "true"
                            : "false"
                        }
                        style={{
                          minWidth: 280,
                          maxWidth: 280,
                          flex:
                            "0 0 280px",
                          padding:
                            "13px 14px",
                          borderRadius:
                            13,
                          background:
                            selected
                              ? "#eff6ff"
                              : "#ffffff",
                          border:
                            selected
                              ? "2px solid rgba(37,99,235,0.58)"
                              : "1px solid rgba(148,163,184,0.26)",
                          boxShadow:
                            selected
                              ? "0 8px 20px rgba(37,99,235,0.10)"
                              : "0 4px 12px rgba(15,23,42,0.05)",
                          boxSizing:
                            "border-box",
                          scrollSnapAlign:
                            "start",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 900,
                            fontSize: 15,
                          }}
                        >
                          {isFR
                            ? `Proposition de ${contributorName}`
                            : `Proposal by ${contributorName}`}
                        </div>

                        <div
                          className="ns-card-meta"
                          style={{
                            marginTop: 3,
                            fontWeight: 750,
                          }}
                        >
                          {isFR
                            ? `Proposition ${proposalIndex + 1}`
                            : `Proposal ${proposalIndex + 1}`}
                          {selected
                            ? (
                                isFR
                                  ? " · sélectionnée"
                                  : " · selected"
                              )
                            : ""}
                        </div>

                        <p
                          style={{
                            margin:
                              "10px 0 0",
                            lineHeight: 1.5,
                            fontSize: 14,
                            display:
                              "-webkit-box",
                            WebkitBoxOrient:
                              "vertical",
                            WebkitLineClamp:
                              3,
                            overflow:
                              "hidden",
                            minHeight:
                              "4.5em",
                          }}
                        >
                          {proposalText ||
                            (
                              isFR
                                ? "Aucun aperçu de texte disponible."
                                : "No text preview available."
                            )}
                        </p>

                        <div
                          style={{
                            marginTop: 11,
                            display: "grid",
                            gap: 6,
                          }}
                        >
                          <button
                            className="ns-button ns-button-outline"
                            type="button"
                            onClick={() => {
                              setOwnerProposalIndex(
                                proposalIndex
                              );
                              setOwnerProposalReviewOpen(
                                true
                              );
                            }}
                          >
                            {isFR
                              ? "Voir la proposition"
                              : "View Proposal"}
                          </button>

                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              flexWrap: "wrap",
                            }}
                          >
                            <button
                              className="ns-button ns-button-primary"
                              type="button"
                              disabled={
                                ownerDecisionWorking
                              }
                              onClick={() =>
                                setOwnerApproveConfirmProposalId(
                                  clean(
                                    proposal?.id
                                  )
                                )
                              }
                              style={{
                                flex:
                                  "1 1 110px",
                              }}
                            >
                              {isFR
                                ? "Approuver et adopter"
                                : "Approve & Adopt"}
                            </button>

                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              disabled={
                                ownerDecisionWorking
                              }
                              onClick={() =>
                                decideOwnerProposal(
                                  "reject",
                                  proposal
                                )
                              }
                              style={{
                                flex:
                                  "1 1 100px",
                              }}
                            >
                              {isFR
                                ? "Rejeter"
                                : "Reject"}
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  }
                )}
              </div>

              {ownerProposalReviewOpen &&
              activeOwnerProposal ? (
                <div
                  data-textbook-owner-proposal-review-backdrop="true"
                  role="presentation"
                  onMouseDown={(event) => {
                    if (
                      event.target ===
                      event.currentTarget
                    ) {
                      setOwnerProposalReviewOpen(
                        false
                      );
                    }
                  }}
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1320,
                    display: "grid",
                    placeItems: "center",
                    padding:
                      "clamp(10px, 2vw, 24px)",
                    background:
                      "rgba(15,23,42,0.56)",
                  }}
                >
                  <section
                    data-textbook-owner-proposal-review-dialog="true"
                    role="dialog"
                    aria-modal="true"
                    aria-label={
                      isFR
                        ? "Revue complète de la proposition"
                        : "Full Proposal Review"
                    }
                    style={{
                      width:
                        "min(1180px, 96vw)",
                      height:
                        "min(900px, 90vh)",
                      background: "#ffffff",
                      borderRadius: 18,
                      border:
                        "1px solid rgba(5,150,105,0.26)",
                      boxShadow:
                        "0 28px 70px rgba(15,23,42,0.28)",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "14px 18px",
                        borderBottom:
                          "1px solid rgba(148,163,184,0.22)",
                        background: "#f7fdf9",
                        display: "flex",
                        justifyContent:
                          "space-between",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 900,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            color: "#047857",
                          }}
                        >
                          {isFR
                            ? "PROPOSITION EN LECTURE SEULE"
                            : "READ-ONLY PROPOSAL"}
                        </div>

                        <div
                          className="ns-textbook-academic-title"
                          style={{
                            marginTop: 3,
                            fontSize: 20,
                          }}
                        >
                          {isFR
                            ? `Proposition de ${clean(
                                activeOwnerProposal
                                  ?.contributor
                                  ?.displayName
                              ) ||
                              clean(
                                activeOwnerProposal
                                  ?.contributor
                                  ?.username
                              ) ||
                              "Writer"}`
                            : `Proposal by ${clean(
                                activeOwnerProposal
                                  ?.contributor
                                  ?.displayName
                              ) ||
                              clean(
                                activeOwnerProposal
                                  ?.contributor
                                  ?.username
                              ) ||
                              "Writer"}`}
                        </div>

                        <div
                          className="ns-card-meta"
                          style={{
                            marginTop: 3,
                            fontWeight: 750,
                          }}
                        >
                          {isFR
                            ? `Proposition ${ownerProposalIndex + 1} sur ${pendingOwnerReviewProposals.length}`
                            : `Proposal ${ownerProposalIndex + 1} of ${pendingOwnerReviewProposals.length}`}
                        </div>
                      </div>

                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        onClick={() =>
                          setOwnerProposalReviewOpen(
                            false
                          )
                        }
                      >
                        {isFR
                          ? "Fermer la revue"
                          : "Close Review"}
                      </button>
                    </div>

                    <div
                      data-textbook-owner-proposal-review-reader="true"
                      style={{
                        flex: "1 1 auto",
                        minHeight: 0,
                        overflowY: "auto",
                        padding: "18px",
                        background: "#ffffff",
                      }}
                    >
                      <TextbookProposalSnapshotReadOnly
                        contribution={
                          activeOwnerProposal
                        }
                        lang={language}
                      />
                    </div>

                    <div
                      style={{
                        padding: "12px 18px",
                        borderTop:
                          "1px solid rgba(148,163,184,0.22)",
                        background: "#f8fafc",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        className="ns-button ns-button-primary"
                        type="button"
                        disabled={
                          ownerDecisionWorking
                        }
                        onClick={() =>
                          setOwnerApproveConfirmProposalId(
                            clean(
                              activeOwnerProposal
                                ?.id
                            )
                          )
                        }
                      >
                        {isFR
                          ? "Approuver et adopter"
                          : "Approve & Adopt"}
                      </button>

                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={
                          ownerDecisionWorking
                        }
                        onClick={() =>
                          decideOwnerProposal(
                            "reject",
                            activeOwnerProposal
                          )
                        }
                      >
                        {isFR
                          ? "Rejeter"
                          : "Reject"}
                      </button>
                    </div>
                  </section>
                </div>
              ) : null}

              {ownerApproveConfirmProposal ? (
                <div
                  data-textbook-owner-adopt-confirm-backdrop="true"
                  role="presentation"
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1480,
                    display: "grid",
                    placeItems: "center",
                    padding: 20,
                    background:
                      "rgba(15,23,42,0.60)",
                  }}
                >
                  <section
                    data-textbook-owner-adopt-confirm-dialog="true"
                    role="alertdialog"
                    aria-modal="true"
                    aria-label={
                      isFR
                        ? "Confirmer l’adoption de la proposition"
                        : "Confirm proposal adoption"
                    }
                    style={{
                      width:
                        "min(620px, 94vw)",
                      borderRadius: 18,
                      border:
                        "1px solid rgba(5,150,105,0.28)",
                      background:
                        "#ffffff",
                      boxShadow:
                        "0 28px 80px rgba(15,23,42,0.34)",
                      padding: 20,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 900,
                        letterSpacing:
                          "0.1em",
                        textTransform:
                          "uppercase",
                        color: "#047857",
                      }}
                    >
                      {isFR
                        ? "CONFIRMATION REQUISE"
                        : "CONFIRMATION REQUIRED"}
                    </div>

                    <h4
                      className="ns-textbook-academic-title"
                      style={{
                        margin:
                          "6px 0 8px",
                        fontSize: 22,
                      }}
                    >
                      {isFR
                        ? "Approuver et adopter cette proposition ?"
                        : "Approve & Adopt this proposal?"}
                    </h4>

                    <p
                      className="ns-card-meta"
                      style={{
                        margin: 0,
                        lineHeight: 1.6,
                      }}
                    >
                      {isFR
                        ? "Cette action ajoutera cette proposition au Canon du chapitre, écartera toutes les autres propositions restantes de ce round, clôturera le Proposal Round et vous ramènera au Canon. Vous pouvez annuler pour poursuivre votre revue."
                        : "This will add this proposal to Chapter Canon, discard all other remaining proposals in this round, close the Proposal Round, and return you to Canon. Cancel if you want to continue reviewing."}
                    </p>

                    <div
                      style={{
                        display: "flex",
                        justifyContent:
                          "flex-end",
                        gap: 8,
                        flexWrap: "wrap",
                        marginTop: 18,
                      }}
                    >
                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={
                          ownerDecisionWorking
                        }
                        onClick={() =>
                          setOwnerApproveConfirmProposalId(
                            ""
                          )
                        }
                      >
                        {isFR
                          ? "Annuler"
                          : "Cancel"}
                      </button>

                      <button
                        className="ns-button ns-button-primary"
                        type="button"
                        disabled={
                          ownerDecisionWorking
                        }
                        aria-busy={
                          ownerDecisionWorking
                        }
                        onClick={() =>
                          decideOwnerProposal(
                            "accept",
                            ownerApproveConfirmProposal
                          )
                        }
                      >
                        {ownerDecisionWorking
                          ? (
                              isFR
                                ? "Adoption…"
                                : "Adopting…"
                            )
                          : (
                              isFR
                                ? "Confirmer l’adoption"
                                : "Confirm Adoption"
                            )}
                      </button>
                    </div>
                  </section>
                </div>
              ) : null}

            </>
          ) : (
            <p
              className="ns-card-meta"
              style={{
                margin:
                  "14px 0 0",
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Aucune proposition n’a encore été envoyée. Le chapitre reste verrouillé jusqu’à la fin du Proposal Round."
                : "No proposals have been submitted yet. The chapter remains locked until the Proposal Round ends."}
            </p>
          )}

          {ownerDecisionError ? (
            <div
              role="alert"
              style={{
                color: "#b91c1c",
                fontWeight: 700,
                marginTop: 14,
              }}
            >
              {ownerDecisionError}
            </div>
          ) : null}

          <div
            className="ns-card-meta"
            style={{
              marginTop: 14,
              lineHeight: 1.5,
            }}
          >
            {isFR
              ? "Rejeter supprime uniquement cette proposition et garde le Proposal Round ouvert. Approuver et adopter intègre une seule proposition au Canon, écarte les autres propositions restantes, clôt le round et ramène le propriétaire au Canon. Rejeter toutes les propositions écarte toutes les propositions restantes et ramène le propriétaire au Canon."
              : "Reject removes only that proposal and keeps this Proposal Round open. Approve & Adopt places one proposal into Canon, discards the other remaining proposals, closes the round, and returns the Owner to Canon. Reject All Proposals discards all remaining proposals and returns the Owner to Canon."}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent:
                "flex-end",
              alignItems:
                "center",
              gap: 8,
              flexWrap: "wrap",
              marginTop: 14,
            }}
          >
            {ownerRejectAllConfirm ? (
              <>
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    ownerDecisionWorking
                  }
                  onClick={() =>
                    setOwnerRejectAllConfirm(
                      false
                    )
                  }
                >
                  {isFR
                    ? "Annuler"
                    : "Cancel"}
                </button>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    ownerDecisionWorking
                  }
                  onClick={() =>
                    decideOwnerProposal(
                      "reject_all"
                    )
                  }
                >
                  {isFR
                    ? "Confirmer le rejet de toutes"
                    : "Confirm Reject All"}
                </button>
              </>
            ) : (
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  ownerDecisionWorking
                }
                onClick={() =>
                  setOwnerRejectAllConfirm(
                    true
                  )
                }
              >
                {isFR
                  ? "Rejeter toutes les propositions"
                  : "Reject All Proposals"}
              </button>
            )}
          </div>
        </section>

        <section
          data-textbook-owner-chapter-reference-launcher="true"
          aria-label={
            isFR
              ? "Chapitre de référence"
              : "Chapter for reference"
          }
          style={{
            borderTop:
              "1px solid rgba(148,163,184,0.24)",
            paddingTop: 16,
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing:
                  "0.1em",
                textTransform:
                  "uppercase",
                color: "#64748b",
              }}
            >
              {isFR
                ? "RÉFÉRENCE DU CANON"
                : "CANON REFERENCE"}
            </div>

            <div
              className="ns-textbook-academic-title"
              style={{
                marginTop: 4,
                fontSize: 18,
              }}
            >
              {isFR
                ? "Canon du chapitre — référence"
                : "Chapter Canon — Reference"}
            </div>

            <p
              className="ns-card-meta"
              style={{
                margin: "4px 0 0",
              }}
            >
              {isFR
                ? "Ouvrez le lecteur de référence sans quitter votre position dans la revue."
                : "Open the reference reader without losing your place in the review."}
            </p>
          </div>

          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={() =>
              setOwnerChapterReferenceOpen(
                true
              )
            }
          >
            {isFR
              ? "Voir la référence du chapitre"
              : "View Chapter Reference"}
          </button>
        </section>

        {ownerChapterReferenceOpen ? (
          <div
            data-textbook-owner-chapter-reference-backdrop="true"
            role="presentation"
            onMouseDown={(event) => {
              if (
                event.target ===
                event.currentTarget
              ) {
                setOwnerChapterReferenceOpen(
                  false
                );
              }
            }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              display: "grid",
              placeItems: "center",
              padding:
                "clamp(10px, 2vw, 24px)",
              background:
                "rgba(15,23,42,0.56)",
            }}
          >
            <section
              data-textbook-owner-chapter-reference-dialog="true"
              role="dialog"
              aria-modal="true"
              aria-label={
                isFR
                  ? "Lecteur de référence du chapitre"
                  : "Chapter Reference Reader"
              }
              style={{
                width:
                  "min(1180px, 96vw)",
                height:
                  "min(900px, 90vh)",
                background:
                  "#ffffff",
                borderRadius: 18,
                border:
                  "1px solid rgba(5,150,105,0.26)",
                boxShadow:
                  "0 28px 70px rgba(15,23,42,0.28)",
                display: "flex",
                flexDirection:
                  "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding:
                    "14px 18px",
                  borderBottom:
                    "1px solid rgba(148,163,184,0.22)",
                  background:
                    "#f7fdf9",
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                  gap: 14,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 900,
                      letterSpacing:
                        "0.1em",
                      textTransform:
                        "uppercase",
                      color: "#047857",
                    }}
                  >
                    {isFR
                      ? "RÉFÉRENCE EN LECTURE SEULE"
                      : "READ-ONLY REFERENCE"}
                  </div>

                  <div
                    className="ns-textbook-academic-title"
                    style={{
                      marginTop: 3,
                      fontSize: 20,
                    }}
                  >
                    {clean(
                      chapter?.title
                    ) ||
                      (
                        isFR
                          ? "Chapitre sans titre"
                          : "Untitled Chapter"
                      )}
                  </div>
                </div>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={() =>
                    setOwnerChapterReferenceOpen(
                      false
                    )
                  }
                >
                  {isFR
                    ? "Fermer"
                    : "Close Reference"}
                </button>
              </div>

              {ownerChapterReferenceParts
                .length > 0 ? (
                <div
                  data-textbook-owner-reference-part-selector="true"
                  style={{
                    padding:
                      "12px 18px",
                    borderBottom:
                      "1px solid rgba(148,163,184,0.22)",
                    background:
                      "#ffffff",
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "space-between",
                    gap: 12,
                    flexWrap:
                      "wrap",
                  }}
                >
                  <label
                    style={{
                      display:
                        "flex",
                      alignItems:
                        "center",
                      gap: 10,
                      flex:
                        "1 1 520px",
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontWeight:
                          850,
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      {isFR
                        ? "Partie de référence"
                        : "Reference Part"}
                    </span>

                    <select
                      className="ns-input"
                      value={
                        clean(
                          ownerChapterReferencePart
                            ?.id
                        )
                      }
                      onChange={(
                        event
                      ) =>
                        setOwnerChapterReferencePartId(
                          clean(
                            event
                              .target
                              .value
                          )
                        )
                      }
                      style={{
                        minWidth: 0,
                        maxWidth:
                          520,
                      }}
                    >
                      {ownerChapterReferenceParts
                        .map(
                          (
                            part,
                            partIndex
                          ) => (
                            <option
                              key={
                                part.id
                              }
                              value={
                                part.id
                              }
                            >
                              {`${partIndex + 1}. ${part.title}`}
                            </option>
                          )
                        )}
                    </select>
                  </label>

                  <div
                    className="ns-card-meta"
                    style={{
                      fontWeight:
                        800,
                    }}
                  >
                    {isFR
                      ? "Une seule partie du Canon est affichée à la fois."
                      : "Only one Canon part is shown at a time."}
                  </div>
                </div>
              ) : null}

              <div
                data-textbook-owner-chapter-reference-reader="true"
                style={{
                  flex: "1 1 auto",
                  minHeight: 0,
                  overflowY: "auto",
                  padding:
                    "16px 18px 22px",
                  background:
                    "#ffffff",
                }}
              >
                <TextbookLiveChapterPageFlow
                  chapterTitle={
                    clean(
                      chapter?.title
                    )
                  }
                  blocks={
                    ownerChapterReferenceBlocks
                  }
                  sections={
                    ownerChapterReferenceSections
                  }
                  sectionLayout={
                    ownerChapterReferenceSectionLayout
                  }
                  formatPlan={formatPlan}
                  isFR={isFR}
                  canEdit={false}
                  editMode={false}
                  proofMode={true}
                  stickyNavigation={true}
                  renderBlock={(
                    block
                  ) =>
                    renderReferenceBlock(
                      block,
                      null,
                      true
                    )
                  }
                  renderFlowUnit={(
                    unit
                  ) =>
                    renderReferenceFlowUnit(
                      unit,
                      true
                    )
                  }
                />
              </div>
            </section>
          </div>
        ) : null}
      </section>
    );
  }


  if (
    !canEdit &&
    !canManageProposalRound
  ) {
    const proposals =
      Array.isArray(
        myProposals
      )
        ? myProposals
        : [];

    const hasSubmittedProposal =
      proposals.length > 0;

    const contributorProposalMode =
      proposalRoundOpen &&
      canSubmitProposal &&
      !hasSubmittedProposal;

    const activeContributorProposal =
      proposals.find(
        (proposal) =>
          clean(
            proposal?.id
          ) ===
          clean(
            contributorProposalReviewId
          )
      ) ||
      null;

    const contributorProposalReviewIndex =
      activeContributorProposal
        ? Math.max(
            0,
            proposals.findIndex(
              (proposal) =>
                clean(
                  proposal?.id
                ) ===
                clean(
                  activeContributorProposal
                    ?.id
                )
            )
          )
        : 0;

    const contributorChapterReferencePart =
      ownerChapterReferenceParts
        .find(
          (part) =>
            clean(
              part?.id
            ) ===
            clean(
              contributorChapterReferencePartId
            )
        ) ||
      ownerChapterReferenceParts[
        0
      ] ||
      null;

    const contributorChapterReferenceBlocks =
      contributorChapterReferencePart
        ?.blocks ||
      [];

    const contributorChapterReferenceSections =
      contributorChapterReferencePart
        ?.sections ||
      [];

    const contributorChapterReferenceSectionLayout =
      sectionLayout.filter(
        (entry) =>
          contributorChapterReferenceSections
            .some(
              (section) =>
                clean(
                  section?.itemId
                ) ===
                clean(
                  entry
                    ?.sectionItemId
                )
            )
      );

    function openContributorChapterReference() {
      const preferredPartId =
        proposalFocusMode ===
          "section" &&
        ownerChapterReferenceParts
          .some(
            (part) =>
              clean(
                part?.id
              ) ===
              clean(
                proposalFocusSectionItemId
              )
          )
          ? proposalFocusSectionItemId
          : clean(
              ownerChapterReferenceParts[
                0
              ]?.id
            );

      setContributorChapterReferencePartId(
        preferredPartId
      );
      setContributorChapterReferenceOpen(
        true
      );
    }

    return (
      <section
        ref={workspaceRef}
        id="textbook-chapter-workspace"
        className="ns-card"
        style={{
          padding:
            "clamp(18px, 3vw, 28px)",
          borderRadius: 20,
          border:
            "1.5px solid rgba(5,150,105,0.20)",
          scrollMarginTop: 104,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems:
              "flex-start",
            justifyContent:
              "space-between",
            gap: 14,
            flexWrap: "wrap",
            marginBottom: 18,
          }}
        >
          <div
            style={{
              minWidth: 0,
              maxWidth: 760,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing:
                  "0.11em",
                textTransform:
                  "uppercase",
                color: "#047857",
              }}
            >
              {isFR
                ? "ESPACE DE CONTRIBUTION"
                : "CONTRIBUTOR WORKSPACE"}
            </div>

            <h3
              className="ns-card-title ns-textbook-academic-title"
              style={{
                margin:
                  "6px 0 6px",
                fontSize: 24,
                lineHeight: 1.2,
              }}
            >
              {clean(
                chapter?.title
              ) ||
                (
                  isFR
                    ? "Chapitre sans titre"
                    : "Untitled Chapter"
                )}
            </h3>

            <p
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Travaillez dans la portée définie par le propriétaire. Le chapitre entier reste disponible comme contexte facultatif en lecture seule."
                : "Work within the Owner-defined focus. The whole chapter remains available as optional read-only context."}
            </p>
          </div>

          {typeof onBackToBookMap ===
          "function" ? (
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={
                requestBackToBookMap
              }
            >
              {isFR
                ? "Retour à Rédiger les chapitres"
                : "Back to Write Chapters"}
            </button>
          ) : null}
        </div>

        {proposalRoundOpen ? (
          <section
            aria-label={
              isFR
                ? "Demande du propriétaire"
                : "Owner request"
            }
            style={{
              marginBottom: 16,
              padding: 16,
              borderRadius: 14,
              border:
                "1.5px solid rgba(37,99,235,0.30)",
              background:
                "#eff6ff",
            }}
          >
            <strong>
              {isFR
                ? "Propositions ouvertes"
                : "Proposals Open"}
            </strong>

            {proposalInstruction ? (
              <div
                style={{
                  marginTop: 10,
                  padding:
                    "11px 13px",
                  borderRadius: 10,
                  background:
                    "#ffffff",
                  border:
                    "1px solid rgba(37,99,235,0.18)",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing:
                      "0.08em",
                    textTransform:
                      "uppercase",
                    color: "#1d4ed8",
                    marginBottom: 5,
                  }}
                >
                  {isFR
                    ? "DEMANDE DU PROPRIÉTAIRE"
                    : "OWNER REQUEST"}
                </div>

                <div
                  style={{
                    lineHeight: 1.6,
                  }}
                >
                  {proposalInstruction}
                </div>
              </div>
            ) : null}
          </section>
        ) : (
          <div
            style={{
              marginBottom: 16,
              padding:
                "12px 14px",
              borderRadius: 12,
              background:
                "#f8fafc",
              border:
                "1px solid rgba(148,163,184,0.24)",
              fontWeight: 700,
            }}
          >
            <div
              style={{
                fontWeight: 900,
                marginBottom: 4,
              }}
            >
              {isFR
                ? "Propositions fermées"
                : "Proposals Closed"}
            </div>

            <div
              className="ns-card-meta"
              style={{
                lineHeight: 1.5,
              }}
            >
              {isFR
                ? "Ce chapitre reste disponible en lecture seule. Vous pourrez soumettre une proposition uniquement lorsque le propriétaire ouvrira un Proposal Round."
                : "This chapter remains available as read-only Canon. You may submit a proposal only when the Owner opens a Proposal Round."}
            </div>
          </div>
        )}

        {proposalRoundOpen
          ? renderProposalFocusCard()
          : null}

        {contributorProposalMode ? (
          <section
            aria-label={
              isFR
                ? "Votre proposition"
                : "Your Proposal"
            }
            style={{
              marginBottom: 22,
              padding: 18,
              borderRadius: 16,
              border:
                "2px solid rgba(5,150,105,0.42)",
              background:
                "#f0fdf4",
              boxShadow:
                "0 10px 24px rgba(5,150,105,0.08)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing:
                  "0.1em",
                textTransform:
                  "uppercase",
                color: "#047857",
              }}
            >
              {isFR
                ? "VOTRE PROPOSITION"
                : "YOUR PROPOSAL"}
            </div>

            <h4
              style={{
                margin:
                  "6px 0 6px",
                fontSize: 20,
              }}
            >
              {isFR
                ? "Écrivez votre proposition"
                : "Write Your Proposal"}
            </h4>

            <p
              className="ns-card-meta"
              style={{
                margin:
                  "0 0 12px",
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Vous pouvez envoyer une proposition pour ce cycle. Elle reste séparée du Canon jusqu’à la décision du propriétaire."
                : "You may submit one proposal for this round. It stays separate from Canon until the Owner decides."}
            </p>

            <button
              className="ns-button ns-button-primary"
              type="button"
              onClick={() => {
                setContributorProposalError("");
                setContributorProposalComposerOpen(
                  true
                );
              }}
            >
              {isFR
                ? "Écrire votre proposition"
                : "Write Your Proposal"}
            </button>
          </section>
        ) : hasSubmittedProposal ? (
          <section
            aria-label={
              isFR
                ? "Proposition envoyée"
                : "Proposal submitted"
            }
            style={{
              marginBottom: 22,
              padding: 16,
              borderRadius: 14,
              border:
                "1.5px solid rgba(5,150,105,0.30)",
              background:
                "#f0fdf4",
            }}
          >
            <strong>
              {isFR
                ? "Proposition envoyée"
                : "Proposal submitted"}
            </strong>
            <p
              className="ns-card-meta"
              style={{
                margin:
                  "6px 0 0",
                lineHeight: 1.5,
              }}
            >
              {isFR
                ? "Vous avez déjà envoyé votre proposition pour ce cycle. Une nouvelle proposition sera possible lorsqu’un nouveau cycle sera ouvert."
                : "You have already submitted your proposal for this round. You can submit again only when the Owner opens a new Proposal Round."}
            </p>
          </section>
        ) : null}

        {contributorProposalComposerOpen &&
        contributorProposalMode ? (
          <div
            data-textbook-floating-editor-backdrop="proposal"
            role="presentation"
            onMouseDown={(event) => {
              if (
                event.target ===
                event.currentTarget &&
                !contributorProposalWorking
              ) {
                setContributorProposalComposerOpen(
                  false
                );
              }
            }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1200,
              display: "grid",
              placeItems: "center",
              padding: 20,
              background:
                "rgba(15,23,42,0.52)",
            }}
          >
            <div
              data-textbook-floating-editor="proposal"
              role="dialog"
              aria-modal="true"
              aria-label={
                isFR
                  ? "Écrire votre proposition"
                  : "Write Your Proposal"
              }
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform:
                  "translate(-50%, -50%)",
                zIndex: 41,
                width:
                  "min(980px, 96vw)",
                maxHeight: "86vh",
                overflowY: "auto",
                padding: 18,
                borderRadius: 18,
                border:
                  "1px solid rgba(5,150,105,0.28)",
                background:
                  "#ffffff",
                boxShadow:
                  "0 28px 80px rgba(15,23,42,0.28)",
                display: "grid",
                gap: 14,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing:
                      "0.09em",
                    textTransform:
                      "uppercase",
                    color: "#047857",
                  }}
                >
                  {isFR
                    ? "VOTRE PROPOSITION"
                    : "YOUR PROPOSAL"}
                </div>

                <h4
                  style={{
                    margin:
                      "4px 0 4px",
                    fontSize: 20,
                  }}
                >
                  {isFR
                    ? "Écrivez votre proposition"
                    : "Write Your Proposal"}
                </h4>

                <p
                  className="ns-card-meta"
                  style={{
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {isFR
                    ? "Écrivez uniquement ce que vous proposez. Vous ne modifiez pas directement le chapitre."
                    : "Write only what you are proposing. You are not directly editing the chapter."}
                </p>
              </div>

              <div
                data-textbook-proposal-rich-editor="true"
              >
                <TextbookRichBlockEditor
                  block={{
                    blockId:
                      "textbook-proposal-draft",
                    type:
                      "paragraph",
                    sectionItemId:
                      "",
                    text:
                      contributorProposalText,
                    manuscriptContent:
                      contributorProposalManuscriptContent,
                  }}
                  disabled={
                    contributorProposalWorking
                  }
                  isFR={isFR}
                  compactToolbar={false}
                  stickyToolbar={true}
                  enableWholeDocumentJustify={true}
                  saveActionLabel={
                    isFR
                      ? "Envoyer la proposition"
                      : "Submit Proposal"
                  }
                  onChange={
                    updateContributorProposalRichBlock
                  }
                />
              </div>

              {contributorProposalError ? (
                <div
                  role="alert"
                  style={{
                    color: "#b91c1c",
                    fontWeight: 700,
                  }}
                >
                  {
                    contributorProposalError
                  }
                </div>
              ) : null}

              <div
                style={{
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "space-between",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <span
                  className="ns-card-meta"
                >
                  {clean(
                    contributorProposalText
                  ).length}
                  /12000
                </span>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={
                      contributorProposalWorking
                    }
                    onClick={() =>
                      setContributorProposalComposerOpen(
                        false
                      )
                    }
                  >
                    {isFR
                      ? "Annuler"
                      : "Cancel"}
                  </button>

                  <button
                    className="ns-button ns-button-primary"
                    type="button"
                    disabled={
                      contributorProposalWorking ||
                      !clean(
                        contributorProposalText
                      ) ||
                      clean(
                        contributorProposalText
                      ).length > 12000
                    }
                    aria-busy={
                      contributorProposalWorking
                    }
                    onClick={() =>
                      setContributorSubmitConfirmOpen(
                        true
                      )
                    }
                  >
                    {contributorProposalWorking
                      ? (
                          isFR
                            ? "Envoi…"
                            : "Submitting…"
                        )
                      : (
                          isFR
                            ? "Envoyer la proposition"
                            : "Submit Proposal"
                        )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {contributorSubmitConfirmOpen &&
        contributorProposalMode ? (
          <div
            data-textbook-contributor-submit-confirm-backdrop="true"
            role="presentation"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1480,
              display: "grid",
              placeItems: "center",
              padding: 20,
              background:
                "rgba(15,23,42,0.60)",
            }}
          >
            <section
              data-textbook-contributor-submit-confirm-dialog="true"
              role="alertdialog"
              aria-modal="true"
              aria-label={
                isFR
                  ? "Confirmer l’envoi de la proposition"
                  : "Confirm proposal submission"
              }
              style={{
                width:
                  "min(620px, 94vw)",
                borderRadius: 18,
                border:
                  "1px solid rgba(5,150,105,0.28)",
                background:
                  "#ffffff",
                boxShadow:
                  "0 28px 80px rgba(15,23,42,0.34)",
                padding: 20,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing:
                    "0.1em",
                  textTransform:
                    "uppercase",
                  color: "#047857",
                }}
              >
                {isFR
                  ? "CONFIRMATION REQUISE"
                  : "CONFIRMATION REQUIRED"}
              </div>

              <h4
                className="ns-textbook-academic-title"
                style={{
                  margin:
                    "6px 0 8px",
                  fontSize: 22,
                }}
              >
                {isFR
                  ? "Envoyer cette proposition ?"
                  : "Submit this proposal?"}
              </h4>

              <p
                className="ns-card-meta"
                style={{
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                {isFR
                  ? "Après l’envoi, cette proposition sera enregistrée pour ce Proposal Round et vous ne pourrez pas en envoyer une autre avant l’ouverture d’un nouveau round. Annulez si vous souhaitez poursuivre la modification."
                  : "After submission, this proposal will be recorded for this Proposal Round and you cannot submit another until the Owner opens a new round. Cancel if you want to continue editing."}
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "flex-end",
                  gap: 8,
                  flexWrap: "wrap",
                  marginTop: 18,
                }}
              >
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    contributorProposalWorking
                  }
                  onClick={() =>
                    setContributorSubmitConfirmOpen(
                      false
                    )
                  }
                >
                  {isFR
                    ? "Annuler"
                    : "Cancel"}
                </button>

                <button
                  className="ns-button ns-button-primary"
                  type="button"
                  disabled={
                    contributorProposalWorking
                  }
                  aria-busy={
                    contributorProposalWorking
                  }
                  onClick={
                    submitContributorProposal
                  }
                >
                  {contributorProposalWorking
                    ? (
                        isFR
                          ? "Envoi…"
                          : "Submitting…"
                      )
                    : (
                        isFR
                          ? "Confirmer l’envoi"
                          : "Confirm Submission"
                      )}
                </button>
              </div>
            </section>
          </div>
        ) : null}

        {proposals.length > 0 ? (
          <section
            data-textbook-contributor-submitted-proposals="true"
            aria-label={
              isFR
                ? "Vos propositions envoyées"
                : "Your submitted proposals"
            }
            style={{
              marginBottom: 22,
              padding:
                "14px 16px",
              borderRadius: 14,
              border:
                "1px solid rgba(148,163,184,0.24)",
              background:
                "#ffffff",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems:
                  "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <strong>
                {isFR
                  ? "Vos propositions envoyées"
                  : "Your submitted proposals"}
              </strong>
              <span
                className="ns-card-meta"
                style={{ fontWeight: 800 }}
              >
                {proposals.length}
              </span>
            </div>

            <div
              style={{
                display: "grid",
                gap: 9,
                marginTop: 10,
              }}
            >
              {proposals
                .slice(0, 5)
                .map(
                  (
                    contribution,
                    index
                  ) => (
                    <div
                      key={
                        contribution?.id ||
                        index
                      }
                      style={{
                        padding:
                          "12px 13px",
                        borderRadius: 11,
                        background:
                          "#f8fafc",
                        border:
                          "1px solid rgba(148,163,184,0.18)",
                        display: "flex",
                        justifyContent:
                          "space-between",
                        alignItems:
                          "center",
                        gap: 12,
                        flexWrap:
                          "wrap",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 850 }}>
                          {isFR
                            ? `Proposition ${index + 1}`
                            : `Proposal ${index + 1}`}
                        </div>
                        <div
                          className="ns-card-meta"
                          style={{ marginTop: 4 }}
                        >
                          {clean(
                            contribution
                              ?.decisionStatus
                          ) || "pending"}
                        </div>
                      </div>

                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        onClick={() =>
                          setContributorProposalReviewId(
                            clean(
                              contribution?.id
                            )
                          )
                        }
                      >
                        {isFR
                          ? "Voir la proposition"
                          : "View Proposal"}
                      </button>
                    </div>
                  )
                )}
            </div>
          </section>
        ) : null}

        {activeContributorProposal ? (
          <div
            data-textbook-contributor-proposal-review-backdrop="true"
            role="presentation"
            onMouseDown={(event) => {
              if (
                event.target ===
                event.currentTarget
              ) {
                setContributorProposalReviewId(
                  ""
                );
              }
            }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1320,
              display: "grid",
              placeItems: "center",
              padding:
                "clamp(10px, 2vw, 24px)",
              background:
                "rgba(15,23,42,0.56)",
            }}
          >
            <section
              data-textbook-contributor-proposal-review-dialog="true"
              role="dialog"
              aria-modal="true"
              aria-label={
                isFR
                  ? "Proposition complète en lecture seule"
                  : "Full read-only proposal"
              }
              style={{
                width:
                  "min(1180px, 96vw)",
                height:
                  "min(900px, 90vh)",
                background:
                  "#ffffff",
                borderRadius: 18,
                border:
                  "1px solid rgba(5,150,105,0.26)",
                boxShadow:
                  "0 28px 70px rgba(15,23,42,0.28)",
                display: "flex",
                flexDirection:
                  "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding:
                    "14px 18px",
                  borderBottom:
                    "1px solid rgba(148,163,184,0.22)",
                  background:
                    "#f7fdf9",
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                  gap: 14,
                  flexWrap:
                    "wrap",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 900,
                      letterSpacing:
                        "0.1em",
                      textTransform:
                        "uppercase",
                      color: "#047857",
                    }}
                  >
                    {isFR
                      ? "PROPOSITION EN LECTURE SEULE"
                      : "READ-ONLY PROPOSAL"}
                  </div>
                  <div
                    className="ns-textbook-academic-title"
                    style={{
                      marginTop: 3,
                      fontSize: 20,
                    }}
                  >
                    {isFR
                      ? "Votre proposition envoyée"
                      : "Your submitted proposal"}
                  </div>
                  <div
                    className="ns-card-meta"
                    style={{
                      marginTop: 3,
                      fontWeight: 750,
                    }}
                  >
                    {isFR
                      ? `Proposition ${contributorProposalReviewIndex + 1} sur ${proposals.length}`
                      : `Proposal ${contributorProposalReviewIndex + 1} of ${proposals.length}`}
                  </div>
                </div>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={() =>
                    setContributorProposalReviewId(
                      ""
                    )
                  }
                >
                  {isFR
                    ? "Fermer la revue"
                    : "Close Review"}
                </button>
              </div>

              <div
                data-textbook-contributor-proposal-review-reader="true"
                style={{
                  flex: "1 1 auto",
                  minHeight: 0,
                  overflowY: "auto",
                  padding: 18,
                  background:
                    "#ffffff",
                }}
              >
                <TextbookProposalSnapshotReadOnly
                  contribution={
                    activeContributorProposal
                  }
                  lang={language}
                />
              </div>
            </section>
          </div>
        ) : null}

        <section
          data-textbook-contributor-chapter-reference-launcher="true"
          aria-label={
            isFR
              ? "Chapitre de référence"
              : "Chapter for reference"
          }
          style={{
            borderTop:
              "1px solid rgba(148,163,184,0.24)",
            paddingTop: 16,
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing:
                  "0.1em",
                textTransform:
                  "uppercase",
                color: "#64748b",
              }}
            >
              {isFR
                ? "RÉFÉRENCE DU CANON"
                : "CANON REFERENCE"}
            </div>
            <div
              className="ns-textbook-academic-title"
              style={{
                marginTop: 4,
                fontSize: 18,
              }}
            >
              {isFR
                ? "Canon du chapitre — référence"
                : "Chapter Canon — Reference"}
            </div>
            <p
              className="ns-card-meta"
              style={{ margin: "4px 0 0" }}
            >
              {isFR
                ? "Ouvrez le lecteur de référence sans perdre votre place dans l’espace de contribution."
                : "Open the reference reader without losing your place in the contribution workspace."}
            </p>
          </div>

          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={
              ownerChapterReferenceParts
                .length < 1
            }
            onClick={
              openContributorChapterReference
            }
          >
            {isFR
              ? "Voir la référence du chapitre"
              : "View Chapter Reference"}
          </button>
        </section>

        {contributorChapterReferenceOpen ? (
          <div
            data-textbook-contributor-chapter-reference-backdrop="true"
            role="presentation"
            onMouseDown={(event) => {
              if (
                event.target ===
                event.currentTarget
              ) {
                setContributorChapterReferenceOpen(
                  false
                );
              }
            }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              display: "grid",
              placeItems: "center",
              padding:
                "clamp(10px, 2vw, 24px)",
              background:
                "rgba(15,23,42,0.56)",
            }}
          >
            <section
              data-textbook-contributor-chapter-reference-dialog="true"
              role="dialog"
              aria-modal="true"
              aria-label={
                isFR
                  ? "Lecteur de référence du chapitre"
                  : "Chapter Reference Reader"
              }
              style={{
                width:
                  "min(1180px, 96vw)",
                height:
                  "min(900px, 90vh)",
                background:
                  "#ffffff",
                borderRadius: 18,
                border:
                  "1px solid rgba(5,150,105,0.26)",
                boxShadow:
                  "0 28px 70px rgba(15,23,42,0.28)",
                display: "flex",
                flexDirection:
                  "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding:
                    "14px 18px",
                  borderBottom:
                    "1px solid rgba(148,163,184,0.22)",
                  background:
                    "#f7fdf9",
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                  gap: 14,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 900,
                      letterSpacing:
                        "0.1em",
                      textTransform:
                        "uppercase",
                      color: "#047857",
                    }}
                  >
                    {isFR
                      ? "RÉFÉRENCE EN LECTURE SEULE"
                      : "READ-ONLY REFERENCE"}
                  </div>
                  <div
                    className="ns-textbook-academic-title"
                    style={{
                      marginTop: 3,
                      fontSize: 20,
                    }}
                  >
                    {clean(
                      chapter?.title
                    ) ||
                      (
                        isFR
                          ? "Chapitre sans titre"
                          : "Untitled Chapter"
                      )}
                  </div>
                </div>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={() =>
                    setContributorChapterReferenceOpen(
                      false
                    )
                  }
                >
                  {isFR
                    ? "Fermer"
                    : "Close Reference"}
                </button>
              </div>

              {ownerChapterReferenceParts
                .length > 0 ? (
                <div
                  data-textbook-contributor-reference-part-selector="true"
                  style={{
                    padding:
                      "12px 18px",
                    borderBottom:
                      "1px solid rgba(148,163,184,0.22)",
                    background:
                      "#ffffff",
                    display: "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems:
                        "center",
                      gap: 10,
                      flex:
                        "1 1 520px",
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 850,
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      {isFR
                        ? "Partie de référence"
                        : "Reference Part"}
                    </span>
                    <select
                      className="ns-input"
                      value={
                        clean(
                          contributorChapterReferencePart
                            ?.id
                        )
                      }
                      onChange={(event) =>
                        setContributorChapterReferencePartId(
                          clean(
                            event.target.value
                          )
                        )
                      }
                      style={{
                        minWidth: 0,
                        maxWidth: 520,
                      }}
                    >
                      {ownerChapterReferenceParts
                        .map(
                          (
                            part,
                            partIndex
                          ) => (
                            <option
                              key={part.id}
                              value={part.id}
                            >
                              {`${partIndex + 1}. ${part.title}`}
                            </option>
                          )
                        )}
                    </select>
                  </label>

                  <div
                    className="ns-card-meta"
                    style={{ fontWeight: 800 }}
                  >
                    {isFR
                      ? "Une seule partie du Canon est affichée à la fois."
                      : "Only one Canon part is shown at a time."}
                  </div>
                </div>
              ) : null}

              <div
                data-textbook-contributor-chapter-reference-reader="true"
                style={{
                  flex: "1 1 auto",
                  minHeight: 0,
                  overflowY: "auto",
                  padding:
                    "16px 18px 22px",
                  background:
                    "#ffffff",
                }}
              >
                <TextbookLiveChapterPageFlow
                  chapterTitle={
                    clean(
                      chapter?.title
                    )
                  }
                  blocks={
                    contributorChapterReferenceBlocks
                  }
                  sections={
                    contributorChapterReferenceSections
                  }
                  sectionLayout={
                    contributorChapterReferenceSectionLayout
                  }
                  formatPlan={formatPlan}
                  isFR={isFR}
                  canEdit={false}
                  editMode={false}
                  proofMode={true}
                  stickyNavigation={true}
                  renderBlock={(block) =>
                    renderReferenceBlock(
                      block,
                      null,
                      true
                    )
                  }
                  renderFlowUnit={(unit) =>
                    renderReferenceFlowUnit(
                      unit,
                      true
                    )
                  }
                />
              </div>
            </section>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <form
      ref={workspaceRef}
      id="textbook-chapter-workspace"
      data-textbook-continuous-chapter="true"
      className="ns-card"
      onSubmit={save}
      style={{
        padding:
          "clamp(18px, 3vw, 28px)",
        borderRadius: 20,
        border:
          "1.5px solid rgba(5,150,105,0.20)",
        scrollMarginTop: 104,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems:
            "flex-start",
          justifyContent:
            "space-between",
          gap: 14,
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div
          style={{
            minWidth: 0,
            maxWidth: 760,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing:
                "0.11em",
              textTransform:
                "uppercase",
              color: "#047857",
            }}
          >
            {isFR
              ? "ÉCRIRE"
              : "WRITE"}
          </div>

          <h3
            className="ns-card-title ns-textbook-academic-title"
            style={{
              margin:
                "6px 0 6px",
              fontSize: 24,
              lineHeight: 1.2,
            }}
          >
            {clean(
              chapter?.title
            ) ||
              (
                isFR
                  ? "Chapitre sans titre"
                  : "Untitled Chapter"
              )}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              lineHeight: 1.55,
            }}
          >
            {clean(
              project?.title
            )
              ? `${clean(
                  project?.title
                )} · `
              : ""}
            {isFR
              ? "L’organisation du chapitre vient de la Structure du livre. Écrivez ici le contenu du chapitre."
              : "The chapter outline comes from Book Structure. Write the chapter content here."}
          </p>
        </div>

        {typeof onBackToBookMap ===
        "function" ? (
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={
              requestBackToBookMap
            }
          >
            {isFR
              ? "← Chapitres"
              : "← Chapters"}
          </button>
        ) : null}
      </div>

      {canManageProposalRound ||
      proposalRoundOpen ||
      proposalRoundBlocksCanon ? (
        <section
          aria-label={
            isFR
              ? "Propositions du chapitre"
              : "Chapter proposals"
          }
          style={{
            marginBottom: 16,
            padding: 14,
            borderRadius: 14,
            border:
              proposalRoundOpen ||
              proposalRoundBlocksCanon
                ? "1.5px solid rgba(37,99,235,0.30)"
                : "1px solid rgba(5,150,105,0.22)",
            background:
              proposalRoundOpen ||
              proposalRoundBlocksCanon
                ? "#eff6ff"
                : "#f0fdf4",
            display: "grid",
            gap: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div>
              <strong>
                {proposalRoundOpen
                  ? (
                      isFR
                        ? "Propositions ouvertes"
                        : "Proposals Open"
                    )
                  : proposalRoundBlocksCanon
                  ? (
                      isFR
                        ? "Préparation des propositions"
                        : "Preparing Proposals"
                    )
                  : (
                      isFR
                        ? "Inviter des propositions"
                        : "Invite Proposals"
                    )}
              </strong>

              <div
                className="ns-card-meta"
                style={{
                  marginTop: 4,
                  lineHeight: 1.5,
                }}
              >
                {proposalRoundOpen
                  ? (
                      isFR
                        ? "Le contenu canonique de ce chapitre reste en lecture seule pendant ce Proposal Round."
                        : "This chapter’s Canon remains read-only while this Proposal Round is open."
                    )
                  : proposalRoundBlocksCanon
                  ? (
                      isFR
                        ? "Le chapitre est temporairement en lecture seule pendant la préparation du Proposal Round."
                        : "The chapter is temporarily read-only while the Proposal Round is being prepared."
                    )
                  : (
                      isFR
                        ? "Ouvrez les propositions uniquement lorsque ce chapitre enregistré est prêt à être examiné."
                        : "Open proposals only when this saved chapter is ready for review."
                    )}
              </div>
            </div>

            {canManageProposalRound &&
            !proposalRoundOpen &&
            !proposalRoundBlocksCanon &&
            !showProposalOpenForm ? (
              <button
                className="ns-button ns-button-primary"
                type="button"
                disabled={
                  proposalWorking ||
                  !canStartProposalRound
                }
                onClick={() => {
                  setProposalError("");
                  setProposalNotice("");
                  setShowProposalOpenForm(
                    true
                  );
                }}
              >
                {isFR
                  ? "Ouvrir les propositions"
                  : "Open Proposals"}
              </button>
            ) : null}

            {canManageProposalRound &&
            proposalRoundOpen &&
            !showProposalCloseForm ? (
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={proposalWorking}
                onClick={() => {
                  setProposalError("");
                  setProposalNotice("");
                  setShowProposalCloseForm(
                    true
                  );
                }}
              >
                {isFR
                  ? "Clôturer les propositions"
                  : "Close Proposals"}
              </button>
            ) : null}
          </div>

          {proposalRoundOpen &&
          proposalInstruction ? (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background:
                  "rgba(255,255,255,0.82)",
                border:
                  "1px solid rgba(37,99,235,0.18)",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing:
                    "0.08em",
                  textTransform:
                    "uppercase",
                  color: "#1d4ed8",
                  marginBottom: 4,
                }}
              >
                {isFR
                  ? "DEMANDE DU PROPRIÉTAIRE"
                  : "OWNER REQUEST"}
              </div>

              <div
                style={{
                  lineHeight: 1.55,
                }}
              >
                {proposalInstruction}
              </div>
            </div>
          ) : null}

          {proposalRoundOpen
            ? renderProposalFocusCard()
            : null}

          {canManageProposalRound &&
          proposalRoundOpen ? (
            <section
              aria-label={
                isFR
                  ? "Propositions reçues"
                  : "Submitted proposals"
              }
              style={{
                padding: 12,
                borderRadius: 12,
                background:
                  "#ffffff",
                border:
                  "1px solid rgba(37,99,235,0.18)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <strong>
                  {isFR
                    ? "Propositions reçues"
                    : "Submitted proposals"}
                </strong>

                <span
                  className="ns-card-meta"
                  style={{
                    fontWeight: 750,
                  }}
                >
                  {
                    pendingOwnerReviewProposals
                      .length
                  }{" "}
                  {isFR
                    ? "en attente"
                    : "pending"}
                </span>
              </div>

              {ownerReviewProposals
                .length === 0 ? (
                <p
                  className="ns-card-meta"
                  style={{
                    margin:
                      "10px 0 0",
                  }}
                >
                  {isFR
                    ? "Aucune proposition n’a encore été envoyée."
                    : "No proposals have been submitted yet."}
                </p>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gap: 10,
                    marginTop: 10,
                  }}
                >
                  {ownerReviewProposals
                    .map(
                      (
                        contribution,
                        index
                      ) => (
                        <article
                          key={
                            contribution
                              ?.id ||
                            index
                          }
                          style={{
                            padding:
                              "12px 13px",
                            borderRadius:
                              11,
                            background:
                              "#f8fafc",
                            border:
                              "1px solid rgba(148,163,184,0.20)",
                          }}
                        >
                          <div
                            style={{
                              fontWeight:
                                850,
                            }}
                          >
                            {clean(
                              contribution
                                ?.contributor
                                ?.displayName
                            ) || "Writer"}
                          </div>

                          <div
                            style={{
                              marginTop: 6,
                              whiteSpace:
                                "pre-wrap",
                              lineHeight:
                                1.55,
                            }}
                          >
                            <TextbookProposalSnapshotReadOnly
                              contribution={
                                contribution
                              }
                              lang={language}
                            />
                          </div>

                          <div
                            className="ns-card-meta"
                            style={{
                              marginTop: 7,
                            }}
                          >
                            {isFR
                              ? "Statut"
                              : "Status"}
                            :{" "}
                            {clean(
                              contribution
                                ?.decisionStatus
                            ) ||
                              "pending"}
                          </div>
                        </article>
                      )
                    )}
                </div>
              )}
            </section>
          ) : null}

          {canManageProposalRound &&
          !proposalRoundOpen &&
          !proposalRoundBlocksCanon &&
          !canStartProposalRound ? (
            <div
              className="ns-card-meta"
              style={{
                fontWeight: 700,
              }}
            >
              {revision < 1
                ? (
                    isFR
                      ? "Enregistrez ce chapitre avant d’ouvrir les propositions."
                      : "Save this chapter before opening proposals."
                  )
                : dirty
                ? (
                    isFR
                      ? "Enregistrez d’abord les modifications du chapitre."
                      : "Save your chapter changes before opening proposals."
                  )
                : (
                    isFR
                      ? "Les propositions ne peuvent pas être ouvertes pour le moment."
                      : "Proposals cannot be opened right now."
                  )}
            </div>
          ) : null}

          {showProposalOpenForm &&
          canManageProposalRound &&
          !proposalRoundOpen ? (
            <div
              style={{
                display: "grid",
                gap: 8,
              }}
            >
              <fieldset
                data-textbook-proposal-scope-picker="true"
                style={{
                  margin: 0,
                  padding: 14,
                  borderRadius: 12,
                  border:
                    "1px solid rgba(180,83,9,0.22)",
                  background:
                    "#fffbeb",
                  display: "grid",
                  gap: 10,
                }}
              >
                <legend
                  style={{
                    padding:
                      "0 6px",
                    fontWeight: 900,
                  }}
                >
                  {isFR
                    ? "Portée académique de la contribution"
                    : "Academic contribution scope"}
                </legend>

                <div
                  className="ns-card-meta"
                  style={{
                    lineHeight: 1.5,
                  }}
                >
                  {isFR
                    ? "Le contributeur pourra lire tout le chapitre comme contexte, mais sa proposition devra rester dans la portée que vous choisissez ici."
                    : "The contributor may read the whole chapter for context, but the proposal should stay within the scope you choose here."}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 9,
                  }}
                >
                  {[
                    {
                      value:
                        "whole_chapter",
                      label:
                        isFR
                          ? "Chapitre entier"
                          : "Whole Chapter",
                      note:
                        isFR
                          ? "Demandez une révision du chapitre dans son ensemble."
                          : "Ask for review of the chapter as a whole.",
                      disabled:
                        false,
                    },
                    {
                      value:
                        "section",
                      label:
                        isFR
                          ? "Section"
                          : "Section",
                      note:
                        isFR
                          ? "Concentrez la proposition sur une section existante."
                          : "Focus the proposal on one existing Section.",
                      disabled:
                        sections.length ===
                        0,
                    },
                    {
                      value:
                        "selected_passage",
                      label:
                        isFR
                          ? "Passage sélectionné"
                          : "Selected Passage",
                      note:
                        isFR
                          ? "Indiquez le passage exact que vous souhaitez faire examiner."
                          : "Provide the exact passage you want reviewed.",
                      disabled:
                        false,
                    },
                  ].map(
                    (option) => (
                      <label
                        key={
                          option.value
                        }
                        style={{
                          display: "grid",
                          gap: 5,
                          padding:
                            "11px 12px",
                          borderRadius: 10,
                          border:
                            proposalScopeMode ===
                            option.value
                              ? "2px solid rgba(180,83,9,0.55)"
                              : "1px solid rgba(180,83,9,0.18)",
                          background:
                            proposalScopeMode ===
                            option.value
                              ? "#ffffff"
                              : "rgba(255,255,255,0.68)",
                          opacity:
                            option.disabled
                              ? 0.5
                              : 1,
                          cursor:
                            option.disabled
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems:
                              "center",
                            gap: 8,
                            fontWeight: 850,
                          }}
                        >
                          <input
                            type="radio"
                            name="textbook-proposal-scope"
                            value={
                              option.value
                            }
                            checked={
                              proposalScopeMode ===
                              option.value
                            }
                            disabled={
                              proposalWorking ||
                              option.disabled
                            }
                            onChange={() => {
                              setProposalScopeMode(
                                option.value
                              );
                              setProposalError(
                                ""
                              );
                            }}
                          />
                          {
                            option.label
                          }
                        </span>

                        <span
                          className="ns-card-meta"
                          style={{
                            lineHeight: 1.4,
                          }}
                        >
                          {option.note}
                        </span>
                      </label>
                    )
                  )}
                </div>

                {proposalScopeMode ===
                  "section" ? (
                  <label
                    style={{
                      display: "grid",
                      gap: 6,
                      fontWeight: 800,
                    }}
                  >
                    <span>
                      {isFR
                        ? "Section à examiner"
                        : "Section to review"}
                    </span>

                    <select
                      className="ns-input"
                      value={
                        proposalScopeSectionItemId
                      }
                      disabled={
                        proposalWorking
                      }
                      onChange={(event) => {
                        setProposalScopeSectionItemId(
                          event.target.value
                        );
                        setProposalError(
                          ""
                        );
                      }}
                    >
                      <option value="">
                        {isFR
                          ? "Choisissez une section"
                          : "Choose a Section"}
                      </option>

                      {sections.map(
                        (section) => (
                          <option
                            key={
                              section.itemId
                            }
                            value={
                              section.itemId
                            }
                          >
                            {
                              section.title
                            }
                          </option>
                        )
                      )}
                    </select>
                  </label>
                ) : null}

                {proposalScopeMode ===
                  "selected_passage" ? (
                  <label
                    style={{
                      display: "grid",
                      gap: 6,
                      fontWeight: 800,
                    }}
                  >
                    <span>
                      {isFR
                        ? "Passage exact à examiner"
                        : "Exact passage to review"}
                    </span>

                    <textarea
                      className="ns-input"
                      rows={5}
                      value={
                        proposalScopeSelectedPassage
                      }
                      disabled={
                        proposalWorking
                      }
                      onChange={(event) => {
                        setProposalScopeSelectedPassage(
                          event.target.value
                        );
                        setProposalError(
                          ""
                        );
                      }}
                      placeholder={
                        isFR
                          ? "Collez ici le passage exact du chapitre que vous souhaitez faire examiner."
                          : "Paste the exact passage from the chapter that you want contributors to review."
                      }
                    />
                  </label>
                ) : null}
              </fieldset>

              <label
                style={{
                  display: "grid",
                  gap: 6,
                  fontWeight: 800,
                }}
              >
                <span>
                  {isFR
                    ? "Que souhaitez-vous que les autres Writers examinent ou améliorent ?"
                    : "What would you like other writers to review or improve?"}
                </span>

                <textarea
                  className="ns-input"
                  rows={3}
                  value={
                    proposalOpeningReason
                  }
                  disabled={
                    proposalWorking
                  }
                  onChange={(event) => {
                    setProposalOpeningReason(
                      event.target.value
                    );
                    setProposalError("");
                  }}
                  placeholder={
                    isFR
                      ? "Ex. : Vérifiez les exemples et proposez des améliorations."
                      : "e.g. Review the examples and suggest improvements."
                  }
                />
              </label>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={proposalWorking}
                  onClick={() => {
                    setShowProposalOpenForm(
                      false
                    );
                    setProposalOpeningReason(
                      ""
                    );
                    setProposalScopeMode(
                      "whole_chapter"
                    );
                    setProposalScopeSectionItemId(
                      ""
                    );
                    setProposalScopeSelectedPassage(
                      ""
                    );
                    setProposalError("");
                  }}
                >
                  {isFR
                    ? "Annuler"
                    : "Cancel"}
                </button>

                <button
                  className="ns-button ns-button-primary"
                  type="button"
                  disabled={
                    proposalWorking ||
                    !proposalScopeReady ||
                    !clean(
                      proposalOpeningReason
                    )
                  }
                  aria-busy={
                    proposalWorking
                  }
                  onClick={
                    openProposalRound
                  }
                >
                  {proposalWorking
                    ? (
                        isFR
                          ? "Ouverture…"
                          : "Opening…"
                      )
                    : (
                        isFR
                          ? "Ouvrir les propositions"
                          : "Open Proposals"
                      )}
                </button>
              </div>
            </div>
          ) : null}

          {showProposalCloseForm &&
          canManageProposalRound &&
          proposalRoundOpen ? (
            <div
              style={{
                display: "grid",
                gap: 8,
              }}
            >
              <label
                style={{
                  display: "grid",
                  gap: 6,
                  fontWeight: 800,
                }}
              >
                <span>
                  {isFR
                    ? "Pourquoi clôturez-vous les propositions ?"
                    : "Why are you closing proposals?"}
                </span>

                <textarea
                  className="ns-input"
                  rows={2}
                  value={
                    proposalClosingReason
                  }
                  disabled={
                    proposalWorking
                  }
                  onChange={(event) => {
                    setProposalClosingReason(
                      event.target.value
                    );
                    setProposalError("");
                  }}
                  placeholder={
                    isFR
                      ? "Une courte note suffit."
                      : "A short note is enough."
                  }
                />
              </label>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={proposalWorking}
                  onClick={() => {
                    setShowProposalCloseForm(
                      false
                    );
                    setProposalClosingReason(
                      ""
                    );
                    setProposalError("");
                  }}
                >
                  {isFR
                    ? "Annuler"
                    : "Cancel"}
                </button>

                <button
                  className="ns-button ns-button-primary"
                  type="button"
                  disabled={
                    proposalWorking ||
                    !clean(
                      proposalClosingReason
                    )
                  }
                  aria-busy={
                    proposalWorking
                  }
                  onClick={
                    closeProposalRound
                  }
                >
                  {proposalWorking
                    ? (
                        isFR
                          ? "Clôture…"
                          : "Closing…"
                      )
                    : (
                        isFR
                          ? "Clôturer les propositions"
                          : "Close Proposals"
                      )}
                </button>
              </div>
            </div>
          ) : null}

          {proposalError ? (
            <div
              role="alert"
              style={{
                color: "#b91c1c",
                fontWeight: 700,
              }}
            >
              {proposalError}
            </div>
          ) : null}

          {proposalNotice ? (
            <div
              role="status"
              style={{
                color: "#166534",
                fontWeight: 750,
              }}
            >
              {proposalNotice}
            </div>
          ) : null}
        </section>
      ) : null}

      {!canEdit ? (
        <div
          role="status"
          style={{
            padding: 12,
            marginBottom: 16,
            borderRadius: 12,
            background:
              "#f8fafc",
            border:
              "1px solid rgba(100,116,139,0.20)",
            fontWeight: 700,
          }}
        >
          {proposalRoundBlocksCanon
            ? (
                isFR
                  ? "Ce chapitre est en lecture seule pendant l’ouverture des propositions."
                  : "This chapter is read-only while proposals are open."
              )
            : (
                isFR
                  ? "Vous pouvez lire ce chapitre. Seul le propriétaire du projet peut le modifier dans cette version."
                  : "You can read this chapter. Only the project owner can edit it in this version."
              )}
        </div>
      ) : null}

      {figureLoadError ? (
        <div
          role="alert"
          style={{
            padding: 12,
            marginBottom: 16,
            borderRadius: 12,
            background:
              "#fff7ed",
            border:
              "1px solid rgba(194,65,12,0.22)",
            color: "#9a3412",
            fontWeight: 700,
          }}
        >
          {isFR
            ? "Les Figures n’ont pas pu être chargées. Le texte du chapitre reste disponible. Réessayez avant d’ajouter une Figure."
            : "Figures could not be loaded. Chapter text remains available. Retry before adding a Figure."}
          {clean(
            figureLoadError
          )
            ? ` ${clean(
                figureLoadError
              )}`
            : ""}
        </div>
      ) : null}

      {figureMessage ? (
        <div
          role="status"
          style={{
            padding: 12,
            marginBottom: 16,
            borderRadius: 12,
            background:
              "#f0fdf4",
            border:
              "1px solid rgba(5,150,105,0.20)",
            color: "#166534",
            fontWeight: 750,
          }}
        >
          {figureMessage}
        </div>
      ) : null}

      <TextbookVersionHistory
        open={versionHistoryOpen}
        projectId={clean(project?.id)}
        isFR={isFR}
        onLoad={onLoadVersionHistory}
        onPreview={onPreviewVersion}
        onClose={() =>
          setVersionHistoryOpen(false)
        }
      />

      <div
        style={{
          position: "sticky",
          top: 88,
          zIndex: 8,
          margin: "0 0 16px",
          padding: "10px 12px",
          borderRadius: 14,
          border:
            "1px solid rgba(5,150,105,0.18)",
          background:
            "rgba(255,255,255,0.96)",
          boxShadow:
            "0 8px 22px rgba(15,23,42,0.08)",
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          backdropFilter:
            "blur(8px)",
        }}
      >
        <span
          className="ns-card-meta"
          style={{
            fontWeight: 750,
          }}
        >
          {editMode
            ? (
                dirty
                  ? (
                      isFR
                        ? "Modification · changements non enregistrés."
                        : "Editing · unsaved changes."
                    )
                  : canonNeedsConsolidation
                    ? (
                        isFR
                          ? "Le Canon enregistré doit être consolidé."
                          : "Saved Canon needs consolidation."
                      )
                    : (
                        isFR
                          ? "Chapitre enregistré · modification."
                          : "Chapter saved · Editing."
                      )
              )
            : (
                isFR
                  ? "Chapitre enregistré."
                  : "Chapter saved."
              )}
        </span>

        {canEdit ? (
          <div
            data-textbook-safe-edit-actions="true"
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {editMode && dirty ? (
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={saving}
                onClick={
                  requestCancelEditing
                }
                title={
                  isFR
                    ? "Revenir à la dernière version enregistrée"
                    : "Return to the last saved chapter"
                }
              >
                {isFR
                  ? "Annuler les modifications"
                  : "Cancel Unsaved Changes"}
              </button>
            ) : null}

            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() =>
                setVersionHistoryOpen(
                  (current) => !current
                )
              }
            >
              {isFR
                ? "Historique des versions"
                : "Version History"}
            </button>

            <button
              className="ns-button ns-button-primary"
              type="submit"
              disabled={
                saving ||
                !editMode ||
                (
                  !dirty &&
                  !canonNeedsConsolidation
                ) ||
                !Number.isInteger(
                  revision
                ) ||
                revision < 0
              }
              aria-busy={saving}
              title={
                !editMode
                  ? (
                      isFR
                        ? "Commencez à modifier le chapitre avant de l’enregistrer."
                        : "Start editing the chapter before saving."
                    )
                  : ""
              }
            >
              {saving
                ? (
                    isFR
                      ? "Enregistrement…"
                      : "Saving…"
                  )
                : (
                    isFR
                      ? "Enregistrer le chapitre"
                      : "Save Chapter"
                  )}
            </button>
          </div>
        ) : null}
      </div>

      <section
        aria-label={
          isFR
            ? "Plan du chapitre"
            : "Chapter outline"
        }
        style={{
          marginBottom: 14,
          border:
            "1px solid rgba(148,163,184,0.25)",
          borderRadius: 14,
          padding: "10px 12px",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            fontWeight: 850,
            color: "#0f5132",
          }}
        >
          {isFR
            ? "Plan du chapitre"
            : "Chapter outline"}
        </div>

        <p
          className="ns-card-meta"
          style={{
            margin: "6px 0 0",
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Choisissez une section pour cibler votre rédaction. La navigation entre les pages se fait plus bas dans Pages du chapitre."
            : "Choose a section to focus your writing. Page navigation is handled below in Live Chapter Pages."}
        </p>

        <div
          data-textbook-chapter-outline-navigator="true"
          style={{
            marginTop: 10,
            display: "grid",
            gridTemplateColumns:
              "auto minmax(0, 1fr) auto",
            alignItems: "end",
            gap: 8,
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={activeSectionIndex < 0}
            onClick={() =>
              selectOutlineGroup(
                activeSectionIndex <= 0
                  ? ""
                  : sectionGroups[
                      activeSectionIndex - 1
                    ]?.itemId
              )
            }
          >
            {isFR ? "Section précédente" : "Previous Section"}
          </button>

          <label
            style={{
              minWidth: 0,
              display: "grid",
              gap: 5,
              fontSize: 12,
              fontWeight: 800,
              color: "#475569",
            }}
          >
            <span>{isFR ? "Aller à" : "Jump to"}</span>
            <select
              data-textbook-chapter-outline-jump="true"
              className="ns-input"
              aria-label={
                isFR
                  ? "Aller directement à une section du chapitre"
                  : "Jump directly to a Chapter Section"
              }
              value={clean(activeGroup?.itemId)}
              onChange={(event) =>
                selectOutlineGroup(event.target.value)
              }
              style={{
                width: "100%",
                minHeight: 38,
                fontWeight: 800,
              }}
            >
              <option value="">
                {isFR ? "Texte du chapitre" : "Chapter text"}
              </option>
              {sectionGroups.map((group, index) => (
                <option key={group.itemId} value={group.itemId}>
                  {`${index + 1} · ${group.title}`}
                </option>
              ))}
            </select>
          </label>

          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={
              sectionGroups.length === 0 ||
              activeSectionIndex >= sectionGroups.length - 1
            }
            onClick={() =>
              selectOutlineGroup(
                sectionGroups[
                  activeSectionIndex + 1
                ]?.itemId
              )
            }
          >
            {isFR ? "Section suivante" : "Next Section"}
          </button>
        </div>

        <div
          data-textbook-chapter-outline-status="true"
          className="ns-card-meta"
          style={{
            marginTop: 9,
            lineHeight: 1.45,
            fontWeight: 750,
            color: "#475569",
          }}
        >
          {activeSectionIndex >= 0
            ? (
                <>
                  {activeGroup.title}
                  {" · "}
                  {isFR
                    ? `Section ${activeSectionIndex + 1} sur ${sectionGroups.length}`
                    : `Section ${activeSectionIndex + 1} of ${sectionGroups.length}`}
                  {activeSectionStartsOnNewPage ? (
                    <>
                      {" · "}
                      {isFR
                        ? "Commence sur une nouvelle page"
                        : "Starts on a new page"}
                    </>
                  ) : null}
                </>
              )
            : (
                isFR
                  ? `Texte du chapitre · ${sectionGroups.length} ${sectionGroups.length === 1 ? "section" : "sections"}`
                  : `Chapter text · ${sectionGroups.length} ${sectionGroups.length === 1 ? "Section" : "Sections"}`
              )}
        </div>

        {editMode &&
        activeGroup ? (
          <div
            data-textbook-active-section-focus="true"
            role="status"
            aria-live="polite"
            data-textbook-active-section-empty={
              activeGroupIsEmpty
                ? "true"
                : "false"
            }
            style={{
              marginTop: 12,
              padding: "12px 14px",
              borderRadius: 12,
              border:
                "2px solid rgba(202,138,4,0.55)",
              background:
                activeGroupIsEmpty
                  ? "#fff1bf"
                  : "#fff7d6",
              boxShadow:
                "0 8px 22px rgba(120,84,0,0.10)",
              display: "flex",
              alignItems: "center",
              justifyContent:
                "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              <strong
                style={{
                  color: "#0f5132",
                }}
              >
                {activeGroup.itemId
                  ? (
                      isFR
                        ? `Cible d’écriture : ${activeGroup.title}`
                        : `Writing target: ${activeGroup.title}`
                    )
                  : (
                      isFR
                        ? "Cible d’écriture : texte du chapitre"
                        : "Writing target: Chapter text"
                    )}
              </strong>

              <div
                className="ns-card-meta"
                style={{
                  marginTop: 4,
                  lineHeight: 1.45,
                }}
              >
                {activeGroupIsEmpty
                  ? (
                      isFR
                        ? `Vous ajoutez le premier contenu à ${activeGroup.title}. Les sections précédentes et suivantes restent visibles, car il s’agit d’un chapitre continu.`
                        : `You are adding the first content to ${activeGroup.title}. Earlier and later sections remain visible because this is one continuous Chapter.`
                    )
                  : (
                      isFR
                        ? `Vous ajoutez du contenu à ${activeGroup.title}. Les sections précédentes et suivantes restent visibles, car il s’agit d’un chapitre continu. Enregistrer le chapitre enregistre tout le chapitre.`
                        : `You are adding to ${activeGroup.title}. Earlier and later sections remain visible because this is one continuous Chapter. Save Chapter saves the whole Chapter together.`
                    )}
              </div>
            </div>

            {canEdit &&
            !proposalRoundBlocksCanon ? (
              <button
                className="ns-button ns-button-outline"
                type="button"
                data-textbook-add-content-to-active-section="true"
                disabled={
                  saving ||
                  !activeGroupInsertionPoint
                }
                onClick={() =>
                  openContentComposer(
                    activeGroupInsertionPoint
                  )
                }
              >
                {isFR
                  ? `+ Ajouter du contenu à ${activeGroup.title}`
                  : `+ Add Content to ${activeGroup.title}`}
              </button>
            ) : null}
          </div>
        ) : null}

        {editMode &&
        clean(
          activeGroup
            ?.itemId
        ) &&
        canEdit &&
        !proposalRoundBlocksCanon ? (
          <label
            data-textbook-section-start-new-page="true"
            style={{
              marginTop: 10,
              padding:
                "10px 12px",
              borderRadius: 10,
              border:
                "1px solid rgba(5,150,105,0.20)",
              background:
                "#ffffff",
              display: "flex",
              alignItems:
                "flex-start",
              gap: 10,
              cursor:
                saving
                  ? "default"
                  : "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={
                activeSectionStartsOnNewPage
              }
              disabled={saving}
              onChange={(event) =>
                updateActiveSectionStartOnNewPage(
                  event
                    .target
                    .checked
                )
              }
              style={{
                marginTop: 3,
              }}
            />

            <span>
              <strong>
                {isFR
                  ? "Commencer cette section sur une nouvelle page"
                  : "Start this section on a new page"}
              </strong>

              <span
                className="ns-card-meta"
                style={{
                  display: "block",
                  marginTop: 3,
                  lineHeight: 1.45,
                }}
              >
                {isFR
                  ? "Le titre de la section et son contenu commenceront ensemble sur la page de travail suivante. Enregistrer le chapitre conserve ce choix de mise en page."
                  : "The Section heading and its content will begin together on the next working page. Save Chapter stores this layout choice."}
              </span>
            </span>
          </label>
        ) : null}

        {outlineNotice ? (
          <div
            role="status"
            style={{
              marginTop: 9,
              color: "#92400e",
              fontWeight: 750,
            }}
          >
            {outlineNotice}
          </div>
        ) : null}
      </section>

      {editMode &&
      !proposalRoundBlocksCanon ? (
        <section
          data-textbook-chapter-rich-editor="true"
          aria-label={
            isFR
              ? "Éditeur du chapitre"
              : "Chapter editor"
          }
          style={{
            maxWidth: 960,
            margin:
              "0 auto 18px",
            padding:
              "14px 18px",
            borderRadius: 16,
            border:
              "1px solid rgba(5,150,105,0.22)",
            background:
              "#ffffff",
          }}
        >
          <div
            className="ns-card-meta"
            style={{
              marginBottom: 10,
              fontWeight: 750,
              color: "#475569",
            }}
          >
            {isFR
              ? "Modifiez le chapitre ci-dessous. Les limites des pages de travail sont approximatives; la pagination finale est fixée pendant la préparation de publication."
              : "Edit the chapter below. Working-page boundaries are approximate; final pagination is set during publication preparation."}
          </div>

          <TextbookChapterRichEditor
            blocks={current}
            disabled={
              saving ||
              !canEdit
            }
            isFR={isFR}
            workingPageGuideCount={
              workingPageMetrics.pageCount
            }
            workingPageBreakBoundaryIds={
              workingPageBreakBoundaryIds
            }
            renderBoundary={
              renderChapterEditorBoundary
            }
            onChange={
              updateChapterRichManuscript
            }
          />
        </section>
      ) : null}

      <TextbookLiveChapterPageFlow
        chapterTitle={
          clean(
            chapter?.title
          )
        }
        blocks={current}
        sections={sections}
        sectionLayout={
          sectionLayout
        }
        formatPlan={formatPlan}
        isFR={isFR}
        canEdit={canEdit}
        editMode={
          editMode &&
          !proposalRoundBlocksCanon
        }
        focusSectionItemId={
          liveSectionNavigation
            .sectionItemId
        }
        focusSectionRequestKey={
          liveSectionNavigation
            .requestKey
        }
        onStartEditing={() => {
          setEditMode(true);
          setActiveBlockId("");
          setInsertionPoint(null);
          setInsertionMessage("");
          setOutlineNotice("");
        }}
        insertionPoint={
          insertionPoint
        }
        onSelectInsertionPoint={
          selectLiveInsertionPoint
        }
        onAddFigure={() =>
          openFigureComposer(
            insertionPoint
              ?.sectionItemId,
            insertionPoint
          )
        }
        onAddContent={() =>
          openContentComposer(
            insertionPoint
          )
        }
        addFigureDisabled={
          figureBusy ||
          Boolean(
            clean(
              figureLoadError
            )
          )
        }
        onWorkingPageMetricsChange={
          handleWorkingPageMetricsChange
        }
        onActivePageChange={
          setActiveWorkingPageIndex
        }
        renderBlock={(
          block
        ) =>
          renderReferenceBlock(
            block,
            null,
            true
          )
        }
        renderFlowUnit={(
          unit
        ) =>
          renderReferenceFlowUnit(
            unit,
            true
          )
        }
      />

      {editMode &&
      figureComposer ? (
        renderFigureComposer(
          figureComposer
            .sectionItemId
        )
      ) : null}

      {editMode
        ? renderContentComposer()
        : null}

      {insertionMessage ? (
        <div
          role="status"
          data-textbook-insertion-message="true"
          style={{
            maxWidth: 700,
            margin:
              "0 auto 12px",
            padding:
              "10px 12px",
            borderRadius: 10,
            background:
              "#f0fdf4",
            border:
              "1px solid rgba(5,150,105,0.20)",
            color: "#166534",
            fontWeight: 750,
          }}
        >
          {insertionMessage}
        </div>
      ) : null}

      {false && editMode ? (
        <details
          data-textbook-detailed-editor="true"
          open
        style={{
          marginTop: 16,
          border:
            "1px solid rgba(148,163,184,0.24)",
          borderRadius: 14,
          background:
            "#ffffff",
        }}
      >
        <summary
          style={{
            cursor: "pointer",
            padding:
              "12px 14px",
            fontWeight: 850,
            color: "#334155",
          }}
        >
          {isFR
            ? "Modifier le texte du chapitre"
            : "Edit Chapter Text"}
        </summary>

      <div
        data-textbook-continuous-groups="true"
        style={{
          display: "grid",
          gap: 0,
        }}
      >
        {groups.map((group) => {
          const activeGroup =
            group;

          const activeGroupBlocks =
            current.filter(
              (block) =>
                clean(
                  block.sectionItemId
                ) ===
                clean(
                  activeGroup.itemId
                )
            );

          const defaultGroupInsertionPoint =
            groupEndInsertionPoint(
              activeGroupBlocks,
              activeGroup
            );

          const editFlowUnits =
            editMode
              ? buildTextbookFigureTextFlowUnits(
                  activeGroupBlocks
                )
              : [];

          const editRichFigureFlowByParagraphId =
            new Map(
              editFlowUnits
                .filter(
                  (unit) =>
                    [
                      "rich_manuscript_figure_flow",
                      "cursor_figure_flow",
                    ].includes(
                      unit.kind
                    )
                )
                .map(
                  (unit) => [
                    clean(
                      unit.paragraph
                        ?.blockId
                    ),
                    unit,
                  ]
                )
            );

          const editConsumedRichFigureBlockIds =
            new Set(
              editFlowUnits
                .filter(
                  (unit) =>
                    [
                      "rich_manuscript_figure_flow",
                      "cursor_figure_flow",
                    ].includes(
                      unit.kind
                    )
                )
                .map(
                  (unit) =>
                    clean(
                      unit.figure
                        ?.blockId
                    )
                )
                .filter(Boolean)
            );

          const activeEditorInGroup =
            editMode &&
            activeGroupBlocks.some(
              (block) =>
                clean(
                  block?.blockId
                ) ===
                clean(
                  activeBlockId
                )
            );

          const isOutlineFocus =
            clean(
              activeGroup.itemId
            ) ===
            clean(
              activeGroupId
            );

          return (
        <section
          key={
            activeGroup.itemId ||
            "__chapter__"
          }
          id={chapterAnchorId(
            "group",
            activeGroup.itemId ||
              "__chapter__"
          )}
          data-textbook-outline-group={
            activeGroup.itemId ||
            "__chapter__"
          }
          data-textbook-active-outline={
            isOutlineFocus
              ? "true"
              : undefined
          }
          style={{
            padding: activeGroup.itemId
              ? "26px 0 22px"
              : "6px 0 22px",
            borderTop:
              activeGroup.itemId
                ? "1px solid rgba(148,163,184,0.24)"
                : "none",
            scrollMarginTop: 180,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems:
                "flex-start",
              justifyContent:
                "space-between",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing:
                    "0.08em",
                  textTransform:
                    "uppercase",
                  color:
                    activeGroup.itemId
                      ? "#64748b"
                      : "#047857",
                }}
              >
                {activeGroup.itemId
                  ? (
                      isFR
                        ? "SECTION"
                        : "SECTION"
                    )
                  : (
                      isFR
                        ? "CHAPITRE"
                        : "CHAPTER"
                    )}
              </div>

              <h4
                className="ns-card-title ns-textbook-academic-title"
                style={{
                  margin:
                    "3px 0 0",
                  fontSize: 19,
                }}
              >
                {activeGroup.title}
              </h4>
            </div>

            {canEdit &&
            editMode ? (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    saving ||
                    figureBusy ||
                    Boolean(
                      clean(
                        figureLoadError
                      )
                    )
                  }
                  onClick={() =>
                    openFigureComposer(
                      activeGroup.itemId,
                      defaultGroupInsertionPoint
                    )
                  }
                >
                  {isFR
                    ? "+ Ajouter une Figure"
                    : "+ Add Figure"}
                </button>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    saving ||
                    figureBusy
                  }
                  onClick={() =>
                    openContentComposer(
                      defaultGroupInsertionPoint
                    )
                  }
                >
                  {isFR
                    ? "+ Ajouter du contenu"
                    : "+ Add Content"}
                </button>

                {dirty ? (
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={saving}
                    onClick={requestCancelEditing}
                  >
                    {isFR
                      ? "Annuler les modifications"
                      : "Cancel Editing"}
                  </button>
                ) : (
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={saving}
                    onClick={() => {
                      setEditMode(false);
                      setActiveBlockId("");
                      setOutlineNotice("");
                    }}
                  >
                    {isFR
                      ? "Terminer"
                      : "Done Editing"}
                  </button>
                )}
              </div>
            ) : null}

            {canEdit &&
            !editMode ? (
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={saving}
                onClick={() => {
                  const firstEditableBlock =
                    activeGroupBlocks
                      .find(
                        (block) =>
                          block.type !==
                          "figure"
                      );

                  setEditMode(true);
                  setActiveBlockId(
                    firstEditableBlock
                      ?.blockId ||
                      ""
                  );
                  setOutlineNotice("");
                }}
              >
                {isFR
                  ? "Modifier le chapitre"
                  : "Edit Chapter"}
              </button>
            ) : null}
          </div>

          {activeGroupBlocks.length ===
          0 ? (
            <div
              className="ns-card-meta"
              style={{
                padding:
                  "14px 0",
                fontStyle:
                  "italic",
              }}
            >
              {editMode
                ? (
                    isFR
                      ? "Aucun contenu pour le moment. Utilisez Ajouter du contenu pour commencer."
                      : "No content yet. Use Add Content to begin."
                  )
                : (
                    isFR
                      ? "Aucun contenu enregistré dans cette partie."
                      : "No saved content in this outline section."
                  )}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gap: 16,
              }}
            >
              {(
                activeEditorInGroup
                  ? activeGroupBlocks.map(
                      (block) => ({
                        kind: "block",
                        block,
                      })
                    )
                  : buildTextbookFigureTextFlowUnits(
                      activeGroupBlocks
                    )
              ).map(
                (unit) => {
                  if (
                    [
                      "figure_text_flow",
                      "rich_manuscript_figure_flow",
                      "cursor_figure_flow",
                    ].includes(
                      unit.kind
                    )
                  ) {
                    const flowTextBlocks =
                      [
                        ...(
                          clean(
                            unit?.paragraph
                              ?.blockId
                          )
                            ? [
                                unit.paragraph,
                              ]
                            : []
                        ),
                        ...(
                          Array.isArray(
                            unit?.paragraphs
                          )
                            ? unit.paragraphs
                            : []
                        ),
                      ].filter(
                        (
                          block,
                          index,
                          items
                        ) =>
                          clean(
                            block?.blockId
                          ) &&
                          items.findIndex(
                            (candidate) =>
                              clean(
                                candidate
                                  ?.blockId
                              ) ===
                              clean(
                                block
                                  ?.blockId
                              )
                          ) === index
                      );

                    return (
                      <div
                        key={
                          `chapter-text-flow-${clean(unit?.figure?.blockId) || clean(unit?.anchorId) || "flow"}`
                        }
                        data-textbook-chapter-text-flow="true"
                      >
                        {renderReferenceFlowUnit(
                          unit
                        )}

                        {editMode &&
                        canEdit &&
                        flowTextBlocks
                          .length > 0 ? (
                          <div
                            style={{
                              marginTop: 8,
                              display:
                                "flex",
                              justifyContent:
                                "flex-end",
                              gap: 6,
                              flexWrap:
                                "wrap",
                            }}
                          >
                            {flowTextBlocks.map(
                              (flowBlock) => (
                                <button
                                  key={
                                    flowBlock
                                      .blockId
                                  }
                                  className="ns-button ns-button-outline"
                                  type="button"
                                  disabled={
                                    saving
                                  }
                                  onClick={() =>
                                    setActiveBlockId(
                                      flowBlock
                                        .blockId
                                    )
                                  }
                                >
                                  {isFR
                                    ? "Modifier le texte"
                                    : "Edit text"}
                                </button>
                              )
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  }

                  const block =
                    unit.block;

                  const blockEditing =
                    editMode &&
                    activeBlockId ===
                      block.blockId;

                  const richFigureFlow =
                    editMode
                      ? (
                          editRichFigureFlowByParagraphId
                            .get(
                              clean(
                                block
                                  ?.blockId
                              )
                            ) ||
                          null
                        )
                      : null;

                  if (
                    editMode &&
                    block.type ===
                      "figure" &&
                    editConsumedRichFigureBlockIds
                      .has(
                        clean(
                          block.blockId
                        )
                      )
                  ) {
                    return null;
                  }

                  if (
                    block.type ===
                      "figure"
                  ) {
                    return (
                      <div
                        key={
                          block.blockId
                        }
                        style={{
                          display: "grid",
                          gap: 8,
                        }}
                      >
                        {renderFigureBlock(
                          block
                        )}

                        {editMode &&
                        canEdit ? (
                          <div
                            style={{
                              display:
                                "flex",
                              gap: 7,
                              flexWrap:
                                "wrap",
                              justifyContent:
                                "flex-end",
                            }}
                          >
                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              disabled={
                                saving ||
                                !canMove(
                                  block,
                                  -1
                                )
                              }
                              onClick={() =>
                                moveBlock(
                                  block.blockId,
                                  -1
                                )
                              }
                            >
                              {isFR
                                ? "Plus tôt"
                                : "Earlier"}
                            </button>

                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              disabled={
                                saving ||
                                !canMove(
                                  block,
                                  1
                                )
                              }
                              onClick={() =>
                                moveBlock(
                                  block.blockId,
                                  1
                                )
                              }
                            >
                              {isFR
                                ? "Plus tard"
                                : "Later"}
                            </button>

                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              disabled={saving}
                              onClick={() =>
                                removeBlock(
                                  block.blockId
                                )
                              }
                            >
                              {isFR
                                ? "Retirer"
                                : "Remove"}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  }

                  if (!blockEditing) {
                    return (
                      <div
                        key={
                          block.blockId
                        }
                        style={{
                          display: "grid",
                          gap: 8,
                        }}
                      >
                        <div>
                          {renderReferenceBlock(
                            block,
                            richFigureFlow
                              ? {
                                  anchorBlockId:
                                    richFigureFlow
                                      .anchorId,
                                  content:
                                    renderFigureBlock(
                                      richFigureFlow
                                        .figure,
                                      true
                                    ),
                                }
                              : null
                          )}
                        </div>

                        {editMode &&
                        canEdit ? (
                          <div
                            style={{
                              display:
                                "flex",
                              justifyContent:
                                "flex-end",
                            }}
                          >
                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              disabled={saving}
                              onClick={() =>
                                setActiveBlockId(
                                  block.blockId
                                )
                              }
                            >
                              {isFR
                                ? "Modifier"
                                : "Edit"}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  }

                  return (
                    <div
                      key={
                        block.blockId
                      }
                      data-textbook-active-editor={
                        block.blockId
                      }
                      style={{
                        display: "grid",
                        gap: 9,
                        padding: 14,
                        borderRadius: 14,
                        border:
                          "2px solid rgba(5,150,105,0.30)",
                        background:
                          "#f0fdf4",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems:
                            "center",
                          gap: 8,
                          flexWrap:
                            "wrap",
                        }}
                      >
                        <select
                          className="ns-input"
                          aria-label={
                            isFR
                              ? "Type de contenu"
                              : "Content type"
                          }
                          value={
                            block.type
                          }
                          disabled={saving}
                          onChange={(
                            event
                          ) =>
                            changeBlockType(
                              block,
                              event
                                .target
                                .value
                            )
                          }
                          style={{
                            width: "auto",
                            minWidth: 170,
                          }}
                        >
                          {TEXTBOOK_CHAPTER_BLOCK_TYPES.map(
                            (type) => (
                              <option
                                key={type}
                                value={type}
                              >
                                {
                                  TYPE_LABELS[
                                    type
                                  ][language]
                                }
                              </option>
                            )
                          )}
                        </select>

                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span
                            className="ns-card-meta"
                            style={{
                              fontWeight: 800,
                            }}
                          >
                            {isFR
                              ? "Déplacer vers"
                              : "Move to"}
                          </span>

                          <select
                            className="ns-input"
                            data-textbook-existing-block-placement="true"
                            defaultValue=""
                            disabled={saving}
                            onChange={(event) => {
                              const placementMode =
                                event.target.value;

                              if (!placementMode) {
                                return;
                              }

                              moveBlockToPlacement(
                                block,
                                placementMode
                              );

                              event.target.value =
                                "";
                            }}
                            style={{
                              width: "auto",
                              minWidth: 190,
                            }}
                          >
                            <option value="">
                              {isFR
                                ? "Choisir…"
                                : "Choose…"}
                            </option>

                            {normalizeTextbookInsertionPoint(
                              insertionPoint
                            ).afterBlockId ? (
                              <option value="cursor">
                                {isFR
                                  ? "Curseur sélectionné"
                                  : "Selected cursor"}
                              </option>
                            ) : null}


                            <option value="section_end">
                              {isFR
                                ? "Fin de la section"
                                : "End of current section"}
                            </option>

                            <option value="chapter_end">
                              {isFR
                                ? "Fin du chapitre"
                                : "End of chapter"}
                            </option>
                          </select>
                        </label>

                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          disabled={
                            saving ||
                            !canMove(
                              block,
                              -1
                            )
                          }
                          onClick={() =>
                            moveBlock(
                              block.blockId,
                              -1
                            )
                          }
                        >
                          {isFR
                            ? "Plus tôt"
                            : "Earlier"}
                        </button>

                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          disabled={
                            saving ||
                            !canMove(
                              block,
                              1
                            )
                          }
                          onClick={() =>
                            moveBlock(
                              block.blockId,
                              1
                            )
                          }
                        >
                          {isFR
                            ? "Plus tard"
                            : "Later"}
                        </button>

                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          disabled={saving}
                          onClick={() => {
                            removeBlock(
                              block.blockId
                            );
                            setActiveBlockId(
                              ""
                            );
                          }}
                        >
                          {isFR
                            ? "Retirer"
                            : "Remove"}
                        </button>

                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          disabled={saving}
                          onClick={() =>
                            setActiveBlockId(
                              ""
                            )
                          }
                        >
                          {isFR
                            ? "Terminer"
                            : "Done"}
                        </button>
                      </div>

                      {!clean(
                        block.text
                      ) ? (
                        <div
                          className="ns-card-meta"
                          style={{
                            fontWeight: 700,
                          }}
                        >
                          {placeholderForType(
                            block.type,
                            isFR
                          )}
                        </div>
                      ) : null}

                      {block.type ===
                      "equation" ? (
                        <TextbookEquationEditor
                          block={block}
                          disabled={
                            saving ||
                            !canEdit
                          }
                          isFR={isFR}
                          onChange={(
                            patch
                          ) =>
                            updateBlock(
                              block.blockId,
                              patch
                            )
                          }
                        />
                      ) : (
                        <div
                          data-textbook-rich-manuscript-editor={
                            block.blockId
                          }
                        >
                          <TextbookRichBlockEditor
                            block={block}
                            disabled={
                              saving ||
                              !canEdit
                            }
                            isFR={isFR}
                            editorSessionKey={
                              `existing:${block.blockId}`
                            }
                            flowAnchorBlockId={
                              richFigureFlow
                                ?.anchorId ||
                              ""
                            }
                            flowContent={
                              richFigureFlow
                                ? renderFigureBlock(
                                    richFigureFlow
                                      .figure,
                                    true
                                  )
                                : null
                            }
                            onChange={(
                              editorValue
                            ) =>
                              updateRichBlock(
                                block,
                                editorValue
                              )
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          )}
        </section>
          );
        })}
      </div>

        </details>
      ) : null}

      {saveError ? (
        <div
          role="alert"
          style={{
            marginTop: 12,
            color: "#b91c1c",
            fontWeight: 700,
          }}
        >
          {saveError}
        </div>
      ) : null}

      {saveMessage ? (
        <div
          role="status"
          style={{
            marginTop: 12,
            color: "#166534",
            fontWeight: 800,
          }}
        >
          {saveMessage}
        </div>
      ) : null}

      <div
        style={{
          marginTop: 18,
          paddingTop: 16,
          borderTop:
            "1px solid rgba(148,163,184,0.24)",
          display: "flex",
          alignItems: "center",
          justifyContent:
            "space-between",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <span
          className="ns-card-meta"
        >
          {dirty
            ? (
                isFR
                  ? "Modifications non enregistrées."
                  : "Unsaved changes."
              )
            : (
                isFR
                  ? "Aucune modification non enregistrée."
                  : "No unsaved changes."
              )}
        </span>

        <div
          data-textbook-draft-safety-note="true"
          className="ns-card-meta"
          style={{
            maxWidth: 720,
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Pour annuler des modifications récentes, utilisez Annuler dans l’éditeur. Pour revenir à la dernière version enregistrée, utilisez Annuler les modifications à côté d’Enregistrer le chapitre."
            : "To reverse recent edits, use Undo in the editor. To return to the last saved chapter, use Cancel Unsaved Changes beside Save Chapter."}
        </div>
      </div>

      <NextScenesConfirmDialog
        open={pendingCancelEditing}
        title={
          isFR
            ? "Annuler les modifications ?"
            : "Cancel Unsaved Changes?"
        }
        message={
          isFR
            ? "Les modifications non enregistrées de ce chapitre seront annulées. La dernière version enregistrée sera conservée."
            : "Unsaved changes in this chapter will be discarded. The last saved version will be kept."
        }
        stayLabel={
          isFR
            ? "Continuer la modification"
            : "Keep Editing"
        }
        leaveLabel={
          isFR
            ? "Annuler les modifications"
            : "Cancel Unsaved Changes"
        }
        onStay={keepEditing}
        onLeave={finishCancelEditing}
      />

      <NextScenesConfirmDialog
        open={pendingLeave}
        title={
          isFR
            ? "Modifications non enregistrées"
            : "Unsaved chapter changes"
        }
        message={
          isFR
            ? "Ce chapitre contient des modifications non enregistrées. Vous pouvez rester et continuer à écrire, ou quitter sans enregistrer."
            : "This chapter has unsaved changes. You can stay and continue writing, or leave without saving."
        }
        stayLabel={
          isFR
            ? "Rester et continuer"
            : "Stay and Continue Writing"
        }
        leaveLabel={
          isFR
            ? "Quitter sans enregistrer"
            : "Leave Without Saving"
        }
        onStay={stayInChapter}
        onLeave={leaveWithoutSaving}
      />
    </form>
  );
}
